import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { invoicesService, clientsService, contractsService, emailLogsService, fiscalService } from '../services/api';
import { fmtCNPJ, fmtDate, fmtShortDate, fmtDateTime, getTodayStr, getCurrentMonthStr } from '../utils/formatters';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import PeriodSelector, { defaultPeriodFilter, periodToParams } from './PeriodSelector';
import { usePeriodoNaUrl } from '../utils/periodoNaUrl';
import ClientPopover from './ClientPopover';
import { BARRA_DE_GRUPO, FILHA_DE_GRUPO, PILULA_DO_EMAIL, TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { pendingAdjustmentForInvoice, seloReajusteNaoCobrado, fmtBRL as fmtBRLAdj, fmtPct as fmtPctAdj } from '../utils/adjustments';
import { useSortState } from './shared/SortableTableHeader';
import { coberturaDaNf, GrupoDeEmpenho, gruposPorEmpenho, nomeSemEmpenho, rotuloDoGrupoDeEmpenho, SUFIXO_DE_EMPENHO } from '../utils/nfParcial';
import NewInvoiceGroupModal from './NewInvoiceGroupModal';
import InvoiceGroupDetailModal from './InvoiceGroupDetailModal';
import PagamentoDaFatura from './PagamentoDaFatura';
import { tipoDaFatura } from '../utils/tipoDaFatura';
import { avisoDeEdicao } from '../utils/avisoDeEdicaoDaFatura';
import { useGruposEconomicos } from '../hooks/useGruposEconomicos';
import AvisoDosGruposEconomicos from './AvisoDosGruposEconomicos';
import { resumoDoGrupoNaFatura } from '../utils/grupoNaLista';
import MarcaDoGrupo from './shared/MarcaDoGrupo';
import { situacaoDoBoleto, periodoDeServico, mostraCampoDeEmpenho, diasNoPeriodo, itensComPeriodoProprio } from '../utils/fichaDaFatura';
import LinhaDeItem from './LinhaDeItem';
import CascaDeModal, { NumeroDaFaixa } from './shared/CascaDeModal';
import ConfirmacaoDeEmpenho from './empenho/ConfirmacaoDeEmpenho';
import type { PreviaDeEmpenhos } from '../utils/empenho';
import { LinhaDoAceite, SeloDoAceite } from './empenho/AceiteDoAviso';
import MaisAcoes from './shared/MaisAcoes';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaCliente, CelulaDinheiro, CelulaAcoes,
  CelulaPrazo, CartaoDoTopo, barraDoEstado, tomDoPrazo,
} from './shared/lista/PecasDaLista';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import ComprovanteDeEnvioModal from './ComprovanteDeEnvioModal';
import DescarteDoCdr from './DescarteDoCdr';
import SeletorDeFaturaDoCdr from './SeletorDeFaturaDoCdr';
import { montarLoteDeEmail, resumoDoLoteDeEmail } from '../utils/loteDeEmail';
import { celulaDeDocumentos, celulaDoGrupo, SinalDoDocumento, TomDoDocumento } from '../utils/documentosDaFatura';
import { estadoDoCdr } from '../utils/cdrNaFatura';
import { useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, mensagemDoErroDeCarga, campo, campoSomenteLeitura, textos } from '../design';
import { situacaoDoEnvio, emBlocos } from '../utils/envioDoEmail';
import { confirmacaoDeSaida } from '../utils/confirmacaoDeSaida';
import { podeExcluirFatura } from '../utils/exclusaoDaFatura';
import { saldoEmAberto } from '../utils/saldoDaFatura';
import { tomDoStatusDaFatura } from '../utils/tomDoStatusDaFatura';
import { CHIPS_DE_STATUS, chipsDePagamento, passaNoCartao, passaNoChipDePagamento, passaNoChipDeStatus, vencida } from '../utils/filtrosDasFaturas';
import { NotaDoCdr, clienteDoBlocoCdr, faturaPreSelecionada, opcoesDeFaturaParaCdr, resumoDoLote, registrosDoCdrJaAplicados, riscoDeAplicar } from '../utils/faturaDoCdr';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';
import ContatosDaFatura from './cobranca/ContatosDaFatura';
import { papelNaCobranca } from '../utils/filaDeCobranca';
import { PreviaDaGeracao, itensDoFormulario, motivoForaDoPeriodo, previaDaGeracaoValida, rotuloDaRegra } from '../utils/previaDaGeracao';

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    dollar: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
    fileText: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    search: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    eye: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>,
    download: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' /></svg>,
    copy: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' /></svg>,
    check: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>,
    ban: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' /></svg>,
    clock: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
    alertCircle: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
    calculator: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    chart: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' /></svg>,
    mail: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' /></svg>,
    link: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' /></svg>,
  };
  return icons[name] || null;
};


const fmt = (v: any) => { const n = parseFloat(String(v)); return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

/** Espelho de DunningCandidate do backend (invoice-dunning.service.ts). */
interface DunningCandidate {
  id: number;
  code: string;
  clientId: number;
  clientName: string;
  dueDate: string;
  daysOverdue: number;
  originalValue: number;
  amountDue: number;
  suggestedLevel: 1 | 2 | 3;
  paymentMode: 'boleto' | 'pix';
  emails: string[];
  dunningCount: number;
  lastDunningAt: string | null;
  lastDunningLevel: number | null;
  dunningMode: 'auto' | 'manual' | 'never';
  snoozedUntil: string | null;
  snoozeReason: string | null;
  blockedReason?: string;
  blockedKind?: string;
  /** Fora do horário comercial: avisa, não bloqueia. */
  foraDaJanela?: boolean;
  /** Validade do PIX da última cobrança enviada (YYYY-MM-DD). */
  pixValidUntil?: string | null;
  /** O PIX daquela cobrança venceu e o meio continua sendo PIX. */
  pixExpirado?: boolean;
  /** O único impedimento é o "Não enviar" do cadastro, e dá para resolver aqui. */
  liberavel?: boolean;
  /**
   * A fila automática não mandaria esta agora (cliente em modo manual, ou já
   * cobrado hoje), mas o operador pode. Informa, não impede.
   */
  avisoFila?: string;
}

const TARIFF_TYPES = [
  'Celular LDN',
  'Celular Local',
  'Fixo LDN',
  'Fixo Local',
  'LDI',
  'Especial',
  'Emergencial',
  'Entrada',
];

interface InvoiceItem {
  productId?: number;
  name: string;
  description?: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  period?: string;
  locationId?: number;
  locationName?: string;
}

interface InvoiceUsageItem {
  tariff: string;
  minutes: number;
  unitValue: number;
  totalValue: number;
  period?: string;
}

interface Invoice {
  id: number;
  code: string;
  clientId: number;
  contractId?: number;
  invoiceGroupId?: number;
  installmentNumber?: number;
  totalInstallments?: number;
  referenceMonth: string;
  periodStart: string;
  periodEnd: string;
  issueDate: string;
  dueDate: string;
  items: InvoiceItem[];
  subtotalItems: number;
  usageItems: InvoiceUsageItem[];
  subtotalUsage: number;
  usageItemsEntradaNormal?: InvoiceUsageItem[];
  subtotalUsageEntradaNormal?: number;
  usageItemsEntrada?: InvoiceUsageItem[];
  subtotalUsageEntrada?: number;
  cdrRecords?: any[];
  totalValue: number;
  status: string;
  paymentDate?: string;
  paidValue?: number;
  paymentMethod?: string;
  jurosMulta?: number;
  notes?: string;
  client?: any;
  contract?: any;
  /** A nota da parcela de grupo mora aqui (`nfseStatus`), não na parcela. */
  invoiceGroup?: { id: number; code: string; nfseStatus?: string | null };
  /** Agregados de nota: null | Rascunho | Processando | Autorizada | Erro. Cancelada volta a null. */
  nfseStatus?: string | null;
  nfcomStatus?: string | null;
  discountStatus?: string;
  borderoNumber?: string;
  nossoNumero?: string;
  boletoStatus?: string;
  /** Nº do empenho desta parcela, quando o órgão paga empenho a empenho. */
  empenhoNumber?: string | null;
  /** Anualidade aplicada depois desta fatura nascer (só na listagem; ausente = em ordem). */
  reajusteNaoCobrado?: {
    falta: number;
    mrrEsperado: number;
    mrrNaFatura: number;
    ajusteId: number;
    sequence: number;
    effectsFrom: string;
    jaPaga: boolean;
    explicacao?: string;
    /** Backend #254: rascunho de complementar não cobre o buraco. */
    situacao?: 'sem_complementar' | 'complementar_curta' | 'rascunho' | 'cancelada';
    complementar?: { id: number; code: string | null; status: string | null } | null;
  };
}

/** Tons da célula "Documentos": só aviso e perigo têm cor; o em dia fica cinza. */
const TOM_DO_DOCUMENTO: Record<TomDoDocumento, string> = {
  quieto: 'text-[11px] text-slate-500',
  apagado: 'text-[11px] text-slate-500 italic',
  neutro: 'text-xs text-slate-300',
  aviso: `text-xs font-semibold ${TONE_TEXT.warning}`,
  perigo: `text-xs font-semibold ${TONE_TEXT.danger}`,
};

/**
 * O CDR na coluna Documentos é PÍLULA (pedido do Heitor, 07/10/2026): a mesma FORMA da pílula de Status
 * (arredondada, com fundo), para dizer "é a mesma espécie de informação", e o PESO pela gravidade.
 * - aplicado: neutra (fundo slate, texto apagado). Informação: "tem CDR e está lá". Em outubro eram 6 de 20
 *   linhas; uma pílula colorida dizendo "tudo certo" em cada uma faria o normal gritar.
 * - não aplicado: âmbar, o mesmo peso de "Rascunho" na coluna Status. É o que precisa de alguém.
 * ⛔ Não usa a cor da coluna Status: lá o verde é "Aberta", e verde aqui diria outra coisa.
 * Mais JUSTA que a de Status (sem o `py-1`): padding vertical faria a linha crescer, e o motivo de o CDR estar
 * nesta coluna é a linha ter voltado de 91 para 68 px.
 */
// Sem peso de fonte aqui: o peso é de cada variante. Classes de peso juntas brigam pela ordem do CSS, não da string.
const FORMA_DA_PILULA = 'inline-block px-2 rounded-full text-[11px] leading-4';
const PILULA_DO_CDR = {
  quieto: `${FORMA_DA_PILULA} font-medium ${TONE_BADGE.neutral}`,
  aviso: `${FORMA_DA_PILULA} font-medium ${TONE_BADGE.warning}`,
} as const;

// O E-MAIL também é pílula (pedido do Heitor, 08/10/2026): as cores moram no mapa, PILULA_DO_EMAIL em
// utils/statusColors, com o porquê do peso de cada uma. Aqui só se junta a forma.

/**
 * Fatura retida para aprovação? Devolve o motivo, ou null se liberada.
 * Espelha `InvoicesService.holdMotivo` do backend: retenção com data expira
 * sozinha quando o dia chega; sem data exige liberação explícita.
 * Fora do componente de propósito, para não dar TDZ em uso durante o render.
 */
function motivoRetencao(inv: any): string | null {
  if (!inv?.approvalHoldReason) return null;
  if (!inv.approvalHoldUntil) return inv.approvalHoldReason;
  const hoje = new Date();
  const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  const ate = String(inv.approvalHoldUntil).slice(0, 10);
  return hojeStr >= ate ? null : `${inv.approvalHoldReason} (a partir de ${ate.split('-').reverse().join('/')})`;
}

/**
 * `canEdit`: as 35 rotas de escrita de invoices viraram ADMIN no backend
 * (0a0ff7d). Com o default novo, editor nasce com invoices:view_own e CHEGA
 * nesta tela — só não pode escrever nela.
 *
 * Cobertura COMPLETA das ações de escrita desta aba (passada de 12/09/2026):
 *   - barra: Gerar Fatura, Nova Fatura, Processar CDR, e no ⋯ ao lado de Nova
 *            Fatura o Novo grupo de parcelas (saiu da barra na #325)
 *   - lote:  Aprovar, Atualizar venc., Enviar Email, Comunicado Boleto, Excluir
 *            (o Cobrar saiu em 30/10/2026: a cobrança mora na aba Cobrança)
 *   - linha: Editar, Finalizar, Enviar Email, Reabrir, Duplicar, Cancelar,
 *            Excluir
 *
 * Seguem visíveis para quem só lê, porque são LEITURA: ver fatura, baixar PDF,
 * relatório técnico, exportar Excel, buscar e filtrar.
 *
 * ⚠️ Esconder botão não é proteção — quem protege é o @Roles do backend. Se uma
 * escrita nova entrar nesta aba, ela nasce visível: o gate é por botão, não
 * automático.
 */
export default function InvoicesTab({ initialSearch, onSearchConsumed, canEdit = true, onVerNaCobranca, onAbrirCobranca }: {
  initialSearch?: string;
  onSearchConsumed?: () => void;
  canEdit?: boolean;
  /** B1: o selo de cobrança da linha leva à fatura na Cobrança. */
  onVerNaCobranca?: (code: string) => void;
  /** D1: o "Cobrar na Cobrança" do cartão Precisa de você. */
  onAbrirCobranca?: () => void;
} = {}) {
  // Confirmação e aviso do HUB no lugar de window.confirm/alert. `confirmarTexto`
  // usa o primeiro parágrafo da mensagem como título e o resto como corpo, para os
  // textos que já existiam não precisarem ser reescritos. Nomes `confirmar`/`avisar`
  // porque `aviso` já é variável local no editor.
  const confirmar = useConfirm();
  const avisar = useAviso();
  const confirmarTexto = (texto: string, perigo = false) => {
    const [titulo, ...resto] = String(texto).split('\n\n');
    return confirmar({ title: titulo, message: resto.join('\n\n') || undefined, danger: perigo });
  };
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  // Descrições abertas na ficha da fatura (chave `${faturaId}-${linha}`): recolhidas por padrão.
  const [descricoesAbertas, setDescricoesAbertas] = useState<Set<string>>(new Set());
  const [contracts, setContracts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch || '');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  // O período na URL (15/10/2026): ?mes=… / ?de=…&ate=…; F5 e link colado mantêm o mês. Ver utils/periodoNaUrl.
  const [periodFilter, setPeriodFilter] = usePeriodoNaUrl(defaultPeriodFilter(initialSearch ? [] : undefined));
  const [modal, setModal] = useState<'create' | 'edit' | 'view' | 'generate' | 'generate-batch' | 'download' | 'cdr' | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  // A aprovação com empenho (16/10/2026, fase 2): contrato com empenho cadastrado mostra o saldo antes/depois e os
  // avisos; sem empenho, a aprovação segue como sempre. `force` = a liberação da retenção vem junto.
  const [aprovacaoComEmpenho, setAprovacaoComEmpenho] = useState<{ inv: any; previa: PreviaDeEmpenhos; force: boolean; motivoRetencao?: string } | null>(null);
  const [aprovandoComEmpenho, setAprovandoComEmpenho] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-NF payment status for the selected invoice (only loaded when modal === 'view')
  const [fiscalDocsStatus, setFiscalDocsStatus] = useState<{
    invoiceId: number;
    totalValue: number;
    totalPaid: number;
    paidPerNF: number;
    paidGeneric: number;
    fiscalDocuments: Array<{
      id: number;
      type: 'nfse' | 'nfcom';
      documentNumber: string | null;
      totalValue: number;
      paidAmount: number;
      status: 'paid' | 'partial' | 'pending';
      fiscalStatus?: string;
    }>;
  } | null>(null);
  const [loadingFiscalDocs, setLoadingFiscalDocs] = useState(false);
  const [regeneratingFiscal, setRegeneratingFiscal] = useState(false);
  // NF parcial (uma NFS-e por empenho): modal de seleção de itens
  const [partialNfModal, setPartialNfModal] = useState(false);
  const [partialNfItems, setPartialNfItems] = useState<any[]>([]);
  const [creatingPartialNf, setCreatingPartialNf] = useState(false);
  // O que já está em NFS-e ativa (ver utils/nfParcial.ts). null = não deu para
  // consultar; o modal abre como antes, avisando.
  const [partialNfCobertura, setPartialNfCobertura] = useState<{ jaNotado: number; resta: number } | null>(null);
  const [abrindoPartialNf, setAbrindoPartialNf] = useState(false);
  // Itens agrupados pelo empenho do item da fatura (backend #273). Com menos de
  // dois grupos o modal é a lista de sempre. Ver utils/nfParcial.ts.
  const [partialNfGrupos, setPartialNfGrupos] = useState<GrupoDeEmpenho[]>([]);
  const [partialNfGrupoAtivo, setPartialNfGrupoAtivo] = useState<string | null>(null);
  // Nº do empenho: aplicado como sufixo no NOME dos itens selecionados (única
  // forma do empenho sair na NFS-e — notes/infCpl não passam pelo AtendeNet)
  const [partialNfEmpenho, setPartialNfEmpenho] = useState('');
  // Registro do empenho na FATURA, separado da emissão: o número chega por
  // e-mail ou WhatsApp dias antes de alguém abrir a tela de NF parcial, e é
  // esse registro que faz a pendência virar cobrança no alarme das 07h10.
  const [empenhoRegistro, setEmpenhoRegistro] = useState('');
  const [salvandoEmpenho, setSalvandoEmpenho] = useState(false);
  const applyEmpenhoToItems = (emp: string) => {
    const val = emp.trim();
    if (!val) return;
    const suffix = ` — Nota de Empenho nº ${val}`;
    setPartialNfItems(arr =>
      arr.map(x => {
        if (!x._selected) return x;
        const base = String(x._editName || x.name).replace(SUFIXO_DE_EMPENHO, '');
        return { ...x, _editName: base + suffix };
      }),
    );
  };
  // P&S Modal
  const [showPSModal, setShowPSModal] = useState(false);
  const [pendingPSItems, setPendingPSItems] = useState<any[]>([]);
  const [loadingPS, setLoadingPS] = useState(false);
  // A prévia da geração (03/11/2026): o período e as linhas MRR que o modal propõe vêm da geração mensal do backend,
  // e não de regra própria da tela. Ver utils/previaDaGeracao.
  const [previaDaGeracao, setPreviaDaGeracao] = useState<{ estado: 'lendo' | 'erro' | 'ok'; previa?: PreviaDaGeracao; erro?: string; repetir?: () => void } | null>(null);
  const pedidoDaPrevia = useRef(0);
  const [activeTab, setActiveTab] = useState<'dados' | 'itens'>('dados');
  // Invoice Groups (faturamento parcelado com NFS-e única)
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [openGroupId, setOpenGroupId] = useState<number | null>(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<number>>(new Set());
  // Grupo econômico na lista (prévia https://claude.ai/artifact/6Eovd5WDr25fRREhY6T5jW):
  // aberto por padrão, para nenhuma fatura ficar escondida; guarda os FECHADOS.
  const [gruposEconFechados, setGruposEconFechados] = useState<Set<string>>(new Set());
  const toggleGroupExpand = (groupId: number) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };
  const [downloadAttachment, setDownloadAttachment] = useState<{ data: string; type: 'pdf' | 'csv'; filename: string } | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [pdfLink, setPdfLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sort, toggleSort] = useSortState({ col: 'dueDate', dir: 'asc' });
  const [batchResult, setBatchResult] = useState<{ success: number; skipped: number; errors: string[] } | null>(null);
  const [selectedContracts, setSelectedContracts] = useState<number[]>([]);
  const [batchSearchTerm, setBatchSearchTerm] = useState('');
  const [itemsModified, setItemsModified] = useState(false); // Rastreia se itens foram modificados pelo usuário
  const [consolidatedMode, setConsolidatedMode] = useState<boolean>(false); // Padrão: fatura individual por assinatura
  const [autoEmitMode, setAutoEmitMode] = useState<boolean>(false); // Modo "Gerar e Emitir" (automação completa)
  const [selectedClients, setSelectedClients] = useState<number[]>([]);

  // CDR Processing
  const [cdrContent, setCdrContent] = useState<string>('');
  const [cdrResult, setCdrResult] = useState<any>(null);
  const [cdrProcessing, setCdrProcessing] = useState(false);
  const [cdrContractMap, setCdrContractMap] = useState<{ [key: string]: number }>({});
  const [cdrApplying, setCdrApplying] = useState<string | null>(null);
  const [cdrAppliedContracts, setCdrAppliedContracts] = useState<string[]>([]);
  const [cdrInvoiceMap, setCdrInvoiceMap] = useState<{ [key: string]: number }>({});
  const [cdrApplyingInvoice, setCdrApplyingInvoice] = useState<string | null>(null);
  const [cdrAppliedInvoices, setCdrAppliedInvoices] = useState<string[]>([]);
  // Notas fiscais por cliente do CDR: o rótulo de cada fatura diz se ela já tem nota autorizada.
  const [cdrNotasPorCliente, setCdrNotasPorCliente] = useState<Record<number, NotaDoCdr[] | null>>({});

  // Download em lote
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // Payment method filter
  const [paymentFilter, setPaymentFilter] = useState('');
  const [emailIssueFilter, setEmailIssueFilter] = useState(false); // só faturas com falha de e-mail (total ou parcial)
  const [resendingTo, setResendingTo] = useState<string | null>(null); // reenvio individual em andamento

  // Seleção múltipla
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Email sending
  const [sendingEmail, setSendingEmail] = useState<number | null>(null);
  const [emailModal, setEmailModal] = useState<{ invoiceId: number; invoiceCode: string; emails: { email: string; checked: boolean }[]; customEmail: string; customNote: string } | null>(null);
  const [emailResult, setEmailResult] = useState<{ sent: number; skipped: number; errors: number; details: any[] } | null>(null);
  const [batchEmailModal, setBatchEmailModal] = useState<{ invoiceIds: number[]; summary: string; customNote: string; validationResult?: { total: number; valid: number; invalid: number; errors: any[] }; validating?: boolean } | null>(null);

  // Email delivery status (array of logs per invoice for multi-recipient support)
  // Quais faturas tiveram o log de e-mail LIDO com sucesso. Fora deste conjunto, "sem log" quer dizer "não sei", e não
  // "nunca enviado" (utils/envioDoEmail): com a API dos logs fora, toda aberta virava "aguardando envio".
  const [logsConhecidos, setLogsConhecidos] = useState<Set<number>>(() => new Set());
  const [falhaDosLogs, setFalhaDosLogs] = useState<string | null>(null);
  const [tentativaDosLogs, setTentativaDosLogs] = useState(0);
  const [emailStatusMap, setEmailStatusMap] = useState<Record<number, Array<{ status: string; openCount: number; sentAt?: string; deliveredAt?: string; openedAt?: string; bouncedAt?: string; bounceReason?: string; recipientEmail?: string }>>>({});
  /**
   * Logs do e-mail de COBRANCA, separados dos da fatura.
   *
   * Sao entidades distintas em `email_logs` ('invoice_dunning' x 'invoice') e
   * misturar as duas faz o indicador mentir: a fatura de marco foi aberta, a
   * cobranca de setembro nao, e a tela dizia que o cliente tinha visto.
   */
  const [dunningLogsMap, setDunningLogsMap] = useState<Record<number, Array<{ status: string; openCount: number; openedAt?: string; recipientEmail?: string }>>>({});
  const [emailPopoverInvoiceId, setEmailPopoverInvoiceId] = useState<number | null>(null);

  // Batch progress
  const [batchProgress, setBatchProgress] = useState<{
    jobId: string; type: string; total: number; completed: number;
    percent: number; errors: any[]; skipped?: any[]; status: string;
  } | null>(null);

  // ── Cobrança de fatura vencida
  // `blockedReason` vem pronto do backend: a regra de quem pode ser cobrado vive
  // lá (função pura, coberta por guardrail), não aqui. A tela só mostra.
  const [dunningMap, setDunningMap] = useState<Record<number, DunningCandidate>>({});

  // Card filter
  const [cardFilter, setCardFilter] = useState<'paid' | 'open' | 'overdue' | 'draft' | 'email_pending' | null>(null);


  const [form, setForm] = useState<any>({
    clientId: 0,
    contractId: null,
    referenceMonth: '',
    periodStart: '',
    periodEnd: '',
    issueDate: getTodayStr(),
    dueDate: '',
    items: [],
    usageItems: [],
    notes: '',
  });

  // ERRO NÃO É VAZIO (07/10/2026): com a API fora, esta carga só logava; a tabela dizia "Nenhuma fatura encontrada —
  // Ajuste os filtros ou GERE NOVAS FATURAS" e os cartões, R$ 0,00. Além de afirmar um banco vazio, a tela
  // convidava a gerar, que é caminho para fatura duplicada. Agora a falha vira estado (design/useEstadoDaCarga) e as
  // listas só são gravadas quando a chamada dá certo: recarga que falha não apaga o que estava na tela.
  const { rodar: rodarCargaDasFaturas, ...cargaDasFaturas } = useEstadoDaCarga();
  const loadData = useCallback(async () => {
    setLoading(true);
    await rodarCargaDasFaturas(async () => {
      const [inv, cli, con] = await Promise.all([
        invoicesService.getAll(),
        clientsService.getAll(),
        contractsService.getAll(),
      ]);
      setInvoices(Array.isArray(inv) ? inv : []);
      setClients(Array.isArray(cli) ? cli : []);
      setContracts(Array.isArray(con) ? con : []);
    });
    setLoading(false);
  }, [rodarCargaDasFaturas]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Assinaturas/clientes que JÁ têm fatura (não-cancelada) na competência do lote —
  // pra não reaparecerem em "Gerar Faturas em Lote" (evita duplicar). O dedup do
  // backend só pega contractId; consolidada (contractId nulo) precisa do match por clientId.
  //
  // ⛔ "Sem contractId" NÃO é sinônimo de consolidada (02/10/2026): avulsa de
  // retroativo também nasce sem contrato, e três delas (IFRN, SORRISO, COM8DN)
  // esconderam R$ 10.343,67 de MRR do lote. Quem decide é `tipoDaFatura`.
  const billedMonthSets = useMemo(() => {
    const month = (form?.referenceMonth || '').slice(0, 7);
    const invoicedContractIds = new Set<number>();
    const consolidatedClientIds = new Set<number>();
    const invoicedClientIds = new Set<number>();
    if (month) {
      invoices.forEach((inv: any) => {
        if (inv.status === 'Cancelada') return;
        if ((inv.referenceMonth || '').slice(0, 7) !== month) return;
        const tipo = tipoDaFatura(inv);
        // Avulsa e parcela de grupo não cobrem a competência de ninguém.
        if (tipo === 'avulsa' || tipo === 'parcela_de_grupo') return;
        if (inv.clientId) invoicedClientIds.add(inv.clientId);
        if (tipo === 'assinatura') invoicedContractIds.add(inv.contractId);
        else if (inv.clientId) consolidatedClientIds.add(inv.clientId);
      });
    }
    return { invoicedContractIds, consolidatedClientIds, invoicedClientIds };
  }, [invoices, form]);

  // contrato já coberto no mês? (própria fatura individual OU consolidada do cliente)
  const isContractBilled = useCallback((c: any) =>
    billedMonthSets.invoicedContractIds.has(c.id) || billedMonthSets.consolidatedClientIds.has(c.clientId),
    [billedMonthSets]);

  useEffect(() => {
    if (initialSearch) {
      setSearch(initialSearch);
      setPeriodFilter(defaultPeriodFilter([]));
      onSearchConsumed?.();
    }
  }, [initialSearch]);

  // Limpar seleção quando os filtros mudam
  useEffect(() => {
    setSelectedInvoices([]);
  }, [search, statusFilter, periodFilter, cardFilter, paymentFilter]);

  // Load per-NF payment status when opening the invoice detail modal — and the
  // EDITOR, which needs it to warn about notes already authorized.
  useEffect(() => {
    if ((modal !== 'view' && modal !== 'edit') || !selectedInvoice) {
      setFiscalDocsStatus(null);
      return;
    }
    let cancelled = false;
    setLoadingFiscalDocs(true);
    invoicesService
      .getFiscalDocumentsPaymentStatus(selectedInvoice.id)
      .then((data: any) => {
        if (cancelled) return;
        setFiscalDocsStatus(data);
      })
      .catch(() => {
        if (cancelled) return;
        setFiscalDocsStatus(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingFiscalDocs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modal, selectedInvoice?.id]);

  // Estado de cobrança das faturas vencidas. Alimenta o selo da lista (só leitura desde a unificação de 30/10/2026).
  // Com a carga falhando, o cartão "Precisa de você" diz que os selos não carregaram, em vez de calar.
  const { rodar: rodarCargaDasCobrancas, ...cargaDasCobrancas } = useEstadoDaCarga();
  const loadDunning = async () => {
    await rodarCargaDasCobrancas(async () => {
      const rows: DunningCandidate[] = await invoicesService.dunningCandidates();
      setDunningMap(Object.fromEntries(rows.map(r => [r.id, r])));
    });
  };
  useEffect(() => { loadDunning(); }, []);

  // Polling batch progress
  useEffect(() => {
    if (!batchProgress || batchProgress.status === 'done') return;
    // Uma falha isolada do poll é tolerável: o próximo tique tenta de novo. Falha PERSISTENTE congelava a barra para
    // sempre, sem aviso (07/10/2026). Com FALHAS_SEGUIDAS_DO_POLL seguidas, o poll para, recarrega a lista e diz que
    // não sabe se a geração terminou: ela roda no servidor e pode ter continuado.
    const FALHAS_SEGUIDAS_DO_POLL = 5;
    let falhasSeguidas = 0;
    const interval = setInterval(async () => {
      try {
        const progress = await invoicesService.getBatchProgress(batchProgress.jobId);
        falhasSeguidas = 0;
        setBatchProgress(prev => prev ? { ...prev, ...progress } : null);
        if (progress.status === 'done') {
          clearInterval(interval);
          await loadData();
          await loadDunning();
          setSelectedInvoices([]);
        }
      } catch (e) {
        falhasSeguidas++;
        if (falhasSeguidas < FALHAS_SEGUIDAS_DO_POLL) return;
        clearInterval(interval);
        setBatchProgress(null);
        await loadData();
        avisar(`Parei de acompanhar o processamento em lote: o servidor não respondeu ${FALHAS_SEGUIDAS_DO_POLL} vezes seguidas (${mensagemDoErroDeCarga(e)}). Ele roda no servidor e pode ter continuado: recarreguei a lista, confira antes de rodar de novo.`, 'error');
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [batchProgress?.jobId, batchProgress?.status]);

  const currentMonth = getCurrentMonthStr();

  // A regra mora em utils/filtrosDasFaturas, junto com os chips, para a conservação ser testável.
  const isOverdue = (inv: Invoice) => vencida(inv);

  const filteredInvoices = invoices.filter((inv) => {
    const client = clients.find((c) => c.id === inv.clientId);
    const s = search.toLowerCase().trim();
    const sDigits = s.replace(/\D/g, '');
    const matchSearch =
      !s ||
      inv.code?.toLowerCase().includes(s) ||
      (client?.name || '').toLowerCase().includes(s) ||
      (client?.tradeName || '').toLowerCase().includes(s) ||
      (client?.companyName || '').toLowerCase().includes(s) ||
      (sDigits.length >= 3 && (client?.cnpj || '').replace(/\D/g, '').includes(sDigits));
    const matchStatus = passaNoChipDeStatus(inv, statusFilter);
    let matchMonth: boolean;
    if (periodFilter.mode === 'dateRange' && periodFilter.startDate && periodFilter.endDate) {
      const due = (inv.dueDate || '').split('T')[0];
      matchMonth = due >= periodFilter.startDate && due <= periodFilter.endDate;
    } else {
      matchMonth = periodFilter.months.length === 0 || periodFilter.months.includes(inv.referenceMonth);
    }

    // Card filter
    if (cardFilter) {
      switch (cardFilter) {
        case 'paid':
        case 'open':
        case 'overdue':
        case 'draft':
          if (!passaNoCartao(inv, cardFilter)) return false;
          break;
        case 'email_pending': {
          const cl = clients.find((c: any) => c.id === inv.clientId);
          if (situacaoDoEnvio(inv, cl, emailStatusMap[inv.id], logsConhecidos.has(inv.id)) !== 'aguardando') return false;
          break;
        }
      }
    }

    const matchPayment = passaNoChipDePagamento(inv, paymentFilter);

    // Filtro "Falha e-mail": pelo menos 1 destinatário com falha no ÚLTIMO envio
    if (emailIssueFilter) {
      const st = getEmailSummary(emailStatusMap[inv.id])?.state;
      if (st !== 'partial' && st !== 'bounced') return false;
    }

    return matchSearch && matchStatus && matchMonth && matchPayment;
  }).sort((a, b) => {
    let valA: any, valB: any;
    if (sort.col === 'clientName') {
      const clientA = clients.find((c) => c.id === a.clientId);
      const clientB = clients.find((c) => c.id === b.clientId);
      valA = clientA?.name || clientA?.tradeName || '';
      valB = clientB?.name || clientB?.tradeName || '';
    } else if (sort.col === 'totalValue') {
      valA = parseFloat(String(a.totalValue)) || 0;
      valB = parseFloat(String(b.totalValue)) || 0;
    } else {
      valA = (a as any)[sort.col] ?? '';
      valB = (b as any)[sort.col] ?? '';
    }
    if (typeof valA === 'number' && typeof valB === 'number') {
      return sort.dir === 'asc' ? valA - valB : valB - valA;
    }
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    if (strA < strB) return sort.dir === 'asc' ? -1 : 1;
    if (strA > strB) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  // Group rows: faturas pertencentes ao mesmo Grupo de Faturamento aparecem
  // colapsadas como 1 row mestre, expandindo pra mostrar as N parcelas
  type DisplayRow =
    | { kind: 'invoice'; invoice: Invoice; isChildOfGroup?: boolean; econ?: boolean; ultima?: boolean }
    | { kind: 'group'; groupId: number; groupCode: string; invoices: Invoice[] }
    | { kind: 'grupoEcon'; chave: string; matrizId: number; invoices: Invoice[] };

  // GRUPO ECONÔMICO: as faturas das empresas de um grupo com rateio, na mesma
  // competência, sob uma linha que SOMA. Mesmo hook e mesmo cálculo da lista de
  // Assinaturas. ⛔ Leitura: nenhuma ação agregada (emitir, baixar boleto, enviar).
  const { paineis: paineisDosGrupos, matrizDoMembro, grupoSolto, gruposQueFaltaram, erroDosGrupos, tentarDeNovo: lerGruposDeNovo } = useGruposEconomicos(clients, contracts);

  const displayRows: DisplayRow[] = (() => {
    const out: DisplayRow[] = [];
    const seenGroupIds = new Set<number>();
    const vistasNoEcon = new Set<number>();
    for (const inv of filteredInvoices) {
      const matriz = !inv.invoiceGroupId ? matrizDoMembro.get(inv.clientId) : undefined;
      if (matriz != null) {
        if (vistasNoEcon.has(inv.id)) continue;
        const chave = `${matriz}|${inv.referenceMonth}`;
        const doGrupo = filteredInvoices.filter(
          (i) => !i.invoiceGroupId && i.referenceMonth === inv.referenceMonth && matrizDoMembro.get(i.clientId) === matriz,
        );
        doGrupo.forEach((i) => vistasNoEcon.add(i.id));
        out.push({ kind: 'grupoEcon', chave, matrizId: matriz, invoices: doGrupo });
        if (!gruposEconFechados.has(chave)) {
          doGrupo.forEach((filha, i) => out.push({ kind: 'invoice', invoice: filha, isChildOfGroup: true, econ: true, ultima: i === doGrupo.length - 1 }));
        }
        continue;
      }
      if (inv.invoiceGroupId) {
        if (seenGroupIds.has(inv.invoiceGroupId)) continue;
        seenGroupIds.add(inv.invoiceGroupId);
        const groupInvs = filteredInvoices
          .filter(i => i.invoiceGroupId === inv.invoiceGroupId)
          .sort((a, b) => (a.installmentNumber || 0) - (b.installmentNumber || 0));
        out.push({
          kind: 'group',
          groupId: inv.invoiceGroupId,
          groupCode: inv.invoiceGroup?.code || `Grupo #${inv.invoiceGroupId}`,
          invoices: groupInvs,
        });
        if (expandedGroupIds.has(inv.invoiceGroupId)) {
          groupInvs.forEach((childInv, i) => out.push({ kind: 'invoice', invoice: childInv, isChildOfGroup: true, ultima: i === groupInvs.length - 1 }));
        }
      } else {
        out.push({ kind: 'invoice', invoice: inv });
      }
    }
    return out;
  })();

  const visibleInvoiceIds = filteredInvoices.filter(inv => inv.status !== 'Rascunho').map(inv => inv.id);
  useEffect(() => {
    if (visibleInvoiceIds.length === 0) return;
    Promise.all(emBlocos(visibleInvoiceIds).map((ids) => emailLogsService.getByEntities('invoice_dunning', ids))).then((partes: any[][]) => {
      const logs = partes.flat();
      const porFatura: Record<number, any[]> = {};
      for (const log of logs) {
        if (!log.entityId) continue;
        (porFatura[log.entityId] ||= []).push({
          status: log.status,
          openCount: log.openCount || 0,
          openedAt: log.openedAt,
          recipientEmail: log.recipientEmail,
        });
      }
      setDunningLogsMap(prev => ({ ...prev, ...porFatura }));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleInvoiceIds.join(',')]);

  // Recarrega os logs de e-mail de UMA fatura (após reenvio individual)
  const refreshEmailLogsFor = async (invoiceId: number) => {
    try {
      const logs: any[] = await emailLogsService.getByEntities('invoice', [invoiceId]);
      const entries = logs.filter(l => l.entityId === invoiceId).map(log => ({
        status: log.status,
        openCount: log.openCount || 0,
        sentAt: log.sentAt,
        deliveredAt: log.deliveredAt,
        openedAt: log.openedAt,
        bouncedAt: log.bouncedAt,
        bounceReason: log.bounceReason,
        recipientEmail: log.recipientEmail,
      }));
      setEmailStatusMap(prev => ({ ...prev, [invoiceId]: entries }));
      setLogsConhecidos(prev => new Set(prev).add(invoiceId));
    } catch { /* melhor esforço */ }
  };


  const monthInvoices = (() => {
    if (periodFilter.mode === 'dateRange' && periodFilter.startDate && periodFilter.endDate) {
      return invoices.filter((i) => {
        const due = (i.dueDate || '').split('T')[0];
        return due >= periodFilter.startDate && due <= periodFilter.endDate;
      });
    }
    // `months` vazio = TODOS os meses, a mesma leitura que o filtro da lista faz
    // (`periodFilter.months.length === 0 || ...`). Antes daqui saía
    // `[currentMonth]`, então "Todos os meses" mostrava 28 vencidas na lista e
    // "Vencidas R$ 0,00 · 0 faturas" no card logo acima — os dois lados da mesma
    // tela discordando sobre o mesmo filtro.
    if (periodFilter.months.length === 0) return invoices;
    return invoices.filter((i) => periodFilter.months.includes(i.referenceMonth));
  })();

  const totalPaid = monthInvoices
    .filter((i) => i.status === 'Paga')
    .reduce((s, i) => s + (parseFloat(String(i.totalValue)) || 0), 0);
  const countPaid = monthInvoices.filter((i) => i.status === 'Paga').length;

  const openInvoices = monthInvoices.filter((i) => i.status === 'Aberta' || i.status === 'Parcial');
  const overdueInvoices = openInvoices.filter((i) => isOverdue(i));
  const onTimeInvoices = openInvoices.filter((i) => !isOverdue(i));

  // "A receber" é o SALDO (utils/saldoDaFatura): a Parcial entrava cheia, contando de novo o que já foi pago.
  const totalOverdue = overdueInvoices.reduce((s, i) => s + saldoEmAberto(i as any), 0);
  const totalOnTime = onTimeInvoices.reduce((s, i) => s + saldoEmAberto(i as any), 0);

  const draftInvoices = monthInvoices.filter((i) => i.status === 'Rascunho');
  const totalDraft = draftInvoices.length;
  const totalDraftValue = draftInvoices.reduce((s, i) => s + (parseFloat(String(i.totalValue)) || 0), 0);

  const situacaoDoEnvioDe = (inv: any) =>
    situacaoDoEnvio(inv, clients.find((c: any) => c.id === inv.clientId), emailStatusMap[inv.id], logsConhecidos.has(inv.id));
  const emailPendingInvoices = monthInvoices.filter((inv: any) => situacaoDoEnvioDe(inv) === 'aguardando');
  // Abertas cujo log NÃO foi lido: não se sabe se já receberam. Enquanto houver alguma, a entrada "aguardando envio"
  // fica travada (é o caminho que o envio em lote recomenda para "só quem nunca recebeu").
  const envioDesconhecido = monthInvoices.filter((inv: any) => situacaoDoEnvioDe(inv) === 'desconhecido');

  // Os logs de e-mail das faturas da lista E das que os cartões contam (o período inteiro). Antes eram só as visíveis
  // na lista filtrada, e o cartão contava como "aguardando envio" toda aberta que o filtro escondia. Em blocos de 200,
  // o limite do servidor (utils/envioDoEmail). Bloco que falha deixa as suas faturas como "não sei", com o motivo.
  const idsDosLogs = Array.from(new Set([...filteredInvoices, ...monthInvoices]
    .filter((inv) => inv.status !== 'Rascunho').map((inv) => inv.id))).sort((a, b) => a - b);
  const chaveDosLogs = idsDosLogs.join(',');
  useEffect(() => {
    if (idsDosLogs.length === 0) return;
    let vivo = true;
    const blocos = emBlocos(idsDosLogs);
    Promise.allSettled(blocos.map((ids) => emailLogsService.getByEntities('invoice', ids))).then((resultados) => {
      if (!vivo) return;
      const novos: Record<number, any[]> = {};
      const lidos: number[] = [];
      let motivo: string | null = null;
      resultados.forEach((r, i) => {
        if (r.status === 'rejected') { motivo = motivo || mensagemDoErroDeCarga(r.reason); return; }
        lidos.push(...blocos[i]);
        for (const log of (r.value as any[]) || []) {
          if (!log.entityId) continue;
          (novos[log.entityId] ||= []).push({
            status: log.status,
            openCount: log.openCount || 0,
            sentAt: log.sentAt,
            deliveredAt: log.deliveredAt,
            openedAt: log.openedAt,
            bouncedAt: log.bouncedAt,
            bounceReason: log.bounceReason,
            recipientEmail: log.recipientEmail,
          });
        }
      });
      setEmailStatusMap(prev => ({ ...prev, ...novos }));
      if (lidos.length) setLogsConhecidos(prev => { const s = new Set(prev); lidos.forEach((id) => s.add(id)); return s; });
      setFalhaDosLogs(motivo);
    });
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDosLogs, tentativaDosLogs]);

  // Sem lista carregada (carregando ou falhou antes da primeira carga boa), os cartões mostram "—" e não 0.
  const semNumerosDeFaturas = cargaDasFaturas.estado !== 'pronto' && invoices.length === 0;

  const totalMonth = monthInvoices
    .reduce((s, i) => s + (parseFloat(String(i.totalValue)) || 0), 0);

  // O formulário da fatura como abriu (09/10/2026): o Esc da casca só fecha direto se nada mudou; senão pergunta.
  const [formAoAbrir, setFormAoAbrir] = useState<any>(null);

  const openModal = (type: 'create' | 'edit' | 'view' | 'generate' | 'generate-batch', invoice?: Invoice) => {
    setModal(type);
    setItemsModified(false);
    setSelectedInvoice(invoice || null);
    setActiveTab('dados');
    setBatchResult(null);
    setEmpenhoRegistro((invoice as any)?.empenhoNumber || '');
    pedidoDaPrevia.current++;
    setPreviaDaGeracao(null);

    if (type === 'create') {
      // O período abre vazio: ele sai da regra do cliente (órgão público fatura o mês anterior), e a tela só o conhece
      // pela prévia da geração, ao escolher o cliente ou a assinatura. Abrir no mês corrente era o erro do Sorriso.
      const inicial = {
        clientId: 0,
        contractId: null,
        referenceMonth: getCurrentMonthStr(),
        periodStart: '',
        periodEnd: '',
        issueDate: getTodayStr(),
        dueDate: '',
        items: [],
        usageItems: [],
        notes: '',
      };
      setForm(inicial);
      setFormAoAbrir(inicial);
    } else if (type === 'edit' && invoice) {
      // Inicializa baseValue com unitValue para itens existentes que não têm baseValue
      const itemsWithBaseValue = (invoice.items || []).map((item: any) => ({
        ...item,
        baseValue: item.baseValue || item.unitValue || 0,
        periodStart: item.periodStart?.split('T')[0] || invoice.periodStart?.split('T')[0] || '',
        periodEnd: item.periodEnd?.split('T')[0] || invoice.periodEnd?.split('T')[0] || '',
      }));
      const inicial = {
        clientId: invoice.clientId,
        contractId: invoice.contractId,
        referenceMonth: invoice.referenceMonth,
        periodStart: invoice.periodStart?.split('T')[0] || '',
        periodEnd: invoice.periodEnd?.split('T')[0] || '',
        issueDate: invoice.issueDate?.split('T')[0] || '',
        dueDate: invoice.dueDate?.split('T')[0] || '',
        items: itemsWithBaseValue,
        usageItems: invoice.usageItems || [],
        notes: invoice.notes || '',
      };
      setForm(inicial);
      setFormAoAbrir(inicial);
    } else if (type === 'generate') {
      setForm({
        contractId: 0,
        referenceMonth: currentMonth,
      });
    } else if (type === 'generate-batch') {
      setForm({
        referenceMonth: currentMonth,
      });
      setSelectedContracts([]);
    }
  };

  const closeModal = () => {
    setModal(null);
    setSelectedInvoice(null);
    setForm({});
    setBatchSearchTerm('');
    setBatchResult(null);
    setSelectedContracts([]);
    setSelectedClients([]);
    setPdfLink(null);
    setLinkCopied(false);
    setDownloadAttachment(null);
    setItemsModified(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Preparar payload - enviar null para dueDate vazio
      const payload = { ...form };
      // O motivo da vigência é texto da tela, não campo da linha da fatura.
      if (Array.isArray(payload.items)) payload.items = payload.items.map(({ motivoDaVigencia, ...i }: any) => i);
      if (!payload.dueDate || payload.dueDate === '') {
        payload.dueDate = null;
      }

      if (modal === 'create') {
        await invoicesService.create(payload);
      } else if (modal === 'edit' && selectedInvoice) {
        // Não sobrescrever items/usageItems se não foram modificados pelo usuário
        if (!itemsModified) {
          delete payload.items;
          delete payload.usageItems;
        }
        await invoicesService.update(selectedInvoice.id, payload);
      } else if (modal === 'generate-batch') {
        let result;

        // Modo "Gerar e Emitir" (automação completa)
        if (autoEmitMode) {
          if (consolidatedMode) {
            // Processar clientes selecionados individualmente com "Gerar e Emitir"
            const clientsToProcess = selectedClients.length > 0
              ? selectedClients
              : Array.from(new Set(contracts.filter((c: any) => c.status === 'Ativo' && parseFloat(c.mrr) > 0).map((c: any) => c.clientId)));

            let success = 0;
            let errors: string[] = [];

            for (const clientId of clientsToProcess) {
              try {
                await invoicesService.generateAndEmitConsolidated({
                  clientId,
                  referenceMonth: form.referenceMonth,
                });
                success++;
              } catch (err: any) {
                const client = clients.find((c: any) => c.id === clientId);
                errors.push(`${client?.name || 'Cliente ' + clientId}: ${err.response?.data?.message || err.message}`);
              }
            }

            result = { success, skipped: 0, errors };
          } else {
            // Processar contratos selecionados individualmente com "Gerar e Emitir"
            const contractsToProcess = selectedContracts.length > 0
              ? selectedContracts
              : contracts.filter((c: any) => c.status === 'Ativo' && parseFloat(c.mrr) > 0).map((c: any) => c.id);

            let success = 0;
            let errors: string[] = [];

            for (const contractId of contractsToProcess) {
              try {
                await invoicesService.generateAndEmit({
                  contractId,
                  referenceMonth: form.referenceMonth,
                });
                success++;
              } catch (err: any) {
                const contract = contracts.find((c: any) => c.id === contractId);
                errors.push(`${contract?.client?.name || 'Contrato ' + contractId}: ${err.response?.data?.message || err.message}`);
              }
            }

            result = { success, skipped: 0, errors };
          }
        } else {
          // Modo normal (apenas gerar, sem emitir)
          if (consolidatedMode) {
            // Gerar faturas consolidadas por cliente
            result = await invoicesService.generateBatchConsolidated({
              referenceMonth: form.referenceMonth,
              clientIds: selectedClients.length > 0 ? selectedClients : undefined,
            });
          } else {
            // Gerar faturas individuais por assinatura
            result = await invoicesService.generateBatch({
              referenceMonth: form.referenceMonth,
              contractIds: selectedContracts.length > 0 ? selectedContracts : undefined,
            });
          }
        }

        setBatchResult(result);
        await loadData();
        setSaving(false); // Resetar estado antes de retornar
        return; // Não fecha o modal para mostrar resultado
      }
      await loadData();
      closeModal();
    } catch (e: any) {
      console.error('Erro ao salvar:', e);
      avisar(e.response?.data?.message || 'Erro ao salvar fatura', 'error');
    }
    setSaving(false);
  };

  // As ações de uma fatura só (Finalizar, Cancelar, Reabrir, Excluir) falhavam caladas: o erro ia para o console, a
  // linha não mudava, e o operador via a fatura "não obedecer" sem saber por quê (07/10/2026). Tentar de novo é seguro
  // em todas: o status da linha só muda quando o servidor confirmou.
  const avisarFalhaDaAcao = (verbo: string, id: number, e: unknown) => {
    const code = invoices.find((i) => i.id === id)?.code || `#${id}`;
    avisar(`Não consegui ${verbo} ${code}: ${mensagemDoErroDeCarga(e)}.`, 'error');
  };

  /**
   * A prévia do empenho antes de aprovar. Fora do controle (contrato sem empenho cadastrado) = null: aprova como sempre.
   * A leitura que falha não trava a aprovação: o backend recusa sozinho quando o aviso exige motivo, e diz por quê.
   */
  const lerPreviaDoEmpenho = async (id: number): Promise<{ previa: PreviaDeEmpenhos | null; erro: string | null }> => {
    try {
      const previa = await invoicesService.getEmpenhoPreview(id);
      // Só entra no fluxo do empenho quando o backend DIZ que está dentro (foraDoControle === false, com a lista de blocos).
      const dentro = !!previa && previa.foraDoControle === false && Array.isArray(previa.blocos);
      return { previa: dentro ? previa : null, erro: null };
    } catch (e) {
      return { previa: null, erro: mensagemDoErroDeCarga(e) };
    }
  };
  const semLeituraDoEmpenho = (erro: string | null) => erro
    ? `\n\nNão consegui ler o empenho desta fatura (${erro}). Se ele exigir motivo, o servidor recusa a aprovação e diz por quê.`
    : '';

  const handleFinalize = async (id: number) => {
    const { previa, erro } = await lerPreviaDoEmpenho(id);
    if (previa) {
      setAprovacaoComEmpenho({ inv: invoices.find((i) => i.id === id) || { id, code: `#${id}` }, previa, force: false });
      return;
    }
    if (!(await confirmarTexto('Finalizar fatura? Ela mudara de Rascunho para Aberta.' + semLeituraDoEmpenho(erro)))) return;
    try {
      await invoicesService.finalize(id);
      await loadData();
    } catch (e) {
      avisarFalhaDaAcao('finalizar', id, e);
    }
  };

  const aprovarComEmpenho = async (motivo: string) => {
    const atual = aprovacaoComEmpenho;
    if (!atual) return;
    setAprovandoComEmpenho(true);
    try {
      if (atual.force) await invoicesService.releaseHold(atual.inv.id);
      await invoicesService.finalize(atual.inv.id, atual.force, motivo ? { empenhoNote: motivo } : undefined);
      avisar(`Fatura ${atual.inv.code} aprovada.`, 'success');
      setAprovacaoComEmpenho(null);
      if (atual.force) closeModal();
      loadData();
    } catch (e) {
      avisarFalhaDaAcao('aprovar', atual.inv.id, e);
    } finally {
      setAprovandoComEmpenho(false);
    }
  };

  // Regera a(s) nota(s) fiscal(is) da fatura. O backend só cria rascunho novo pros
  // tipos SEM nota ativa (não-cancelada) — então o fluxo é: 1) cancelar a NF atual
  // em Notas Fiscais → Documentos, 2) clicar aqui (cria rascunho), 3) Transmitir em
  // Documentos → a nova NF sai com a DATA DE HOJE (Focus carimba na transmissão).
  // Usado p/ cliente público que pede reemissão por novo empenho (ex: SAMU FRONTEIRA).
  const handleRegenerateFiscal = async (inv: any) => {
    if (
      !(await confirmarTexto(`Regerar nota fiscal da fatura ${inv.code}?\n\n` +
          `• Cria um NOVO RASCUNHO de NF (notas canceladas são ignoradas).\n` +
          `• Se ainda houver NF ativa (Autorizada), NADA é criado — cancele-a antes em Notas Fiscais → Documentos.\n\n` +
          `Depois, vá em Notas Fiscais → Documentos e clique Transmitir para emitir com a data de HOJE.`,))
    )
      return;
    setRegeneratingFiscal(true);
    try {
      const docs = await fiscalService.splitInvoice(inv.id);
      if (Array.isArray(docs) && docs.length > 0) {
        avisar(
          `✅ ${docs.length} rascunho(s) de NF criado(s): ${docs
            .map((d: any) => String(d.type).toUpperCase())
            .join(', ')}.\n\n` +
            `Agora vá em Notas Fiscais → Documentos e clique Transmitir para emitir com a data de hoje.`
        , 'info');
      } else {
        avisar(
          'Nenhum rascunho criado — provavelmente ainda existe uma NF ativa (não cancelada) para esta fatura.\n\n' +
            'Cancele a NF em Notas Fiscais → Documentos e tente de novo.'
        , 'warning');
      }
      // Recarrega o painel de NF do modal
      try {
        const data = await invoicesService.getFiscalDocumentsPaymentStatus(inv.id);
        setFiscalDocsStatus(data);
      } catch {
        /* noop */
      }
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao regerar nota fiscal', 'error');
    }
    setRegeneratingFiscal(false);
  };

  // NF PARCIAL: cria rascunho de NFS-e só com os itens selecionados (caso órgão
  // público com uma NFS-e por empenho — o nº do empenho vai no NOME do item).
  // Rascunho criado com autoTransmit=false: só emite quando clicar Transmitir.
  const openPartialNf = async (inv: any) => {
    // Item que já está numa NFS-e ativa abre desmarcado e travado: marcá-lo
    // era garantir a recusa do backend ("Soma das NFS-e ativas... ultrapassa
    // o total da fatura"). Ver utils/nfParcial.ts.
    setAbrindoPartialNf(true);
    let cobertura: ReturnType<typeof coberturaDaNf> | null = null;
    try {
      const notas = await fiscalService.getByInvoice(inv.id);
      cobertura = coberturaDaNf(inv.items || [], Array.isArray(notas) ? notas : [], Number(inv.totalValue) || 0);
    } catch {
      cobertura = null;
    }
    setAbrindoPartialNf(false);
    const items = (inv.items || []).map((it: any, i: number) => {
      const nota = cobertura?.porItem[i] ?? null;
      return { ...it, _selected: !nota, _jaNotado: nota, _editName: it.name || '' };
    });
    const grupos = gruposPorEmpenho(inv.items || [], cobertura?.porItem ?? [], inv.empenhoNumber);
    setPartialNfGrupos(grupos.length >= 2 ? grupos : []);
    setPartialNfCobertura(cobertura ? { jaNotado: cobertura.jaNotado, resta: cobertura.resta } : null);
    const pendente = grupos.length >= 2 ? grupos.find(g => !g.concluido) : undefined;
    if (grupos.length >= 2) {
      // Uma nota por empenho: abre com o primeiro grupo pendente marcado, e só
      // ele. Marcar tudo misturaria dois empenhos na mesma NFS-e.
      setPartialNfItems(pendente ? marcarGrupo(items, pendente) : items.map((x: any) => ({ ...x, _selected: false })));
      setPartialNfGrupoAtivo(pendente ? pendente.chave : null);
      setPartialNfEmpenho(pendente ? pendente.empenho : '');
      setPartialNfModal(true);
      return;
    }
    setPartialNfItems(items);
    setPartialNfGrupoAtivo(null);
    // Vem preenchido quando o numero ja foi registrado na fatura (o caminho
    // normal: a gestora manda por WhatsApp dias antes da emissao).
    const jaRegistrado = inv.empenhoNumber || '';
    setPartialNfEmpenho(jaRegistrado);
    setPartialNfModal(true);
    if (jaRegistrado) setTimeout(() => applyEmpenhoToItems(jaRegistrado), 0);
  };

  /**
   * Marca só os itens livres do grupo e põe o empenho dele no nome (é assim que
   * o empenho sai na NFS-e). Item já notado fica desmarcado, como no #178.
   */
  const marcarGrupo = (arr: any[], g: GrupoDeEmpenho) =>
    arr.map((x: any, i: number) => {
      if (x._jaNotado) return { ...x, _selected: false };
      if (!g.indices.includes(i)) return { ...x, _selected: false };
      const base = nomeSemEmpenho(x._editName || x.name);
      return { ...x, _selected: true, _editName: g.empenho ? `${base} — Nota de Empenho nº ${g.empenho}` : base };
    });

  const escolherGrupoDeEmpenho = (g: GrupoDeEmpenho) => {
    setPartialNfItems(arr => marcarGrupo(arr, g));
    setPartialNfGrupoAtivo(g.chave);
    setPartialNfEmpenho(g.empenho);
  };

  const salvarEmpenho = async () => {
    if (!selectedInvoice) return;
    setSalvandoEmpenho(true);
    try {
      await fiscalService.registrarEmpenho(selectedInvoice.id, empenhoRegistro.trim());
      setSelectedInvoice({ ...selectedInvoice, empenhoNumber: empenhoRegistro.trim() || null });
      loadData();
    } catch (e: any) {
      avisar('Erro ao registrar empenho: ' + (e?.response?.data?.message || e.message), 'error');
    } finally {
      setSalvandoEmpenho(false);
    }
  };

  const handleCreatePartialNf = async () => {
    if (!selectedInvoice) return;
    const chosen = partialNfItems
      .filter(it => it._selected)
      .map(({ _selected, _editName, ...it }) => ({
        name: _editName || it.name,
        quantity: Number(it.quantity) || 1,
        unitValue: Number(it.unitValue) || 0,
        totalValue: Number(it.totalValue) || 0,
        description: it.description || undefined,
        productId: it.productId || undefined,
        productCode: it.productCode || undefined,
        period: it.period || undefined,
      }));
    if (chosen.length === 0) {
      avisar('Selecione ao menos um item', 'warning');
      return;
    }
    // Mesma conta do backend, feita antes, para o usuário ver o porquê.
    const totalEscolhido = chosen.reduce((s, it) => s + it.totalValue, 0);
    if (partialNfCobertura && totalEscolhido > partialNfCobertura.resta + 0.01) {
      avisar(
        `Esta NFS-e (R$ ${fmt(totalEscolhido)}) passa do que ainda falta notar nesta fatura ` +
          `(R$ ${fmt(partialNfCobertura.resta)}): já existem R$ ${fmt(partialNfCobertura.jaNotado)} em NFS-e ativas.`
      , 'warning');
      return;
    }
    setCreatingPartialNf(true);
    try {
      const doc = await fiscalService.createPartialNfse(selectedInvoice.id, chosen, partialNfEmpenho.trim() || undefined);
      avisar(
        `✅ Rascunho de NFS-e parcial criado (R$ ${fmt(doc.totalValue)}).\n\n` +
          `Ele NÃO será transmitido automaticamente — vá em Notas Fiscais → Documentos e clique Transmitir quando quiser emitir.`
      , 'info');
      // Com grupos por empenho e outro grupo ainda pendente, reabre já com as
      // notas atualizadas: o grupo emitido aparece concluído e o próximo vem
      // marcado. Sem grupos, fecha como antes.
      const outroPendente = partialNfGrupos.some(g => !g.concluido && g.chave !== partialNfGrupoAtivo);
      if (outroPendente) {
        await openPartialNf(selectedInvoice);
      } else {
        setPartialNfModal(false);
      }
      try {
        const data = await invoicesService.getFiscalDocumentsPaymentStatus(selectedInvoice.id);
        setFiscalDocsStatus(data);
      } catch {
        /* noop */
      }
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao criar NFS-e parcial', 'error');
    }
    setCreatingPartialNf(false);
  };

  // O confirm de cancelar e de reabrir lê a fatura e as notas NA HORA (utils/confirmacaoDeSaida): a lista pode estar
  // velha, e o que fica vivo é o que está no servidor agora. Nota que não deu para ler é dita como "não conferi".
  const lerOQueFicaVivo = async (id: number) => {
    const daLista: any = invoices.find(i => i.id === id);
    const [fatura, notas] = await Promise.allSettled([
      invoicesService.getById(id),
      invoicesService.getFiscalDocumentsPaymentStatus(id),
    ]);
    return {
      inv: (fatura.status === 'fulfilled' && fatura.value) || daLista || { code: null },
      notas: notas.status === 'fulfilled' ? (notas.value?.fiscalDocuments || []) : null,
      motivo: notas.status === 'rejected' ? mensagemDoErroDeCarga(notas.reason) : null,
    };
  };

  const handleCancel = async (id: number) => {
    const { inv, notas, motivo } = await lerOQueFicaVivo(id);
    const c = confirmacaoDeSaida('cancelar', inv, notas, motivo);
    if (!(await confirmar({ title: c.titulo, message: c.mensagem, danger: true, confirmLabel: 'Cancelar fatura', cancelLabel: 'Voltar' }))) return;
    try {
      await invoicesService.cancel(id);
      await loadData();
    } catch (e) {
      avisarFalhaDaAcao('cancelar', id, e);
    }
  };

  const handleReopen = async (id: number) => {
    const { inv, notas, motivo } = await lerOQueFicaVivo(id);
    const c = confirmacaoDeSaida('reabrir', inv, notas, motivo);
    if (!(await confirmar({ title: c.titulo, message: c.mensagem, confirmLabel: 'Reabrir', cancelLabel: 'Voltar' }))) return;
    try {
      await invoicesService.reopen(id);
      await loadData();
    } catch (e) {
      avisarFalhaDaAcao('reabrir', id, e);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirmarTexto('Excluir esta fatura?\n\nEsta ação não pode ser desfeita.', true))) return;
    try {
      await invoicesService.delete(id);
      await loadData();
    } catch (e) {
      avisarFalhaDaAcao('excluir', id, e);
    }
  };

  const handleDuplicate = async (invoice: Invoice) => {
    const nextMonth = getNextMonth(invoice.referenceMonth);
    if (!(await confirmarTexto(`Duplicar fatura para ${formatMonth(nextMonth)}?`))) return;
    try {
      await invoicesService.duplicate(invoice.id, nextMonth);
      await loadData();
    } catch (e: any) {
      // Antes só logava: nada aparecia, e o operador clicava de novo. Se a primeira chamada tinha gravado e só a
      // resposta se perdeu (timeout, deploy), saía fatura DUPLICADA. Agora: o servidor recusou (4xx) → não duplicou,
      // e diz por quê; a resposta não chegou ou foi 5xx → não dá para saber, então recarrega a lista e manda conferir
      // ANTES de tentar de novo.
      const status = e?.response?.status;
      if (status && status < 500) {
        avisar(`Não duplicou ${invoice.code} para ${formatMonth(nextMonth)}: ${mensagemDoErroDeCarga(e)}.`, 'error');
      } else {
        await loadData();
        avisar(`Não sei se ${invoice.code} foi duplicada para ${formatMonth(nextMonth)}: ${mensagemDoErroDeCarga(e)}. Recarreguei a lista: confira se a fatura de ${formatMonth(nextMonth)} já existe antes de duplicar de novo.`, 'error');
      }
    }
  };

  const handleGenerateTechnicalReport = async (invoice: Invoice) => {
    try {
      const client = clients.find((c: any) => c.id === invoice.clientId);
      if (!client?.ec2InstanceId) {
        avisar('Este cliente não possui instância EC2 configurada. Configure no cadastro do cliente.', 'warning');
        return;
      }

      // Abre o PDF em uma nova aba (URL com token de download de 60s)
      window.open(await invoicesService.getTechnicalReportPdfUrl(invoice.id), '_blank');
    } catch (e) {
      console.error('Erro ao gerar relatório técnico:', e);
      avisar('Erro ao gerar relatório técnico', 'error');
    }
  };

  const openDownloadModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDownloadAttachment(null);
    setModal('download');
  };

  // CDR Processing
  const handleCdrFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result as string;
      setCdrContent(content);
    };
    reader.readAsText(file);
  };

  const handleCdrFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleCdrFiles(e.target.files);
  };

  const processCdr = async () => {
    if (!cdrContent) return;
    setCdrProcessing(true);
    try {
      // Construir filtros de clientes a partir das configurações de CDR
      const clientFilters: { [tag: string]: string[] } = {};
      clients.forEach((client: any) => {
        if (client.cdrTag && client.cdrStatusFilter && client.cdrStatusFilter.length > 0) {
          clientFilters[client.cdrTag] = client.cdrStatusFilter;
        }
      });

      // Construir configurações de classificação POR CLIENTE (cada cliente pode ter sua própria configuração)
      const clientClassificationConfigs: {
        [cdrTag: string]: {
          entrada0800DestinationPatterns?: string[];
          localAreaCode?: string
        }
      } = {};

      // Associar configurações de contrato ao cdrTag do cliente
      const activeContracts = contracts.filter((c: any) => c.status === 'Ativo');
      for (const contract of activeContracts) {
        // Encontrar o cliente deste contrato para pegar o cdrTag
        const client = clients.find((c: any) => c.id === contract.clientId);
        const cdrTag = client?.cdrTag;

        if (cdrTag && (contract.entrada0800DestinationPatterns || contract.localAreaCode)) {
          clientClassificationConfigs[cdrTag] = {};

          if (contract.entrada0800DestinationPatterns) {
            clientClassificationConfigs[cdrTag].entrada0800DestinationPatterns = contract.entrada0800DestinationPatterns
              .split(',')
              .map((p: string) => p.trim())
              .filter((p: string) => p.length > 0);
          }
          if (contract.localAreaCode) {
            clientClassificationConfigs[cdrTag].localAreaCode = contract.localAreaCode;
          }
        }
      }

      // Usar o novo endpoint se tiver configurações de classificação
      const hasConfig = Object.keys(clientClassificationConfigs).length > 0;
      const result = hasConfig
        ? await invoicesService.processCdrWithConfig(cdrContent, clientClassificationConfigs, Object.keys(clientFilters).length > 0 ? clientFilters : undefined)
        : await invoicesService.processCdr(cdrContent, Object.keys(clientFilters).length > 0 ? clientFilters : undefined);
      setCdrResult(result);

      // Auto-select contracts and invoices based on cdrTag, name, or tradeName
      if (result?.clients) {
        const newContractMap: { [key: string]: number } = {};
        const newInvoiceMap: { [key: string]: number } = {};

        const clientesDoCdr = new Set<number>();
        result.clients.forEach((cdrClient: any) => {
          const key = `${cdrClient.clientTag}_${cdrClient.direction}`;
          const matchingClient: any = clienteDoBlocoCdr(cdrClient.clientTag, clients as any[]);
          if (!matchingClient) return;
          clientesDoCdr.add(matchingClient.id);
          const matchingContract = contracts.find((c: any) => c.clientId === matchingClient.id && c.status === 'Ativo');
          if (matchingContract) newContractMap[key] = matchingContract.id;
          // Só Rascunho. Até 05/10/2026 pegava Rascunho OU Aberta, e a Aberta não aparecia no
          // select: o lote aplicava numa fatura que ninguém via na tela.
          const pre = faturaPreSelecionada(invoices, matchingClient.id);
          if (pre) newInvoiceMap[key] = pre;
        });

        setCdrContractMap(newContractMap);
        setCdrInvoiceMap(newInvoiceMap);
        carregarNotasDoCdr(Array.from(clientesDoCdr));
      }
    } catch (e) {
      avisar(`Não consegui processar o CDR: ${mensagemDoErroDeCarga(e)}.`, 'error');
    }
    setCdrProcessing(false);
  };

  const downloadCdrCsv = async (clientTag: string, direction: 'entrada' | 'entrada_0800' | 'saida') => {
    try {
      const result = await invoicesService.getCdrCsv(cdrContent, clientTag, direction);
      if (result.error) {
        // O servidor respondeu com o erro no corpo, e não como exceção: antes ia só para o console (a 16ª forma).
        avisar(`Não consegui gerar o CSV do CDR: ${result.error}.`, 'error');
        return;
      }
      // Decode base64 and download
      const byteCharacters = atob(result.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      avisar(`Não consegui baixar o CSV do CDR: ${mensagemDoErroDeCarga(e)}.`, 'error');
    }
  };

  /** CSV das faturas que estão na lista agora (com os filtros aplicados). Antes era o botão "CSV" da barra. */
  const exportarCsvDaLista = () => {
    const header = ['Codigo', 'Cliente', 'Competencia', 'Vencimento', 'Valor', 'Status', 'Data Pgto', 'Juros/Multa'];
    const rows = filteredInvoices.map((inv: any) => {
      const client = clients.find((c: any) => c.id === inv.clientId);
      const competencia = inv.periodStart ? inv.periodStart.split('T')[0].slice(0, 7) : (inv.referenceMonth || '');
      return [inv.code, nomeDeExibicao(client), competencia, inv.dueDate?.split('T')[0] || '', inv.totalValue || 0, inv.status || '', inv.paymentDate?.split('T')[0] || '', inv.jurosMulta || 0].map((v: any) => { const s = String(v); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; }).join(',');
    });
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `faturas_${getTodayStr()}.csv`;
    link.click();
  };

  const handleExportExcel = async () => {
    setExcelExporting(true);
    try {
      const exportMonth = periodFilter.months.length === 1 ? periodFilter.months[0] : undefined;
      const blob = await invoicesService.exportExcel(exportMonth, statusFilter !== 'Todos' ? statusFilter : undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cobrancas_${periodFilter.months.length === 1 ? periodFilter.months[0] : periodFilter.months.length > 1 ? [...periodFilter.months].sort().join('_') : 'todos'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erro ao exportar Excel:', e);
      avisar('Erro ao exportar Excel', 'error');
    }
    setExcelExporting(false);
  };

  // Uma consulta por cliente do CDR (não por fatura). Falha não trava: o rótulo diz
  // "notas não carregadas" em vez de afirmar "sem nota".
  const carregarNotasDoCdr = (ids: number[]) => {
    setCdrNotasPorCliente(Object.fromEntries(ids.map((id) => [id, null])));
    ids.forEach((id) => {
      fiscalService.getAll({ clientId: id })
        .then((docs: any) => {
          const lista = Array.isArray(docs) ? docs : docs?.data || [];
          const notas: NotaDoCdr[] = lista.map((d: any) => ({ invoiceId: d.invoiceId, invoiceGroupId: d.invoiceGroupId, type: d.type, status: d.status, documentNumber: d.documentNumber }));
          setCdrNotasPorCliente((m) => ({ ...m, [id]: notas }));
        })
        .catch(() => {});
    });
  };

  /** As faturas do bloco, com o estado de cada uma no rótulo. */
  const opcoesDoBlocoCdr = (clientTag: string) => {
    const c: any = clienteDoBlocoCdr(clientTag, clients as any[]);
    if (!c) return { cliente: null, opcoes: [] as ReturnType<typeof opcoesDeFaturaParaCdr> };
    const nome = c.name?.trim() || c.tradeName?.trim() || c.companyName || 'Cliente';
    return { cliente: c, opcoes: opcoesDeFaturaParaCdr({ id: c.id, nome }, invoices, cdrNotasPorCliente[c.id] ?? null, fmt) };
  };

  const openCdrModal = () => {
    setCdrNotasPorCliente({});
    setCdrContent('');
    setCdrResult(null);
    setCdrContractMap({});
    setCdrInvoiceMap({});
    setCdrAppliedContracts([]);
    setCdrAppliedInvoices([]);
    setModal('cdr');
  };

  // Aplicar minutos do CDR na assinatura
  const applyMinutesToContract = async (clientTag: string, direction: string, minutesBilled: any) => {
    const key = `${clientTag}_${direction}`;
    const contractId = cdrContractMap[key];
    if (!contractId) {
      avisar('Selecione uma assinatura primeiro', 'warning');
      return;
    }

    setCdrApplying(key);
    try {
      // Buscar contrato atual
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) {
        avisar('Assinatura não encontrada', 'error');
        return;
      }

      const updateData: any = { ...contract };

      if (direction === 'entrada') {
        // Minutos de ENTRADA NORMAL (apenas relatório, R$0,00)
        updateData.usageMinutesEntradaNormal = {
          ...(contract.usageMinutesEntradaNormal || {}),
          entrada: minutesBilled.entrada || 0,
        };
        updateData.hasNormalEntrada = true;
      } else if (direction === 'entrada_0800') {
        // Minutos de ENTRADA 0800 (cobrada)
        updateData.usageMinutesEntrada = {
          ...(contract.usageMinutesEntrada || {}),
          entrada: minutesBilled.entrada || 0,
        };
        updateData.has0800Link = true;
      } else {
        // Minutos de SAÍDA
        updateData.usageMinutes = {
          ...(contract.usageMinutes || {}),
          celularLDN: minutesBilled.celularLDN || 0,
          celularLocal: minutesBilled.celularLocal || 0,
          fixoLDN: minutesBilled.fixoLDN || 0,
          fixoLocal: minutesBilled.fixoLocal || 0,
          ldi: minutesBilled.ldi || 0,
          especial: minutesBilled.especial || 0,
          emergencial: minutesBilled.emergencial || 0,
        };
        updateData.includeUsageItems = true; // Garante que vai incluir na fatura
      }

      await contractsService.update(contractId, updateData);

      // Recarregar contratos
      const updatedContracts = await contractsService.getAll();
      setContracts(updatedContracts);

      // Marcar como aplicado
      setCdrAppliedContracts([...cdrAppliedContracts, key]);
      const tipoMsg = direction === 'entrada' ? 'entrada (normal)' : (direction === 'entrada_0800' ? 'entrada (0800)' : 'saída');
      avisar(`Minutos de ${tipoMsg} aplicados na assinatura #${contractId} com sucesso!`, 'success');
    } catch (e) {
      console.error('Erro ao aplicar minutos:', e);
      avisar('Erro ao aplicar minutos na assinatura', 'error');
    }
    setCdrApplying(null);
  };

  const applyCdrToInvoiceForClient = async (clientTag: string, direction: string, clientData: any) => {
    const key = `${clientTag}_${direction}`;
    const invoiceId = cdrInvoiceMap[key];
    if (!invoiceId) {
      avisar('Selecione uma fatura primeiro', 'warning');
      return;
    }

    // Buscar fatura e contrato para verificar flags
    const currentInvoice = invoices.find(i => i.id === invoiceId);
    const contract = contracts.find(c => c.id === currentInvoice?.contractId);

    const risco = riscoDeAplicar(opcoesDoBlocoCdr(clientTag).opcoes.find((o) => o.id === invoiceId));
    if (risco && !(await confirmarTexto(`${risco}\n\nAplicar mesmo assim?`, true))) return;

    // Verificar se o contrato permite essa direção de entrada
    if (direction === 'entrada' && contract && !contract.hasNormalEntrada) {
      if (!(await confirmarTexto(`O contrato não tem "Entrada Normal" habilitada.\n\nDeseja aplicar mesmo assim? Os minutos aparecerão na fatura mas sem cobrança.`))) {
        return;
      }
    }
    if (direction === 'entrada_0800' && contract && !contract.has0800Link) {
      if (!(await confirmarTexto(`O contrato não tem "Link 0800" habilitado.\n\nDeseja aplicar mesmo assim? Os minutos aparecerão na fatura mas sem cobrança.`))) {
        return;
      }
    }

    setCdrApplyingInvoice(key);
    try {
      // Coletar registros apenas deste cliente
      const records: any[] = [];
      const usageItems: any[] = [];

      // Adicionar registros detalhados
      for (const record of (clientData.records || [])) {
        records.push({
          dateTime: record.dateTime instanceof Date ? record.dateTime.toISOString() : (record.dateTime || new Date().toISOString()),
          origin: String(record.origin || ''),
          destination: String(record.destination || ''),
          tag: String(record.tag || clientTag || ''),
          status: String(record.status || 'DESCONHECIDO'),
          totalDuration: Number(record.totalDuration) || 0,
          talkDuration: Number(record.talkDuration) || 0,
          billedDuration: record.status === 'ATENDIDA' ? calculateBilledSeconds(Number(record.talkDuration) || 0) : 0,
          direction: direction || 'saida',
        });
      }

      // Agregar por tipo de tarifa
      const tariffTypes = ['celularLDN', 'celularLocal', 'fixoLDN', 'fixoLocal', 'ldi', 'especial', 'emergencial', 'entrada'];
      const tariffNames: any = {
        celularLDN: 'Celular LDN',
        celularLocal: 'Celular Local',
        fixoLDN: 'Fixo LDN',
        fixoLocal: 'Fixo Local',
        ldi: 'LDI',
        especial: 'Especial',
        emergencial: 'Emergencial',
        entrada: 'Entrada',
      };

      // Para ENTRADA NORMAL: somar tudo em um único item "Entrada"
      // Para SAÍDA e ENTRADA 0800: manter separado por tipo
      if (direction === 'entrada') {
        // Entrada Normal - somar todos os minutos em um único total
        let totalEntradaMinutes = 0;
        for (const key of tariffTypes) {
          totalEntradaMinutes += clientData.minutesBilledByType?.[key] || 0;
        }
        if (totalEntradaMinutes > 0) {
          usageItems.push({
            tariff: 'Entrada',
            minutes: totalEntradaMinutes,
            unitValue: 0,
            totalValue: 0,
            period: '',
          });
        }
      } else {
        // Saída ou Entrada 0800 - manter separado por tipo
        for (const key of tariffTypes) {
          const minutes = clientData.minutesBilledByType?.[key] || 0;
          if (minutes > 0) {
            usageItems.push({
              tariff: tariffNames[key],
              minutes: minutes,
              unitValue: 0,
              totalValue: 0,
              period: '',
            });
          }
        }
      }

      // Buscar fatura atual para manter dados de OUTRA direção
      // (via getById: a listagem não traz mais cdrRecords — payload de 100MB)
      const currentInvoice = await invoicesService.getById(invoiceId);
      const existingRecords = registrosDoCdrJaAplicados(currentInvoice);

      // Filtrar registros CDR existentes - remover os da mesma direção e adicionar os novos
      const otherDirectionRecords = existingRecords.filter((r: any) => r.direction !== direction);

      // Substituir dados da direção atual (não concatenar para evitar duplicação)
      const updateData: any = {
        cdrRecords: [...otherDirectionRecords, ...records],
      };

      if (direction === 'entrada') {
        // Entrada Normal - substituir usageItemsEntradaNormal completamente
        updateData.usageItemsEntradaNormal = usageItems;
      } else if (direction === 'entrada_0800') {
        // Entrada 0800 - substituir usageItemsEntrada completamente
        updateData.usageItemsEntrada = usageItems;
      } else {
        // Substituir usageItems (saída) completamente
        updateData.usageItems = usageItems;
      }

      await invoicesService.update(invoiceId, updateData);

      // Recarregar faturas
      await loadData();

      // Marcar como aplicado
      setCdrAppliedInvoices([...cdrAppliedInvoices, `${clientTag}_${direction}`]);
      avisar(`CDR de ${clientTag} (${direction}) aplicado na fatura com sucesso! ${records.length} ligações anexadas.`, 'success');
    } catch (e) {
      console.error('Erro ao aplicar CDR na fatura:', e);
      // Nada foi gravado: a leitura do CDR existente vem ANTES do update (ver registrosDoCdrJaAplicados).
      avisar(`Não apliquei o CDR de ${clientTag} (${direction}): ${mensagemDoErroDeCarga(e)}. Nada foi gravado na fatura.`, 'error');
    }
    setCdrApplyingInvoice(null);
  };

  // Apply all CDRs to invoices at once
  const [applyingAll, setApplyingAll] = useState(false);
  const [applyingAllContracts, setApplyingAllContracts] = useState(false);
  const applyAllCdrToInvoices = async () => {
    if (!cdrResult?.clients) return;

    // Get pending clients (not already applied)
    const pendingClients = cdrResult.clients.filter((client: any) => {
      const key = `${client.clientTag}_${client.direction}`;
      return !cdrAppliedInvoices.includes(key) && cdrInvoiceMap[key];
    });

    // Quem fica de fora por não ter fatura: dito no confirmar e no fim, nunca pulado calado.
    const semFatura: string[] = cdrResult.clients
      .filter((client: any) => {
        const key = `${client.clientTag}_${client.direction}`;
        return !cdrAppliedInvoices.includes(key) && !cdrInvoiceMap[key];
      })
      .map((client: any) => `${client.clientTag} (${client.direction})`);

    if (pendingClients.length === 0) {
      avisar(`Nenhum CDR pendente com fatura selecionada.${semFatura.length ? ` Sem fatura: ${semFatura.join(', ')}.` : ''}`, 'warning');
      return;
    }

    const riscos = Array.from(new Set(pendingClients
      .map((client: any) => riscoDeAplicar(opcoesDoBlocoCdr(client.clientTag).opcoes.find((o) => o.id === cdrInvoiceMap[`${client.clientTag}_${client.direction}`])))
      .filter(Boolean))) as string[];
    const textoDoLote = [
      `Aplicar CDR em ${pendingClients.length} bloco(s)?`,
      semFatura.length ? `Ficam de fora, sem fatura selecionada: ${semFatura.join(', ')}.` : '',
      ...riscos,
    ].filter(Boolean).join('\n\n');
    if (!(await confirmarTexto(textoDoLote, riscos.length > 0))) return;

    setApplyingAll(true);
    let successCount = 0;
    let errorCount = 0;
    const appliedKeys: string[] = [];

    // Cache local de cdrRecords por fatura - importante para quando múltiplas direções usam a mesma fatura
    const cdrRecordsCache: { [invoiceId: number]: any[] } = {};
    const naoLidas: string[] = [];
    let motivoNaoLidas: string | null = null;

    const semFlag: string[] = [];
    for (const client of pendingClients) {
      const key = `${client.clientTag}_${client.direction}`;
      const invoiceId = cdrInvoiceMap[key];
      if (!invoiceId) continue;

      // Inicializar cache com dados existentes se ainda não foi feito
      // (via getById: a listagem não traz mais cdrRecords — payload de 100MB)
      // ⛔ FALHA FECHADA (08/10/2026): sem ler o CDR que já está na fatura, este bloco NÃO é aplicado. Antes, a leitura
      // falhando virava [] e o update gravava só a direção atual, apagando as outras (ver registrosDoCdrJaAplicados).
      if (cdrRecordsCache[invoiceId] === undefined) {
        try {
          cdrRecordsCache[invoiceId] = registrosDoCdrJaAplicados(await invoicesService.getById(invoiceId));
        } catch (e) {
          naoLidas.push(`${client.clientTag} (${client.direction})`);
          motivoNaoLidas = motivoNaoLidas || mensagemDoErroDeCarga(e);
          continue;
        }
      }

      // Buscar fatura e contrato para verificar flags
      const currentInvoice = invoices.find(i => i.id === invoiceId);
      const contract = contracts.find(c => c.id === currentInvoice?.contractId);

      // Pular entrada normal se não tiver flag habilitada
      if (client.direction === 'entrada' && contract && !contract.hasNormalEntrada) {
        semFlag.push(`${client.clientTag} (entrada)`);
        continue;
      }
      // Pular entrada 0800 se não tiver flag habilitada
      if (client.direction === 'entrada_0800' && contract && !contract.has0800Link) {
        semFlag.push(`${client.clientTag} (entrada_0800)`);
        continue;
      }

      try {
        // Collect records for this client (same logic as individual apply)
        const records: any[] = [];
        const usageItems: any[] = [];

        for (const record of (client.records || [])) {
          records.push({
            dateTime: record.dateTime instanceof Date ? record.dateTime.toISOString() : (record.dateTime || new Date().toISOString()),
            origin: String(record.origin || ''),
            destination: String(record.destination || ''),
            tag: String(record.tag || client.clientTag || ''),
            status: String(record.status || 'DESCONHECIDO'),
            totalDuration: Number(record.totalDuration) || 0,
            talkDuration: Number(record.talkDuration) || 0,
            billedDuration: record.status === 'ATENDIDA' ? calculateBilledSeconds(Number(record.talkDuration) || 0) : 0,
            direction: client.direction || 'saida',
          });
        }

        const tariffNames: { [key: string]: string } = {
          celularLocal: 'Celular Local',
          celularLDN: 'Celular LDN',
          fixoLocal: 'Fixo Local',
          fixoLDN: 'Fixo LDN',
          ldi: 'LDI',
          especial: 'Especial',
          entrada: 'Entrada',
          emergencial: 'Emergencial',
        };
        const tariffTypes = ['celularLDN', 'celularLocal', 'fixoLDN', 'fixoLocal', 'ldi', 'especial', 'emergencial', 'entrada'];

        // Para ENTRADA NORMAL: somar tudo em um único item "Entrada"
        // Para SAÍDA e ENTRADA 0800: manter separado por tipo
        if (client.direction === 'entrada') {
          // Entrada Normal - somar todos os minutos em um único total
          let totalEntradaMinutes = 0;
          for (const tariffKey of tariffTypes) {
            totalEntradaMinutes += client.minutesBilledByType?.[tariffKey] || 0;
          }
          if (totalEntradaMinutes > 0) {
            usageItems.push({
              tariff: 'Entrada',
              minutes: totalEntradaMinutes,
              unitValue: 0,
              totalValue: 0,
              period: '',
            });
          }
        } else {
          // Saída ou Entrada 0800 - manter separado por tipo
          for (const tariffKey of tariffTypes) {
            const minutes = client.minutesBilledByType?.[tariffKey] || 0;
            if (minutes > 0) {
              usageItems.push({
                tariff: tariffNames[tariffKey],
                minutes: minutes,
                unitValue: 0,
                totalValue: 0,
                period: '',
              });
            }
          }
        }

        // Usar cache local para preservar cdrRecords de outras direções
        // (o state 'invoices' não é atualizado entre iterações do loop)
        const existingRecords = cdrRecordsCache[invoiceId] || [];

        // Filter existing records - remove those from same direction, keep others
        const otherDirectionRecords = existingRecords.filter((r: any) => r.direction !== client.direction);

        // Atualizar o cache local com os novos registros
        const newCdrRecords = [...otherDirectionRecords, ...records];
        cdrRecordsCache[invoiceId] = newCdrRecords;

        const updateData: any = {
          cdrRecords: newCdrRecords,
        };

        if (client.direction === 'entrada') {
          // Entrada Normal
          updateData.usageItemsEntradaNormal = usageItems;
        } else if (client.direction === 'entrada_0800') {
          // Entrada 0800
          updateData.usageItemsEntrada = usageItems;
        } else {
          updateData.usageItems = usageItems;
        }

        await invoicesService.update(invoiceId, updateData);
        appliedKeys.push(key);
        successCount++;
      } catch (e) {
        console.error(`Erro ao aplicar CDR para ${client.clientTag}:`, e);
        errorCount++;
      }
    }

    setCdrAppliedInvoices(prev => [...prev, ...appliedKeys]);
    await loadData();
    setApplyingAll(false);
    const resumo = resumoDoLote({ aplicados: successCount, erros: errorCount, semFatura, semFlag, naoLidas, motivoNaoLidas });
    avisar(resumo.texto, resumo.tom);
  };

  const applyAllCdrToContracts = async () => {
    if (!cdrResult?.clients) return;

    // Get pending clients (not already applied to contracts)
    const pendingClients = cdrResult.clients.filter((client: any) => {
      const key = `${client.clientTag}_${client.direction}`;
      return !cdrAppliedContracts.includes(key) && cdrContractMap[key];
    });

    if (pendingClients.length === 0) {
      avisar('Nenhum CDR pendente para aplicar nas assinaturas. Verifique se as assinaturas estão selecionadas.', 'warning');
      return;
    }

    if (!(await confirmarTexto(`Aplicar minutos em ${pendingClients.length} assinatura(s)?`))) return;

    setApplyingAllContracts(true);
    let successCount = 0;
    let errorCount = 0;
    const appliedKeys: string[] = [];

    for (const client of pendingClients) {
      const key = `${client.clientTag}_${client.direction}`;
      const contractId = cdrContractMap[key];
      if (!contractId) continue;

      try {
        const contract = contracts.find(c => c.id === contractId);
        if (!contract) continue;

        const updateData: any = { ...contract };

        if (client.direction === 'entrada') {
          // Entrada Normal
          updateData.usageMinutesEntradaNormal = {
            ...(contract.usageMinutesEntradaNormal || {}),
            entrada: client.minutesBilledByType?.entrada || 0,
          };
          updateData.hasNormalEntrada = true;
        } else if (client.direction === 'entrada_0800') {
          // Entrada 0800
          updateData.usageMinutesEntrada = {
            ...(contract.usageMinutesEntrada || {}),
            entrada: client.minutesBilledByType?.entrada || 0,
          };
          updateData.has0800Link = true;
        } else {
          updateData.usageMinutes = {
            ...(contract.usageMinutes || {}),
            celularLDN: client.minutesBilledByType?.celularLDN || 0,
            celularLocal: client.minutesBilledByType?.celularLocal || 0,
            fixoLDN: client.minutesBilledByType?.fixoLDN || 0,
            fixoLocal: client.minutesBilledByType?.fixoLocal || 0,
            ldi: client.minutesBilledByType?.ldi || 0,
            especial: client.minutesBilledByType?.especial || 0,
            emergencial: client.minutesBilledByType?.emergencial || 0,
          };
          updateData.includeUsageItems = true;
        }

        await contractsService.update(contractId, updateData);
        appliedKeys.push(key);
        successCount++;
      } catch (e) {
        console.error(`Erro ao aplicar minutos para ${client.clientTag}:`, e);
        errorCount++;
      }
    }

    setCdrAppliedContracts(prev => [...prev, ...appliedKeys]);
    const updatedContracts = await contractsService.getAll();
    setContracts(updatedContracts);
    setApplyingAllContracts(false);
    avisar(`Minutos aplicados: ${successCount} sucesso(s), ${errorCount} erro(s)`, 'error');
  };

  // Calcular segundos tarifados (cadência 0/30/6)
  const calculateBilledSeconds = (talkDurationSeconds: number): number => {
    if (talkDurationSeconds <= 0) return 0;
    if (talkDurationSeconds <= 30) return 30;
    const beyondFirst30 = talkDurationSeconds - 30;
    const fractions = Math.ceil(beyondFirst30 / 6);
    return 30 + (fractions * 6);
  };

  const handleAttachmentFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      setDownloadAttachment(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const type = file.name.endsWith('.csv') ? 'csv' : 'pdf';
      setDownloadAttachment({ data: base64, type, filename: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleAttachmentFiles(e.target.files);
  };

  const handleGetPdfLink = async (invoice: Invoice) => {
    try {
      const result = await invoicesService.getPdfToken(invoice.id);
      setPdfLink(result.url);
      setLinkCopied(false);
    } catch (e) {
      avisar(`Não consegui gerar o link do PDF de ${invoice.code}: ${mensagemDoErroDeCarga(e)}.`, 'error');
    }
  };

  const handleCopyPdfLink = async () => {
    if (!pdfLink) return;
    try {
      await navigator.clipboard.writeText(pdfLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = pdfLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  const handleDownloadPdf = async (invoice: Invoice, attachment?: { data: string; type: 'pdf' | 'csv'; filename?: string } | null) => {
    setDownloadingPdf(true);
    try {
      let blob;
      if (attachment) {
        blob = await invoicesService.getPdfWithAttachment(invoice.id, attachment);
      } else {
        blob = await invoicesService.getPdf(invoice.id);
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Formatar nome do arquivo: Cliente_MesAno_Codigo.pdf
      const clientName = nomeDeExibicao(invoice.client, 'Cliente')
        .replace(/[^a-zA-Z0-9À-ÿ\s]/g, '') // Remove caracteres especiais
        .replace(/\s+/g, '_') // Substitui espaços por underline
        .substring(0, 30); // Limita tamanho
      a.download = `Fatura_${clientName}_${getCompetenciaFileLabel(invoice)}_${invoice.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setModal(null);
    } catch (e) {
      avisar(`Não consegui baixar o PDF de ${invoice.code}: ${mensagemDoErroDeCarga(e)}.`, 'error');
    }
    setDownloadingPdf(false);
  };

  // Baixa o PDF exatamente como o CLIENTE recebe no e-mail: unificado (Fatura +
  // página do boleto com linha digitável) pra quem paga boleto; fatura limpa pros demais.
  const handleDownloadClientView = async (inv: Invoice) => {
    setDownloadingPdf(true);
    try {
      const isBoleto = inv.paymentMethod === 'boleto' && inv.nossoNumero;
      const blob = isBoleto
        ? await invoicesService.getUnifiedPdf(inv.id)
        : await invoicesService.getPdf(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isBoleto ? `IPCOM_${inv.code}_Fatura_Boleto.pdf` : `IPCOM_${inv.code}_Fatura.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erro ao baixar PDF (visão do cliente):', e);
      avisar('Erro ao baixar o PDF unificado.', 'error');
    }
    setDownloadingPdf(false);
  };

  // Lotes de download (todos os PDFs, os PDFs selecionados, os relatórios técnicos): cada falha ia para o console e a
  // barra chegava a N/N com menos arquivos que N, como se tivesse baixado tudo (07/10/2026). Agora diz quantos de
  // quantos e quais faltaram. Com tudo baixado, nada a dizer: os arquivos estão no navegador.
  const avisarLoteDeDownload = (oQue: string, total: number, faltaram: string[], motivo: string) => {
    if (faltaram.length === 0) return;
    avisar(`Baixei ${total - faltaram.length} de ${total} ${oQue}. Faltaram ${faltaram.length} (${faltaram.join(', ')}): ${motivo}.`, 'error');
  };

  const handleDownloadAllPdfs = async () => {
    if (filteredInvoices.length === 0) return;

    setDownloadingAll(true);
    setDownloadProgress({ current: 0, total: filteredInvoices.length });

    const faltaram: string[] = [];
    let motivo = '';
    for (let i = 0; i < filteredInvoices.length; i++) {
      const inv = filteredInvoices[i];
      try {
        const blob = await invoicesService.getPdf(inv.id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const client = clients.find((c) => c.id === inv.clientId);
        const clientName = (client?.name?.trim() || client?.companyName || 'Cliente')
          .replace(/[^a-zA-Z0-9À-ÿ\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 30);
        a.download = `Fatura_${clientName}_${getCompetenciaFileLabel(inv)}_${inv.code}.pdf`;
        a.click();
        URL.revokeObjectURL(url);

        setDownloadProgress({ current: i + 1, total: filteredInvoices.length });

        // Pequeno delay entre downloads para não sobrecarregar
        if (i < filteredInvoices.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (e) {
        faltaram.push(inv.code);
        motivo = motivo || mensagemDoErroDeCarga(e);
      }
    }

    avisarLoteDeDownload('PDF(s) de fatura', filteredInvoices.length, faltaram, motivo);
    setDownloadingAll(false);
    setDownloadProgress({ current: 0, total: 0 });
  };

  // Toggle seleção individual
  const toggleSelectInvoice = (id: number) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Toggle selecionar todas as faturas filtradas
  const toggleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
    }
  };

  // Limpar seleção
  const clearSelection = () => {
    setSelectedInvoices([]);
  };

  // Re-sincroniza o vencimento das faturas selecionadas pelo dia do cadastro do
  // cliente (fatura emitida não muda sozinha ao editar o cliente). Pula pagas/canceladas.
  const handleBulkResyncDueDates = async () => {
    if (selectedInvoices.length === 0) return;
    if (!(await confirmarTexto(`Atualizar o vencimento de ${selectedInvoices.length} fatura(s) pelo dia do cadastro do cliente?\n\nFaturas pagas/canceladas são ignoradas.`))) return;
    setBulkProcessing(true);
    try {
      const res = await invoicesService.resyncDueDates(selectedInvoices);
      const inalteradas = (res.results?.length || 0) - (res.updated || 0);
      avisar(`✓ Vencimentos atualizados: ${res.updated || 0}.\nSem alteração/ignoradas: ${inalteradas}.`, 'success');
      await loadData();
      clearSelection();
    } catch (e) {
      console.error(e);
      avisar('Erro ao atualizar vencimentos.', 'error');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Ações em lote
  const handleBulkDelete = async () => {
    // Só Rascunho, a mesma regra do menu da linha (utils/exclusaoDaFatura). Antes o lote aceitava Aberta e Cancelada,
    // e o servidor apagava a fatura junto com os registros fiscais, com NF autorizada e boleto vivo lá fora.
    const codigo = (id: number) => invoices.find(i => i.id === id)?.code || `#${id}`;
    const listar = (ids: number[]) => ids.slice(0, 8).map(codigo).join(', ') + (ids.length > 8 ? ` e mais ${ids.length - 8}` : '');
    const deletable = selectedInvoices.filter(id => podeExcluirFatura(invoices.find(i => i.id === id)));
    const deFora = selectedInvoices.filter(id => !deletable.includes(id));

    if (deletable.length === 0) {
      avisar('Nenhuma das faturas selecionadas pode ser excluída: só Rascunho se exclui. Fatura aprovada já gerou nota ou boleto; cancele em vez de excluir.', 'warning');
      return;
    }

    const linhas = [listar(deletable), 'Esta ação não pode ser desfeita.'];
    if (deFora.length > 0) {
      linhas.push(`${deFora.length} fatura(s) selecionada(s) não ${deFora.length === 1 ? 'é Rascunho e fica' : 'são Rascunho e ficam'} de fora: ${listar(deFora)}. Fatura aprovada se cancela, não se exclui.`);
    }
    if (!(await confirmar({
      title: `Excluir ${deletable.length} ${deletable.length === 1 ? 'rascunho' : 'rascunhos'}?`,
      message: linhas.join('\n\n'),
      danger: true,
      confirmLabel: `Excluir ${deletable.length}`,
      cancelLabel: 'Voltar',
    }))) return;

    setBulkProcessing(true);
    let deleted = 0;
    // Antes, cada falha ia para o console e o aviso final dizia "N excluída(s) com sucesso" contando só as que deram
    // certo: com 5 marcadas e 2 falhas, "3 excluídas com sucesso", e as 2 sumiam do relato. Número confiante o
    // operador anota; agora o aviso diz quantas de quantas, quais falharam e por quê.
    const falharam: string[] = [];
    let motivo = '';
    for (const id of deletable) {
      try {
        await invoicesService.delete(id);
        deleted++;
      } catch (e) {
        falharam.push(invoices.find((i) => i.id === id)?.code || `#${id}`);
        motivo = motivo || mensagemDoErroDeCarga(e);
      }
    }
    await loadData();
    setSelectedInvoices([]);
    setBulkProcessing(false);
    if (falharam.length === 0) {
      avisar(`${deleted} fatura(s) excluída(s) com sucesso.`, 'success');
    } else {
      avisar(`${deleted} de ${deletable.length} fatura(s) excluída(s). ${falharam.length} NÃO foi(ram) excluída(s) (${falharam.join(', ')}): ${motivo}.`, 'error');
    }
  };

  /**
   * Libera a retenção e aprova. É a saída explícita: chega o dia da janela ou o
   * órgão libera, e o operador confirma sabendo o motivo que estava barrando.
   */
  const handleLiberarEAprovar = async (inv: any) => {
    const motivo = motivoRetencao(inv);
    const { previa, erro } = await lerPreviaDoEmpenho(inv.id);
    if (previa) {
      setAprovacaoComEmpenho({ inv, previa, force: true, motivoRetencao: motivo || undefined });
      return;
    }
    if (!(await confirmarTexto(`Esta fatura está retida:\n\n${motivo}\n\nLiberar a retenção e aprovar ${inv.code} mesmo assim?${semLeituraDoEmpenho(erro)}`,))) return;
    try {
      await invoicesService.releaseHold(inv.id);
      await invoicesService.finalize(inv.id, true);
      avisar(`Fatura ${inv.code} liberada e aprovada.`, 'success');
      closeModal();
      loadData();
    } catch (e: any) {
      avisar('Erro ao liberar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleBulkFinalize = async () => {
    const drafts = selectedInvoices.filter(id => {
      const inv = invoices.find(i => i.id === id);
      return inv?.status === 'Rascunho';
    });

    if (drafts.length === 0) {
      avisar('Nenhuma fatura em Rascunho selecionada para aprovar.', 'warning');
      return;
    }

    // Retidas são separadas ANTES de enviar: o backend as pularia de qualquer
    // forma, mas em silêncio, e o operador acharia que aprovou tudo.
    const retidas = drafts
      .map(id => invoices.find(i => i.id === id))
      .filter(inv => inv && motivoRetencao(inv)) as any[];
    const liberadas = drafts.filter(id => !retidas.some(r => r.id === id));

    if (liberadas.length === 0) {
      avisar(
        `Nenhuma fatura pode ser aprovada agora.\n\n${retidas
          .map(r => `• ${r.code} — ${motivoRetencao(r)}`)
          .join('\n')}\n\nPara aprovar mesmo assim, abra a fatura e libere a retenção.`
      , 'warning');
      return;
    }

    const aviso = retidas.length
      ? `\n\n⚠ ${retidas.length} fatura(s) NÃO serão aprovadas por estarem retidas:\n${retidas
          .map(r => `• ${r.code} — ${motivoRetencao(r)}`)
          .join('\n')}`
      : '';

    if (!(await confirmarTexto(`Aprovar ${liberadas.length} fatura(s)? Elas mudarão de Rascunho para Aberta.${aviso}`,))) return;

    setBulkProcessing(true);
    try {
      const { jobId, total } = await invoicesService.finalizeBatch(liberadas);
      setBatchProgress({ jobId, type: 'finalize', total, completed: 0, percent: 0, errors: [], status: 'processing' });
    } catch (e: any) {
      avisar('Erro ao iniciar aprovação em lote: ' + (e.response?.data?.message || e.message), 'error');
    }
    setBulkProcessing(false);
  };

  const handleBulkDownload = async () => {
    if (selectedInvoices.length === 0) return;

    const selected = filteredInvoices.filter(inv => selectedInvoices.includes(inv.id));

    setBulkProcessing(true);
    setDownloadProgress({ current: 0, total: selected.length });

    const faltaram: string[] = [];
    let motivo = '';
    for (let i = 0; i < selected.length; i++) {
      const inv = selected[i];
      try {
        const blob = await invoicesService.getPdf(inv.id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const client = clients.find((c) => c.id === inv.clientId);
        const clientName = (client?.name?.trim() || client?.companyName || 'Cliente')
          .replace(/[^a-zA-Z0-9À-ÿ\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 30);
        a.download = `Fatura_${clientName}_${getCompetenciaFileLabel(inv)}_${inv.code}.pdf`;
        a.click();
        URL.revokeObjectURL(url);

        setDownloadProgress({ current: i + 1, total: selected.length });

        if (i < selected.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (e) {
        faltaram.push(inv.code);
        motivo = motivo || mensagemDoErroDeCarga(e);
      }
    }

    avisarLoteDeDownload('PDF(s) de fatura', selected.length, faltaram, motivo);
    setBulkProcessing(false);
    setDownloadProgress({ current: 0, total: 0 });
  };

  const handleBulkDownloadTechnicalReports = async () => {
    if (selectedInvoices.length === 0) return;

    const selected = filteredInvoices.filter(inv => selectedInvoices.includes(inv.id));

    // Filtra apenas faturas de clientes com EC2 configurado
    const withEc2 = selected.filter(inv => {
      const client = clients.find(c => c.id === inv.clientId);
      return client?.ec2InstanceId;
    });

    if (withEc2.length === 0) {
      avisar('Nenhuma das faturas selecionadas possui cliente com EC2 configurado.', 'warning');
      return;
    }

    if (withEc2.length < selected.length) {
      const skip = selected.length - withEc2.length;
      if (!(await confirmarTexto(`${skip} fatura(s) não possuem EC2 configurado e serão ignoradas. Continuar?`))) {
        return;
      }
    }

    setBulkProcessing(true);
    setDownloadProgress({ current: 0, total: withEc2.length });

    const faltaram: string[] = [];
    let motivo = '';
    for (let i = 0; i < withEc2.length; i++) {
      const inv = withEc2[i];
      try {
        const response = await fetch(await invoicesService.getTechnicalReportPdfUrl(inv.id));
        if (!response.ok) throw new Error('Erro ao gerar relatório');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const client = clients.find((c) => c.id === inv.clientId);
        const clientName = nomeDeExibicao(client, 'Cliente')
          .replace(/[^a-zA-Z0-9À-ÿ\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 30);
        const rtCode = inv.code.replace('FAT', 'RT');
        a.download = `RelatorioTecnico_${clientName}_${getCompetenciaFileLabel(inv)}_${rtCode}.pdf`;
        a.click();
        URL.revokeObjectURL(url);

        setDownloadProgress({ current: i + 1, total: withEc2.length });

        if (i < withEc2.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (e) {
        faltaram.push(inv.code);
        motivo = motivo || mensagemDoErroDeCarga(e);
      }
    }

    avisarLoteDeDownload('relatório(s) técnico(s)', withEc2.length, faltaram, motivo);
    setBulkProcessing(false);
    setDownloadProgress({ current: 0, total: 0 });
  };

  // Enviar fatura por email (individual) — abre modal de seleção
  const handleSendEmail = (invoiceId: number) => {
    const inv = invoices.find(i => i.id === invoiceId);
    const client = clients.find((c: any) => c.id === inv?.clientId);

    const financialEmails = (client?.emails || [])
      .filter((e: any) => e.type === 'Financeiro')
      .map((e: any) => e.email)
      .filter((e: string) => !!e);
    if (financialEmails.length === 0 && client?.emailFinancial) {
      financialEmails.push(client.emailFinancial);
    }

    setEmailModal({
      invoiceId,
      invoiceCode: inv?.code || '',
      emails: financialEmails.map((e: string) => ({ email: e, checked: true })),
      customEmail: '',
      customNote: '',
    });
  };

  const handleConfirmSendEmail = async () => {
    if (!emailModal) return;
    const selectedEmails = emailModal.emails.filter(e => e.checked).map(e => e.email);
    if (emailModal.customEmail.trim()) {
      selectedEmails.push(emailModal.customEmail.trim());
    }
    if (selectedEmails.length === 0) {
      avisar('Selecione ao menos um email', 'warning');
      return;
    }
    const invoiceId = emailModal.invoiceId;
    const customNote = emailModal.customNote?.trim() || undefined;
    setEmailModal(null);
    setSendingEmail(invoiceId);
    try {
      const result = await invoicesService.sendEmail(invoiceId, selectedEmails, customNote);
      if (result.success) {
        const count = result.sentTo?.length || 1;
        avisar(`Email enviado para ${count} destinatario(s): ${result.sentTo?.join(', ') || selectedEmails.join(', ')}`, 'success');
        setEmailStatusMap(prev => ({ ...prev, [invoiceId]: [{ status: 'sent', openCount: 0, sentAt: new Date().toISOString() }] }));
      } else {
        avisar(`Erro: ${result.error || 'Falha ao enviar email'}`, 'error');
      }
    } catch (e: any) {
      avisar('Erro ao enviar email: ' + (e.response?.data?.message || e.message), 'error');
    }
    setSendingEmail(null);
  };

  // Helpers para tipo de envio
  const getDeliveryTypeLabel = (type: string) => {
    switch (type) {
      case 'nf_fatura_boleto': return 'NF+Fat+Boleto';
      case 'nf_fatura': return 'NF+Fatura';
      case 'nf_fatura_relatorio': return 'NF+Fat+Relat.';
      case 'nf_fatura_boleto_relatorio': return 'NF+Fat+Bol+Rel.';
      case 'nenhum': return 'Nao enviar';
      default: return 'NF+Fat+Boleto';
    }
  };

  // Agrupa logs por destinatário; vale o ÚLTIMO envio de cada um (reenvio OK limpa falha antiga)
  function groupLogsByRecipient(logs: typeof emailStatusMap[number]) {
    const byRecipient = new Map<string, typeof emailStatusMap[number]>();
    for (const log of logs || []) {
      const key = (log.recipientEmail || '?').toLowerCase();
      if (!byRecipient.has(key)) byRecipient.set(key, []);
      byRecipient.get(key)!.push(log);
    }
    // Ordena tentativas de cada destinatário: mais recente primeiro
    byRecipient.forEach(list => list.sort((a, b) => new Date(b.sentAt || 0).getTime() - new Date(a.sentAt || 0).getTime()));
    return byRecipient;
  }

  // Estado agregado por DESTINATÁRIO (não por linha): ok = todos entregues;
  // partial = parte falhou; bounced = NINGUÉM recebeu; sent = aguardando confirmação
  function getEmailSummary(logs: typeof emailStatusMap[number]): { state: string; ok: number; fail: number; total: number; attempts: number } | null {
    if (!logs || logs.length === 0) return null;
    const byRecipient = groupLogsByRecipient(logs);
    let ok = 0, fail = 0, pending = 0, anyOpened = false;
    byRecipient.forEach(list => {
      const latest = getIndividualStatus(list[0]);
      if (latest === 'bounced') fail++;
      else if (latest === 'opened') { ok++; anyOpened = true; }
      else if (latest === 'delivered') ok++;
      else pending++;
    });
    const total = byRecipient.size;
    let state: string;
    if (fail > 0 && ok === 0 && pending === 0) state = 'bounced';
    else if (fail > 0) state = 'partial';
    else if (anyOpened) state = 'opened';
    else if (ok > 0) state = 'delivered';
    else state = 'sent';
    return { state, ok, fail, total, attempts: logs.length };
  }

  const emailStatusConfig: Record<string, { label: string; color: string }> = {
    sent: { label: 'Enviado', color: TONE_BADGE.progress },
    delivered: { label: 'Entregue', color: TONE_BADGE.open },
    opened: { label: 'Aberto', color: TONE_BADGE.success },
    partial: { label: 'Parcial', color: TONE_BADGE.warning },
    bounced: { label: 'Falha', color: TONE_BADGE.danger },
  };

  function getIndividualStatus(log: typeof emailStatusMap[number][number]): string {
    if (['bounced', 'dropped', 'spam_report', 'failed'].includes(log.status)) return 'bounced';
    if (log.openedAt || ['opened', 'clicked'].includes(log.status)) return 'opened';
    if (log.deliveredAt || ['delivered'].includes(log.status)) return 'delivered';
    return 'sent';
  }

  /**
   * COMPROVANTE DE ENVIO, montado UMA vez no nível da tabela. Até 04/10/2026 ele
   * vivia dentro do selo de cada linha: uma instância por linha, e só existia se a
   * linha da fatura estivesse na tela. O selo da linha de PARCELAS aponta para a
   * primeira fatura do grupo, que não é renderizada com o grupo recolhido: o clique
   * não abria nada. Aqui a fatura é achada pelo id, esteja a linha visível ou não.
   */
  const renderComprovanteDeEnvio = (invoiceId: number) => {
    const logs = emailStatusMap[invoiceId];
    if (!logs || logs.length === 0) return null;
    const inv = invoices.find((i) => i.id === invoiceId);
    const client = inv ? clients.find((c: any) => c.id === inv.clientId) : null;
    const invoiceCode = inv?.code;
    return (
          <div onClick={(e) => e.stopPropagation()} className="text-left">
          <ComprovanteDeEnvioModal
            invoiceId={invoiceId}
            invoiceCode={invoiceCode || `Fatura #${invoiceId}`}
            clienteNome={nomeDeExibicao(client, '—')}
            rotuloDoModelo={getDeliveryTypeLabel}
            onClose={() => setEmailPopoverInvoiceId(null)}
            destinatarios={Array.from(groupLogsByRecipient(logs).entries()).map(([recipient, attempts]) => {
              const ultimo = attempts[0];
              const cfg = emailStatusConfig[getIndividualStatus(ultimo)];
              return { email: ultimo.recipientEmail || recipient, status: cfg.label, cor: cfg.color, ultimoEnvio: ultimo.sentAt || null, tentativas: attempts.length };
            })}
            trilha={
            <div className="space-y-2">
              {/* Agrupado por DESTINATÁRIO: vale o último envio; tentativas antigas viram nota */}
              {Array.from(groupLogsByRecipient(logs).entries()).map(([recipient, attempts], i) => {
                const log = attempts[0]; // mais recente
                const logStatus = getIndividualStatus(log);
                const cfg = emailStatusConfig[logStatus];
                return (
                  <div key={i} className="bg-slate-700/50 rounded p-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] text-slate-300 truncate max-w-[160px]" title={recipient}>
                        {log.recipientEmail || 'Destinatario'}
                      </span>
                      <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${cfg.color}`}>
                        {cfg.label}{attempts.length > 1 ? ` · ${attempts.length}º envio` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[11px]">
                      {log.sentAt && (
                        <div className="flex items-center gap-0.5 text-blue-400">
                          <span className="w-1 h-1 rounded-full bg-blue-400" />
                          <span>{fmtDateTime(log.sentAt)}</span>
                        </div>
                      )}
                      {(log.deliveredAt || ['delivered', 'opened', 'clicked'].includes(log.status)) && (
                        <>
                          <span className="text-slate-600">›</span>
                          <div className={`flex items-center gap-0.5 ${TONE_TEXT.open}`}>
                            <span className="w-1 h-1 rounded-full bg-current" />
                            <span>{log.deliveredAt ? fmtDateTime(log.deliveredAt) : 'Sim'}</span>
                          </div>
                        </>
                      )}
                      {(log.openedAt || ['opened', 'clicked'].includes(log.status)) && (
                        <>
                          <span className="text-slate-600">›</span>
                          <div className={`flex items-center gap-0.5 ${TONE_TEXT.success}`}>
                            <span className="w-1 h-1 rounded-full bg-current" />
                            <span>{log.openedAt ? fmtDateTime(log.openedAt) : 'Sim'}{log.openCount > 1 ? ` (${log.openCount}x)` : ''}</span>
                          </div>
                        </>
                      )}
                      {logStatus === 'bounced' && (
                        <>
                          <span className="text-slate-600">›</span>
                          <div className="flex items-center gap-0.5 text-red-400">
                            <span className="w-1 h-1 rounded-full bg-red-400" />
                            <span>{log.bouncedAt ? fmtDateTime(log.bouncedAt) : log.status}</span>
                          </div>
                        </>
                      )}
                    </div>
                    {log.bounceReason && (
                      <div className={`text-[11px] mt-1 truncate ${TONE_TEXT.danger}`} title={log.bounceReason}>
                        {log.bounceReason}
                      </div>
                    )}
                    {attempts.length > 1 && (
                      <div className="text-[11px] text-slate-500 mt-1">
                        Tentativas anteriores: {attempts.slice(1).map(a => fmtDateTime(a.sentAt || '')).join(', ')}
                      </div>
                    )}
                    {log.recipientEmail && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!(await confirmarTexto(`Reenviar a fatura SOMENTE para ${log.recipientEmail}?`))) return;
                          setResendingTo(recipient);
                          try {
                            await invoicesService.sendEmail(invoiceId, [log.recipientEmail]);
                            await refreshEmailLogsFor(invoiceId);
                            avisar(`Reenviado para ${log.recipientEmail}.`, 'success');
                          } catch (err: any) {
                            avisar('Erro ao reenviar: ' + (err.response?.data?.message || err.response?.data?.error || err.message), 'error');
                          }
                          setResendingTo(null);
                        }}
                        disabled={resendingTo !== null}
                        className={`mt-1.5 w-full px-2 py-1 text-xs font-medium rounded border transition-colors disabled:opacity-50 ${
                          logStatus === 'bounced'
                            ? `${TONE_BADGE.danger} ${TONE_BORDER.danger} hover:brightness-125`
                            : 'bg-slate-600/30 text-slate-300 border-slate-600 hover:bg-slate-600/60'
                        }`}
                      >
                        {resendingTo === recipient ? 'Reenviando…' : `↻ Reenviar só para este destinatário`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            }
          />
          </div>
    );
  };

  /** A regra da célula "Documentos" para uma fatura (utils/documentosDaFatura), com o resumo dos logs de e-mail. */
  const documentosDaFatura = (inv: Invoice) => {
    const client = clients.find((c: any) => c.id === inv.clientId);
    const finEmails = (client?.emails || []).filter((e: any) => e.type === 'Financeiro').map((e: any) => e.email).filter(Boolean);
    const summary = getEmailSummary(emailStatusMap[inv.id]);
    // CDR: só para a fatura cuja assinatura inclui consumo (utils/cdrNaFatura); as outras recebem null.
    const precisaDeCdr = !!(inv.contract?.includeUsageItems ?? contracts.find((c: any) => c.id === inv.contractId)?.includeUsageItems);
    const celula = celulaDeDocumentos(inv, inv.invoiceGroup, {
      resumo: summary,
      clienteConhecido: !!client,
      modelo: client?.emailDeliveryType || 'nf_fatura_boleto',
      temEmailFinanceiro: finEmails.length > 0 || !!client?.emailFinancial,
      logConhecido: logsConhecidos.has(inv.id),
    }, estadoDoCdr(inv, precisaDeCdr));
    return { celula, summary };
  };

  /**
   * As duas linhas da célula "Documentos" (fatura ou grupo). Linha 1, o e-mail: com log, abre o comprovante
   * e a trilha (`abrirComprovanteDe`). Linha 2, a pior exceção de nota/boleto e o "+N". O modelo de envio do
   * cadastro ("NF+Fat+Boleto") saiu desta coluna a pedido do Heitor (07/10/2026).
   */
  const renderDocumentos = (
    celula: { email: SinalDoDocumento | null; documento: SinalDoDocumento | null; mais: number; excecoes: SinalDoDocumento[]; cdr?: SinalDoDocumento | null },
    abrirComprovanteDe: number | null,
    detalheDoEnvio?: string,
  ) => {
    const maisN = (mais: number) =>
      mais > 0 && <span className="ml-1 text-[11px] font-normal not-italic text-slate-500" data-mais={mais}>+{mais}</span>;
    // CDR e e-mail são PÍLULA (pedidos do Heitor, 07 e 08/10/2026): a forma da pílula de Status, peso pela
    // gravidade. Nota e boleto seguem texto.
    const pilula = (s: SinalDoDocumento) =>
      s.documento === 'cdr' ? PILULA_DO_CDR[s.tom === 'aviso' ? 'aviso' : 'quieto'] : s.documento === 'email' ? `${FORMA_DA_PILULA} ${PILULA_DO_EMAIL[s.tom]}` : null;
    const linha = (s: SinalDoDocumento, mais = 0) =>
      pilula(s) ? (
        <span className="inline-flex items-center whitespace-nowrap">
          <span className={`${pilula(s)} whitespace-nowrap`} data-documento={s.documento} data-tom={s.tom}>
            {s.texto}
          </span>
          {maisN(mais)}
        </span>
      ) : (
        <span className={`${TOM_DO_DOCUMENTO[s.tom]} whitespace-nowrap`} data-documento={s.documento} data-tom={s.tom}>
          {s.texto}
          {maisN(mais)}
        </span>
      );
    // Ao passar o mouse: tudo o que a célula sabe, inclusive o que ficou no "+N" e o CDR aplicado.
    const titulo = Array.from(new Set([...celula.excecoes, ...(celula.cdr ? [celula.cdr] : [])].map((s) => s.detalhe || s.texto)))
      .concat(detalheDoEnvio ? [detalheDoEnvio] : [])
      .join(' · ');
    return (
      <div className="relative flex flex-col items-center gap-0.5" title={titulo || undefined}>
        {celula.email && (abrirComprovanteDe != null ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEmailPopoverInvoiceId(emailPopoverInvoiceId === abrirComprovanteDe ? null : abrirComprovanteDe);
            }}
            // Pílula clicável: o caminho para o comprovante e a trilha. Sem sublinhado (não cabe em pílula), o
            // sinal de clique é o cursor e o anel ao passar o mouse ou focar.
            className="cursor-pointer rounded-full hover:ring-1 hover:ring-slate-400/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300"
            title="Ver o comprovante e a trilha do envio"
          >
            {linha(celula.email)}
          </button>
        ) : linha(celula.email))}
        {celula.documento && linha(celula.documento, celula.mais)}
      </div>
    );
  };

  const getDocumentosCell = (inv: Invoice) => {
    const { celula, summary } = documentosDaFatura(inv);
    return renderDocumentos(
      celula,
      summary ? inv.id : null,
      summary ? `${summary.total} destinatário(s), ${summary.attempts} disparo(s): clique para ver o comprovante` : undefined,
    );
  };

  /** Célula "Documentos" da linha de GRUPO: a mesma regra, somada sobre as faturas do grupo. */
  const getDocumentosDoGrupo = (faturas: Invoice[]) => {
    const resumo = getEmailSummary(faturas.flatMap((i) => emailStatusMap[i.id] || []));
    const comEmail = faturas.filter((i) => (emailStatusMap[i.id] || []).length > 0);
    const emDia: SinalDoDocumento | null = resumo
      ? { documento: 'email', tom: 'quieto', texto: `e-mail ${emailStatusConfig[resumo.state].label.toLowerCase()} ${comEmail.length} de ${faturas.length}` }
      : null;
    const celula = celulaDoGrupo(faturas.map((i) => documentosDaFatura(i).celula), emDia);
    return renderDocumentos(
      celula,
      comEmail[0]?.id ?? null,
      resumo ? `${comEmail.length} de ${faturas.length} faturas com e-mail enviado: clique para ver o comprovante` : undefined,
    );
  };

  // Enviar faturas por email em lote.
  // ⛔ O resumo e o disparo usam o MESMO conjunto (`lote.enviar`), e só fatura
  // Aberta entra. Até 02/10/2026 o disparo recebia a seleção inteira, inclusive
  // as "ignoradas" e as em Rascunho (ver utils/loteDeEmail). Conta só o que está
  // VISÍVEL e selecionado: fatura selecionada e escondida pelo filtro não sai.
  const handleBulkSendEmail = async () => {
    if (selectedInvoices.length === 0) return;

    const selected = filteredInvoices.filter(inv => selectedInvoices.includes(inv.id));
    const lote = montarLoteDeEmail(selected, clients);

    if (lote.enviar.length === 0) {
      avisar(`Nenhuma das faturas selecionadas sera enviada.\n${resumoDoLoteDeEmail(lote).split('\n').slice(2).join('\n') || 'Sem email financeiro ou configuradas para nao receber.'}`, 'success');
      return;
    }

    setBatchEmailModal({ invoiceIds: lote.enviar, summary: resumoDoLoteDeEmail(lote), customNote: '' });
  };

  const handleValidateBoletos = async () => {
    if (!batchEmailModal) return;
    setBatchEmailModal(prev => prev ? { ...prev, validating: true, validationResult: undefined } : null);
    try {
      const boletosIds = batchEmailModal.invoiceIds.filter(id => {
        const inv = invoices.find(i => i.id === id);
        return inv?.paymentMethod === 'boleto' && inv?.nossoNumero;
      });
      if (boletosIds.length === 0) {
        setBatchEmailModal(prev => prev ? { ...prev, validating: false, validationResult: { total: 0, valid: 0, invalid: 0, errors: [] } } : null);
        return;
      }
      const result = await invoicesService.validateBoletos(boletosIds);
      setBatchEmailModal(prev => prev ? { ...prev, validating: false, validationResult: result } : null);
    } catch (e: any) {
      setBatchEmailModal(prev => prev ? { ...prev, validating: false } : null);
      avisar('Erro ao validar: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleConfirmBatchEmail = async () => {
    if (!batchEmailModal) return;
    const { invoiceIds, customNote } = batchEmailModal;
    const note = customNote.trim() || undefined;
    setBatchEmailModal(null);
    setBulkProcessing(true);
    try {
      const result = await invoicesService.sendEmailBatch(invoiceIds, note);
      if (result.jobId) {
        setBatchProgress({ jobId: result.jobId, type: 'email', total: result.total, completed: 0, percent: 0, errors: [], status: 'processing' });
      } else {
        setEmailResult(result);
      }
    } catch (e: any) {
      avisar('Erro ao enviar emails: ' + (e.response?.data?.message || e.message), 'error');
    }
    setBulkProcessing(false);
  };

  // Enviar comunicado de correção de boleto em lote
  const handleBulkSendBoletoCorrection = async () => {
    if (selectedInvoices.length === 0) return;

    const selected = filteredInvoices.filter(inv => selectedInvoices.includes(inv.id));

    // Filtrar apenas faturas com boleto registrado
    const comBoleto = selected.filter(inv => inv.nossoNumero && inv.boletoStatus === 'registrado');

    if (comBoleto.length === 0) {
      avisar('Nenhuma das faturas selecionadas possui boleto registrado.', 'warning');
      return;
    }

    // Verificar quais têm email financeiro
    let semEmailFinanceiro = 0;
    const aptas: number[] = [];
    for (const inv of comBoleto) {
      const client = clients.find((c: any) => c.id === inv.clientId);
      const finEmails = (client?.emails || []).filter((e: any) => e.type === 'Financeiro').map((e: any) => e.email).filter(Boolean);
      const hasFinancialEmail = finEmails.length > 0 || !!client?.emailFinancial;
      if (!hasFinancialEmail) { semEmailFinanceiro++; continue; }
      aptas.push(inv.id);
    }

    if (aptas.length === 0) {
      avisar('Nenhuma das faturas selecionadas possui email financeiro cadastrado.', 'warning');
      return;
    }

    let message = `Enviar comunicado de correção de boleto para ${aptas.length} fatura(s)?\n\nO email informará sobre a correção e anexará a fatura com o boleto atualizado.`;
    if (semEmailFinanceiro > 0) message += `\n\n${semEmailFinanceiro} ignorada(s) (sem email financeiro)`;
    if (comBoleto.length < selected.length) message += `\n${selected.length - comBoleto.length} ignorada(s) (sem boleto registrado)`;

    if (!(await confirmarTexto(message))) return;

    setBulkProcessing(true);
    try {
      const result = await invoicesService.sendBoletoCorrectionBatch(aptas);
      if (result.jobId) {
        setBatchProgress({ jobId: result.jobId, type: 'correction', total: result.total, completed: 0, percent: 0, errors: [], status: 'processing' });
      }
    } catch (e: any) {
      avisar('Erro ao enviar comunicados: ' + (e.response?.data?.message || e.message), 'error');
    }
    setBulkProcessing(false);
  };

  const getNextMonth = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    const nextMonth = m === 12 ? 1 : m + 1;
    const nextYear = m === 12 ? y + 1 : y;
    return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
  };

  const formatMonth = (month: string) => {
    const [y, m] = month.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(m, 10) - 1]}/${y}`;
  };

  // Competência = mês do periodStart (para órgão público mostra mês anterior)
  const getCompetencia = (inv: any) => {
    if (inv.periodStart) {
      const ps = inv.periodStart.split('T')[0];
      const [y, m] = ps.split('-');
      return formatMonth(`${y}-${m}`);
    }
    return formatMonth(inv.referenceMonth);
  };

  // Mês/ano da competência para nomes de arquivo (ex: "Fev2026")
  const getCompetenciaFileLabel = (inv: any) => {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const ref = inv.periodStart ? inv.periodStart.split('T')[0].slice(0, 7) : inv.referenceMonth;
    const [y, m] = ref.split('-');
    return `${monthNames[parseInt(m, 10) - 1]}${y}`;
  };

  const addItem = () => {
    setItemsModified(true);
    const periodStart = form.periodStart?.split('T')[0] || '';
    const periodEnd = form.periodEnd?.split('T')[0] || '';
    const period = periodStart && periodEnd
      ? `${fmtShortDate(periodStart)} à ${fmtShortDate(periodEnd)}`
      : '';
    setForm({
      ...form,
      items: [
        ...form.items,
        { name: '', description: '', quantity: 1, unitValue: 0, baseValue: 0, totalValue: 0, period, periodStart, periodEnd },
      ],
    });
  };

  // Calcula total de dias do ciclo de faturamento do cliente
  // O divisor do proporcional tem que ser o número de dias do mês em que o serviço
  // foi PRESTADO, não o da competência. O faturamento é postecipado, então competência
  // agosto cobre julho: usar agosto dividiria uma prestação de fevereiro por 31.
  // `periodStart` manda quando existe; `refMonth` fica como fallback.
  const getCycleDays = (refMonth: string, periodStart?: string) => {
    const [year, month] = refMonth.split('-').map(Number);
    const client = clients.find((c: any) => c.id === form.clientId);
    const cycleDay = client?.billingCycleStartDay;
    if (cycleDay && cycleDay > 1) {
      const cycleStart = new Date(year, month - 2, cycleDay);
      const cycleEnd = new Date(year, month - 1, cycleDay - 1);
      return Math.ceil((cycleEnd.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    const ps = String(periodStart || '').split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(ps)) {
      const [py, pm] = ps.split('-').map(Number);
      return new Date(py, pm, 0).getDate();
    }
    return new Date(year, month, 0).getDate();
  };

  // Calcula valor proporcional baseado no período
  const calcProportional = (baseValue: number, qty: number, periodStart: string, periodEnd: string, referenceMonth: string) => {
    if (!baseValue || !periodStart || !periodEnd || !referenceMonth) {
      return { unitValue: baseValue, totalValue: baseValue * qty };
    }

    const totalDaysInMonth = getCycleDays(referenceMonth, periodStart);

    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const periodDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Se período completo ou maior, usa valor cheio
    if (periodDays >= totalDaysInMonth) {
      return { unitValue: baseValue, totalValue: Math.round(baseValue * qty * 100) / 100 };
    }

    // Calcula proporcional
    const unitValue = Math.round((baseValue * periodDays / totalDaysInMonth) * 100) / 100;
    const totalValue = Math.round(unitValue * qty * 100) / 100;
    return { unitValue, totalValue };
  };

  const updateItem = (index: number, field: string, value: any) => {
    setItemsModified(true);
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };

    const item = newItems[index];
    const qty = parseFloat(item.quantity) || 1;
    const baseValue = parseFloat(item.baseValue) || 0;
    const periodStart = item.periodStart || form.periodStart?.split('T')[0] || '';
    const periodEnd = item.periodEnd || form.periodEnd?.split('T')[0] || '';

    // Atualiza período formatado
    if (periodStart && periodEnd) {
      newItems[index].period = `${fmtShortDate(periodStart)} à ${fmtShortDate(periodEnd)}`;
    }

    // Recalcula quando muda: baseValue, quantidade ou período.
    // `!== 0` e não `> 0`: item de DESCONTO tem base negativa e ficava de fora
    // do rateio, saindo com o valor cheio numa fatura proporcional (FATOR,
    // agosto/2026: ramal rateado pra 14/31 dias, desconto inteiro).
    if (field === 'baseValue' || field === 'quantity' || field === 'periodStart' || field === 'periodEnd') {
      // O motivo da vigência descrevia a conta da geração; mexida à mão, ele deixa de ser verdade.
      delete newItems[index].motivoDaVigencia;
      if (baseValue !== 0) {
        const { unitValue, totalValue } = calcProportional(baseValue, qty, periodStart, periodEnd, form.referenceMonth);
        newItems[index].unitValue = unitValue;
        newItems[index].totalValue = totalValue;
      }
    }

    // Se mudar unitValue diretamente (sem baseValue), apenas atualiza o total
    if (field === 'unitValue' && !baseValue) {
      const unit = parseFloat(value) || 0;
      newItems[index].totalValue = Math.round(qty * unit * 100) / 100;
    }

    setForm({ ...form, items: newItems });
  };

  // Muda o período da fatura (aba Dados Gerais) e PROPAGA pra todos os itens,
  // re-rateando o pro-rata (caso "cliente novo, 1ª fatura parcial").
  const setInvoicePeriod = (field: 'periodStart' | 'periodEnd', value: string) => {
    const periodStart = field === 'periodStart' ? value : (form.periodStart?.split('T')[0] || '');
    const periodEnd = field === 'periodEnd' ? value : (form.periodEnd?.split('T')[0] || '');
    const newItems = (form.items || []).map((it: any) => {
      const baseValue = parseFloat(it.baseValue) || parseFloat(it.unitValue) || 0;
      const qty = parseFloat(it.quantity) || 1;
      const next: any = { ...it, baseValue, periodStart, periodEnd };
      delete next.motivoDaVigencia;
      if (periodStart && periodEnd) {
        next.period = `${fmtShortDate(periodStart)} à ${fmtShortDate(periodEnd)}`;
      }
      // Negativo entra no rateio igual: desconto de fatura parcial também é
      // proporcional (ver comentário em updateItem).
      if (baseValue !== 0) {
        const { unitValue, totalValue } = calcProportional(baseValue, qty, periodStart, periodEnd, form.referenceMonth);
        next.unitValue = unitValue;
        next.totalValue = totalValue;
      }
      return next;
    });
    if (newItems.length > 0) setItemsModified(true);
    setForm({ ...form, [field]: value, items: newItems });
  };

  const removeItem = (index: number) => {
    setItemsModified(true);
    setForm({ ...form, items: form.items.filter((_: any, i: number) => i !== index) });
  };

  // Busca itens P&S pendentes do contrato
  const fetchPendingPSItems = async () => {
    if (!form.contractId) {
      avisar('Selecione uma assinatura primeiro', 'warning');
      return;
    }
    setLoadingPS(true);
    try {
      const result = await contractsService.getPendingPSItems(form.contractId);
      setPendingPSItems(result.items || []);
      setShowPSModal(true);
    } catch (e: any) {
      console.error('Erro ao buscar itens P&S:', e);
      avisar('Erro ao buscar itens P&S: ' + (e.response?.data?.message || e.message), 'error');
    }
    setLoadingPS(false);
  };

  // Adiciona item P&S à fatura
  //
  // ⛔ FALHA FECHADA (07/10/2026). Há DOIS escritores do mesmo fato: a fatura diz "cobrei este item" e o contrato diz
  // "este item está pendente". O item entrava no formulário e SÓ DEPOIS o contrato era marcado; se a marcação falhava,
  // o erro ia para o console, a fatura saía com o item e o contrato seguia com ele pendente. Os dois divergiam calados,
  // e a próxima geração mensal, lendo o contrato, cobrava o cliente DE NOVO. Agora o contrato é marcado PRIMEIRO, e o
  // item só entra no formulário se a marcação deu certo. Falhando, nada foi cobrado nem marcado: o operador lê o
  // motivo e tenta de novo. "Entrar com aviso" não serve: o aviso se lê hoje, e a cobrança em dobro sai no mês que vem,
  // por um caminho automático, para o cliente.
  const addPSItemToInvoice = async (psItem: any) => {
    try {
      await contractsService.markPSItemBilled(form.contractId, psItem.itemIndex);
    } catch (e) {
      avisar(`O item P&S não entrou na fatura: não consegui marcá-lo como faturado na assinatura (${mensagemDoErroDeCarga(e)}). Nada foi cobrado; tente de novo.`, 'error');
      return;
    }
    setItemsModified(true);
    const periodStart = form.periodStart?.split('T')[0] || '';
    const periodEnd = form.periodEnd?.split('T')[0] || '';
    const period = periodStart && periodEnd
      ? `${fmtShortDate(periodStart)} à ${fmtShortDate(periodEnd)}`
      : '';

    // Determinar nome com parcela se aplicável
    let itemName = psItem.name;
    if (psItem.installments > 1) {
      itemName = `${psItem.name} (${psItem.currentInstallment}/${psItem.installments})`;
    }

    const newItem = {
      name: itemName,
      description: psItem.description || '',
      quantity: 1,
      unitValue: psItem.installmentValue,
      baseValue: psItem.installmentValue,
      totalValue: psItem.installmentValue,
      period,
      periodStart,
      periodEnd,
      type: 'PS',
      psItemIndex: psItem.itemIndex, // Para rastrear e marcar como faturado
    };

    setForm({
      ...form,
      items: [...form.items, newItem],
    });

    // Atualizar lista de P&S pendentes (o contrato já foi marcado, lá em cima)
    setPendingPSItems(pendingPSItems.filter(p => p.itemIndex !== psItem.itemIndex ||
      (p.installments > 1 && p.remainingInstallments > 1)));

    setShowPSModal(false);
  };

  // Calcula o período do mês de referência completo (01/MM/YY a último dia/MM/YY)
  // O consumo é do período de SERVIÇO: em órgão público a competência é o mês seguinte a ele (03/11/2026).
  const getFullMonthPeriod = () => {
    const ps = form.periodStart?.split('T')[0];
    const pe = form.periodEnd?.split('T')[0];
    if (ps && pe) return `${fmtShortDate(ps)} a ${fmtShortDate(pe)}`;
    if (!form.referenceMonth) return '';
    const [year, month] = form.referenceMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0); // Último dia do mês
    return `${fmtShortDate(firstDay)} a ${fmtShortDate(lastDay)}`;
  };

  const addUsageItem = () => {
    setItemsModified(true);
    const period = getFullMonthPeriod();
    setForm({
      ...form,
      usageItems: [
        ...form.usageItems,
        { tariff: TARIFF_TYPES[0], minutes: 0, unitValue: 0, totalValue: 0, period },
      ],
    });
  };

  const addAllUsageItems = () => {
    setItemsModified(true);
    const period = getFullMonthPeriod();
    const newItems = TARIFF_TYPES.map(tariff => ({
      tariff,
      minutes: 0,
      unitValue: 0,
      totalValue: 0,
      period,
    }));
    setForm({
      ...form,
      usageItems: [...form.usageItems, ...newItems],
    });
  };

  // Carrega tarifas da assinatura vinculada
  // Se já existem itens de consumo, atualiza apenas os valores unitários (mantém os minutos do CDR)
  // Se não existem itens, cria novos com os dados da assinatura
  const loadUsageFromContract = () => {
    setItemsModified(true);
    if (!form.contractId) {
      avisar('Selecione uma assinatura primeiro.', 'warning');
      return;
    }
    const contract = contracts.find((c: any) => c.id === form.contractId);
    if (!contract) {
      avisar('Assinatura não encontrada.', 'error');
      return;
    }
    if (!contract.includeUsageItems) {
      avisar('Esta assinatura não tem tarifas de consumo configuradas.', 'info');
      return;
    }

    const period = getFullMonthPeriod();
    const rates = contract.usageRates || {};
    const minutes = contract.usageMinutes || {};
    const ratesEntrada = contract.usageRatesEntrada || {};

    // Mapeamento de chave para nome da tarifa
    const tariffMapping: { [key: string]: string } = {
      celularLDN: 'Celular LDN',
      celularLocal: 'Celular Local',
      fixoLDN: 'Fixo LDN',
      fixoLocal: 'Fixo Local',
      ldi: 'LDI',
      especial: 'Especial',
      emergencial: 'Emergencial',
    };

    // Mapeamento reverso: nome da tarifa para chave
    const reverseMapping: { [name: string]: string } = {};
    for (const [key, name] of Object.entries(tariffMapping)) {
      reverseMapping[name] = key;
    }

    const existingItems = form.usageItems || [];

    if (existingItems.length > 0) {
      // Atualizar valores unitários dos itens existentes (mantém os minutos)
      const updatedItems = existingItems.map((item: any) => {
        const tariffName = item.tariff?.replace(' (Saída)', '').replace(' (Entrada)', '').replace(' (Entrada (0800))', '').trim();
        const isEntrada = item.tariff?.toLowerCase().includes('entrada');
        const key = reverseMapping[tariffName];

        let unitValue = item.unitValue;
        if (key) {
          unitValue = isEntrada ? (parseFloat(ratesEntrada[key]) || 0) : (parseFloat(rates[key]) || 0);
        } else if (tariffName === 'Entrada') {
          // Tarifa genérica de entrada
          unitValue = parseFloat(ratesEntrada.entrada) || 0;
        }

        const mins = parseFloat(item.minutes) || 0;
        return {
          ...item,
          unitValue,
          totalValue: Number((mins * unitValue).toFixed(2)),
          period: item.period || period,
        };
      });

      setForm({ ...form, usageItems: updatedItems });
    } else {
      // Criar novos itens com os dados da assinatura
      const newItems: any[] = [];
      for (const [key, tariffName] of Object.entries(tariffMapping)) {
        const unitValue = parseFloat(rates[key]) || 0;
        const mins = parseFloat(minutes[key]) || 0;
        if (unitValue > 0 || mins > 0) {
          newItems.push({
            tariff: tariffName,
            minutes: mins,
            unitValue: unitValue,
            totalValue: Number((mins * unitValue).toFixed(2)),
            period,
          });
        }
      }

      if (newItems.length === 0) {
        avisar('Nenhuma tarifa configurada na assinatura.', 'warning');
        return;
      }

      setForm({ ...form, usageItems: newItems });
    }
  };

  const updateUsageItem = (index: number, field: string, value: any) => {
    setItemsModified(true);
    const newItems = [...form.usageItems];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'minutes' || field === 'unitValue') {
      const mins = parseFloat(newItems[index].minutes) || 0;
      const unit = parseFloat(newItems[index].unitValue) || 0;
      newItems[index].totalValue = mins * unit;
    }

    setForm({ ...form, usageItems: newItems });
  };

  const removeUsageItem = (index: number) => {
    setItemsModified(true);
    setForm({ ...form, usageItems: form.usageItems.filter((_: any, i: number) => i !== index) });
  };

  // Atualiza o período de todas as tarifas para o mês de referência completo
  const updateAllUsagePeriods = () => {
    setItemsModified(true);
    const newPeriod = getFullMonthPeriod();
    if (!newPeriod) return;
    const updatedItems = (form.usageItems || []).map((item: any) => ({
      ...item,
      period: newPeriod,
    }));
    setForm({ ...form, usageItems: updatedItems });
  };

  // Calcula o valor proporcional dos itens baseado nos dias do período
  const calculateProportional = () => {
    setItemsModified(true);
    if (!form.periodStart || !form.periodEnd || !form.referenceMonth) return;

    const totalDaysInMonth = getCycleDays(form.referenceMonth, form.periodStart);

    const startDate = new Date(form.periodStart);
    const endDate = new Date(form.periodEnd);
    const periodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (periodDays >= totalDaysInMonth) {
      avisar('O período já cobre o mês inteiro. Não é necessário calcular proporcional.', 'info');
      return;
    }

    const proportion = periodDays / totalDaysInMonth;

    const newPeriod = `${fmtDate(form.periodStart)} a ${fmtDate(form.periodEnd)}`;

    const updatedItems = (form.items || []).map((item: any) => {
      // Parte do valor BASE, não do unitário já rateado: usando o unitário,
      // clicar no botão duas vezes aplicava a proporção duas vezes.
      const baseValue = parseFloat(item.baseValue) || parseFloat(item.unitValue) || 0;
      const qty = parseFloat(item.quantity) || 1;
      const newUnitValue = Math.round(baseValue * proportion * 100) / 100;
      const newTotalValue = Math.round(newUnitValue * qty * 100) / 100;
      return {
        ...item,
        baseValue,
        unitValue: newUnitValue,
        totalValue: newTotalValue,
        period: newPeriod,
      };
    });

    // Também atualiza o período das tarifas para o mesmo período proporcional
    const updatedUsageItems = (form.usageItems || []).map((item: any) => ({
      ...item,
      period: newPeriod,
    }));

    setForm({ ...form, items: updatedItems, usageItems: updatedUsageItems });
    avisar(`Valores calculados proporcionalmente: ${periodDays} dias de ${totalDaysInMonth} (${Math.round(proportion * 100)}%)`, 'success');
  };

  const subtotalItems = (form.items || []).reduce((s: number, i: any) => s + (parseFloat(i.totalValue) || 0), 0);
  const subtotalUsage = (form.usageItems || []).reduce((s: number, i: any) => s + (parseFloat(i.totalValue) || 0), 0);
  const totalValue = subtotalItems + subtotalUsage;

  /**
   * Lê a prévia da geração e aplica no formulário: o período sempre; as linhas MRR só com assinatura e só na criação.
   *
   * ⛔ As linhas MRR são TROCADAS, e o resto fica: P&S puxado pelo "Puxar P&S" já marcou a parcela como faturada no
   * contrato, e perder a linha aqui cobraria a parcela em lugar nenhum. Item digitado à mão também fica.
   * ⚠️ Sem prévia não há linha: copiar o contrato cheio, como antes, era exatamente o defeito do Sorriso.
   */
  const lerPreviaDaGeracao = async (referenceMonth: string, clientId: number, contractId: number | null, comItens: boolean) => {
    const n = ++pedidoDaPrevia.current;
    if (!referenceMonth || (!clientId && !contractId)) { setPreviaDaGeracao(null); return; }
    const repetir = () => { lerPreviaDaGeracao(referenceMonth, clientId, contractId, comItens); };
    setPreviaDaGeracao({ estado: 'lendo' });
    try {
      const p = previaDaGeracaoValida(await invoicesService.generationPreview(
        contractId ? { referenceMonth, contractId } : { referenceMonth, clientId },
      ));
      if (n !== pedidoDaPrevia.current) return;
      if (!p) throw new Error('A prévia da geração veio fora da forma esperada.');
      setForm((f: any) => ({
        ...f,
        referenceMonth: p.referenceMonth,
        periodStart: p.periodStart,
        periodEnd: p.periodEnd,
        ...(comItens && contractId
          ? { items: [...itensDoFormulario(p), ...(f.items || []).filter((i: any) => i.type !== 'MRR')] }
          : {}),
      }));
      setPreviaDaGeracao({ estado: 'ok', previa: p });
    } catch (e) {
      if (n !== pedidoDaPrevia.current) return;
      setPreviaDaGeracao({ estado: 'erro', erro: mensagemDoErroDeCarga(e), repetir });
    }
  };

  const handleClientChange = (clientId: number) => {
    setForm({ ...form, clientId });
    if (modal === 'create') lerPreviaDaGeracao(form.referenceMonth, clientId, null, false);
  };

  const handleContractChange = (contractId: number) => {
    const contract = contracts.find((c) => c.id === contractId);
    // Na edição a fatura já existe: trocar a assinatura não refaz período nem linhas (antes trocava as linhas na tela
    // pelas do contrato, cheias, e elas nem eram gravadas sem outra edição nos itens).
    if (modal !== 'create') {
      setForm({ ...form, contractId: contract ? contractId : null, ...(contract ? { clientId: contract.clientId } : {}) });
      return;
    }
    if (!contract) {
      setForm({ ...form, contractId: null, items: (form.items || []).filter((i: any) => i.type !== 'MRR') });
      lerPreviaDaGeracao(form.referenceMonth, form.clientId, null, false);
      return;
    }

    setForm({
      ...form,
      contractId,
      clientId: contract.clientId,
      items: (form.items || []).filter((i: any) => i.type !== 'MRR'),
    });
    lerPreviaDaGeracao(form.referenceMonth, contract.clientId, contractId, modal === 'create');
  };

  // O mapa do domínio inteiro mora em utils/tomDoStatusDaFatura (Parcial deixou de dividir o âmbar com o Rascunho).
  const getStatusColor = (status: string) => TONE_BADGE[tomDoStatusDaFatura(status)];

  // A barra de seleção e o cabeçalho da lista são fixos no mesmo <main>: sem isto, a barra (z-30) cobria o cabeçalho
  // assim que se marcava uma fatura e se rolava (visto na prévia, 22/10/2026). O cabeçalho gruda logo abaixo dela; a
  // altura é medida porque os botões da barra quebram linha em tela estreita.
  const barraDeSelecaoRef = useRef<HTMLDivElement>(null);
  const [alturaDaBarraDeSelecao, setAlturaDaBarraDeSelecao] = useState(0);
  const comSelecao = selectedInvoices.length > 0;
  useEffect(() => {
    const el = barraDeSelecaoRef.current;
    if (!comSelecao || !el) { setAlturaDaBarraDeSelecao(0); return; }
    const medir = () => setAlturaDaBarraDeSelecao(el.offsetHeight);
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [comSelecao]);

  return (
    <div className="space-y-4">
      {/* Modal de Envio de Email */}
      {emailModal && (
        // Tem seleção e texto digitado: não fecha ao clicar fora (só × ou Cancelar).
        <CascaDeModal
          largura="xs"
          titulo="Enviar Email"
          subtitulo={`Fatura ${emailModal.invoiceCode}`}
          onClose={() => setEmailModal(null)}
          corpoClassName="px-5 py-4"
          rodape={
            <>
              <button
                onClick={() => setEmailModal(null)}
                className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSendEmail}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium"
              >
                Enviar
              </button>
            </>
          }
        >
            <div className="space-y-2 mb-4">
              <p className="text-xs text-slate-500 uppercase font-semibold">Destinatarios</p>
              {emailModal.emails.map((item, idx) => (
                <label key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => setEmailModal(prev => prev ? {
                      ...prev,
                      emails: prev.emails.map((e, i) => i === idx ? { ...e, checked: !e.checked } : e),
                    } : null)}
                    className="w-4 h-4 rounded border-slate-600 text-orange-500 focus:ring-orange-500 bg-slate-800"
                  />
                  <span className="text-sm text-slate-300">{item.email}</span>
                </label>
              ))}
            </div>

            <div className="mb-4">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Email adicional (teste)</p>
              <input
                type="email"
                value={emailModal.customEmail}
                onChange={e => setEmailModal(prev => prev ? { ...prev, customEmail: e.target.value } : null)}
                placeholder="email@exemplo.com"
                className={`w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
              />
            </div>

            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Nota no email (opcional)</p>
              <textarea
                value={emailModal.customNote}
                onChange={e => setEmailModal(prev => prev ? { ...prev, customNote: e.target.value } : null)}
                placeholder="Ex: Favor desconsiderar o boleto enviado anteriormente."
                rows={2}
                className={`w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none resize-none`}
              />
            </div>
        </CascaDeModal>
      )}


      {/* Modal de Confirmação Envio em Lote */}
      {batchEmailModal && (
        // Tem nota digitada: não fecha ao clicar fora (só × ou Cancelar).
        <CascaDeModal
          largura="xs"
          titulo="Enviar Emails em Lote"
          subtitulo={`${batchEmailModal.invoiceIds.length} fatura(s)`}
          onClose={() => setBatchEmailModal(null)}
          corpoClassName="px-5 py-4"
          rodape={
            <>
              <button
                onClick={() => setBatchEmailModal(null)}
                className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmBatchEmail}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium"
              >
                Enviar {batchEmailModal.invoiceIds.length} emails
              </button>
            </>
          }
        >
            <p className="text-sm text-slate-400 mb-4 whitespace-pre-line">{batchEmailModal.summary}</p>

            {/* Log não lido: não se sabe quem já recebeu. Sem este aviso, o "serão REENVIADAS" abaixo simplesmente sumia. */}
            {(() => {
              const naoSei = batchEmailModal.invoiceIds.filter(id => !logsConhecidos.has(id));
              if (naoSei.length === 0) return null;
              return (
                <div role="alert" data-envio="desconhecido" className={`mb-4 p-3 rounded-lg border ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`}>
                  <p className={`text-sm font-medium ${TONE_TEXT.warning}`}>
                    Não sei se {naoSei.length} de {batchEmailModal.invoiceIds.length} fatura(s) já receberam e-mail: o log de envios {falhaDosLogs ? `não carregou (${falhaDosLogs})` : 'ainda está carregando'}.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Enviar agora pode repetir o e-mail para quem já recebeu.</p>
                </div>
              );
            })()}

            {/* Controle de reenvio: avisa quais faturas JÁ receberam e-mail antes */}
            {(() => {
              const resend = batchEmailModal.invoiceIds.filter(id => (emailStatusMap[id]?.length || 0) > 0);
              if (resend.length === 0) return null;
              return (
                <div className="mb-4 p-3 rounded-lg bg-amber-900/30 border border-amber-700">
                  <p className="text-amber-400 text-sm font-medium">⚠️ {resend.length} de {batchEmailModal.invoiceIds.length} fatura(s) JÁ receberam e-mail antes — serão REENVIADAS.</p>
                  <p className="text-amber-200/70 text-xs mt-1">
                    {resend.slice(0, 8).map(id => invoices.find(i => i.id === id)?.code || `#${id}`).join(', ')}{resend.length > 8 ? ` +${resend.length - 8}` : ''}
                  </p>
                  <p className="text-amber-200/50 text-xs mt-1">Pra enviar só quem nunca recebeu, use a entrada "e-mails aguardando envio" do cartão "Precisa de você" e selecione todas por lá.</p>
                </div>
              );
            })()}

            <div className="mb-4">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Nota no email (opcional)</p>
              <textarea
                value={batchEmailModal.customNote}
                onChange={e => setBatchEmailModal(prev => prev ? { ...prev, customNote: e.target.value } : null)}
                placeholder="Ex: Favor desconsiderar o boleto enviado anteriormente."
                rows={2}
                className={`w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none resize-none`}
              />
            </div>

            {/* Validação de boletos */}
            <div>
              <button
                onClick={handleValidateBoletos}
                disabled={batchEmailModal.validating}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
              >
                {batchEmailModal.validating ? 'Validando boletos...' : 'Validar Boletos antes de enviar'}
              </button>
              {batchEmailModal.validationResult && (
                <div className={`mt-2 p-3 rounded-lg text-sm ${batchEmailModal.validationResult.invalid > 0 ? 'bg-red-900/30 border border-red-700' : `${TONE_SURFACE.success} border ${TONE_BORDER.success}`}`}>
                  {batchEmailModal.validationResult.invalid === 0 ? (
                    <p className={`${TONE_TEXT.success} font-medium`}>{batchEmailModal.validationResult.valid} boleto(s) validado(s) com sucesso</p>
                  ) : (
                    <>
                      <p className="text-red-400 font-medium">{batchEmailModal.validationResult.invalid} boleto(s) com erro:</p>
                      {batchEmailModal.validationResult.errors.map((err: any, i: number) => (
                        <p key={i} className="text-red-300 text-xs mt-1">{err.code} - {err.clientName}: {err.error}</p>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
        </CascaDeModal>
      )}

      {/* Summary Cards. Sem lista carregada (falhou antes de qualquer carga boa), os números não são zero: são
          "não sei", e a tela mostra "—" (ver loadData). Com lista e RECARGA que falhou, os números que ficam são os da
          carga anterior: a faixa diz isso em cima deles (10/10/2026), e não só na lista, lá embaixo. */}
      {cargaDasFaturas.estado === 'erro' && invoices.length > 0 && (
        <div role="alert" data-carga-cartao="velhos" className={`mb-3 px-4 py-2.5 rounded-lg border border-amber-600/50 text-xs ${TONE_BADGE.warning}`}>
          {textos.naoConseguiAtualizar('as faturas', cargaDasFaturas.erro)}
          {cargaDasFaturas.atualizadoEm ? textos.numerosDaUltimaCargaBoa(fmtDateTime(cargaDasFaturas.atualizadoEm)) : ''}
          <button type="button" onClick={loadData} className="ml-2 px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs font-medium">
            {textos.tentarDeNovo}
          </button>
        </div>
      )}
      {/* Cartões do topo (padrão das listas no Financeiro, 22/10/2026): o CartaoDoTopo, com a barra primary no escolhido
          (saiu o anel laranja) e os números em branco. */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <CartaoDoTopo
          rotulo="Faturamento"
          valor={semNumerosDeFaturas ? '—' : `R$ ${fmt(totalMonth)}`}
          sub={<>{semNumerosDeFaturas ? '—' : monthInvoices.length} faturas</>}
          ativo={cardFilter === null && statusFilter === 'Todos'}
          onClick={() => { setCardFilter(null); setStatusFilter('Todos'); }}
        />

        <CartaoDoTopo
          rotulo="Pagas"
          valor={semNumerosDeFaturas ? '—' : `R$ ${fmt(totalPaid)}`}
          sub={<>{semNumerosDeFaturas ? '—' : countPaid} faturas</>}
          ativo={cardFilter === 'paid'}
          onClick={() => { setCardFilter('paid'); setStatusFilter('Todos'); }}
        />

        <CartaoDoTopo
          rotulo="A Vencer"
          valor={semNumerosDeFaturas ? '—' : `R$ ${fmt(totalOnTime)}`}
          sub={<>{semNumerosDeFaturas ? '—' : onTimeInvoices.length} faturas</>}
          ativo={cardFilter === 'open'}
          onClick={() => { setCardFilter('open'); setStatusFilter('Todos'); }}
        />

        <CartaoDoTopo
          rotulo="Rascunho"
          valor={semNumerosDeFaturas ? '—' : `R$ ${fmt(totalDraftValue)}`}
          sub={<>{semNumerosDeFaturas ? '—' : totalDraft} faturas</>}
          ativo={cardFilter === 'draft'}
          onClick={() => { setCardFilter('draft'); setStatusFilter('Todos'); }}
        />

        {/* "Precisa de você" (06/10/2026) substitui os cartões Vencidas e Email Pendente, com as MESMAS
            regras e os mesmos filtros (cardFilter 'overdue' e 'email_pending'). Entrada com zero não vira
            link. Rascunho e falha de e-mail ficam de fora de propósito: já têm cartão e chip próprios, e
            repetir aqui cria dois lugares que divergem no primeiro ajuste. O destaque de ativo fica na
            entrada que está filtrando, não no cartão. */}
        <div className="bg-central-cartao rounded-central p-4 border border-central-borda text-left" data-testid="precisa-de-voce">
          <p className="text-sm text-slate-400">Precisa de você</p>
          {semNumerosDeFaturas ? (
            <p className="text-xs text-slate-500 mt-2" data-carga-cartao="sem-numeros">— (as faturas não carregaram)</p>
          ) : overdueInvoices.length === 0 && emailPendingInvoices.length === 0 && envioDesconhecido.length === 0 ? (
            <p className="text-xs text-slate-500 mt-2">Nenhuma vencida e nenhum e-mail aguardando envio</p>
          ) : (
            <div className="mt-2 flex flex-col gap-1.5">
              {overdueInvoices.length > 0 && (
                <button
                  onClick={() => { setCardFilter('overdue'); setStatusFilter('Todos'); }}
                  aria-pressed={cardFilter === 'overdue'}
                  className={`text-left text-sm rounded-lg px-2 py-1 -mx-2 transition-colors ${cardFilter === 'overdue' ? 'bg-central-elevado border-l-[3px] border-l-primary' : 'hover:bg-central-elevado'}`}
                >
                  <span className={`font-semibold ${TONE_TEXT.danger}`}>{overdueInvoices.length} {overdueInvoices.length === 1 ? 'vencida' : 'vencidas'}</span>
                  <span className="text-slate-400"> · R$ {fmt(totalOverdue)}</span>
                </button>
              )}
              {overdueInvoices.length > 0 && onAbrirCobranca && (
                <button type="button" onClick={onAbrirCobranca} className="self-start text-xs text-slate-400 hover:text-white underline" data-cobrar-na-cobranca>
                  Cobrar na Cobrança →
                </button>
              )}
              {emailPendingInvoices.length > 0 && (
                <button
                  onClick={() => { setCardFilter('email_pending'); setStatusFilter('Todos'); }}
                  disabled={envioDesconhecido.length > 0}
                  title={envioDesconhecido.length > 0 ? 'Travado até o log de e-mail carregar: sem ele não dá para separar quem nunca recebeu' : undefined}
                  aria-pressed={cardFilter === 'email_pending'}
                  className={`text-left text-sm rounded-lg px-2 py-1 -mx-2 transition-colors ${cardFilter === 'email_pending' ? 'bg-central-elevado border-l-[3px] border-l-primary' : 'hover:bg-central-elevado'}`}
                >
                  <span className={`font-semibold ${TONE_TEXT.warning}`}>{emailPendingInvoices.length} {emailPendingInvoices.length === 1 ? 'e-mail aguardando envio' : 'e-mails aguardando envio'}</span>
                </button>
              )}
              {overdueInvoices.length === 0 && <p className="text-xs text-slate-500">Nenhuma vencida</p>}
              {envioDesconhecido.length > 0 && (
                <p className={`text-xs ${TONE_TEXT.warning}`} data-envio="desconhecido">
                  {falhaDosLogs
                    ? <>Não sei quem já recebeu e-mail ({envioDesconhecido.length} {envioDesconhecido.length === 1 ? 'fatura aberta' : 'faturas abertas'}): o log não carregou ({falhaDosLogs}).{' '}
                        <button type="button" onClick={() => setTentativaDosLogs((n) => n + 1)} className="underline hover:text-white">Tentar de novo</button></>
                    : <>Conferindo quem já recebeu e-mail…</>}
                </p>
              )}
              {emailPendingInvoices.length === 0 && envioDesconhecido.length === 0 && <p className="text-xs text-slate-500">Nenhum e-mail aguardando envio</p>}
            </div>
          )}
          {/* Sem a carga das cobranças, os selos da lista somem calados e parecia que ninguém foi cobrado. */}
          {cargaDasCobrancas.estado === 'erro' && Object.keys(dunningMap).length === 0 && (
            <p className={`text-xs mt-2 ${TONE_TEXT.warning}`} data-cobrancas-nao-carregaram>
              Os selos de cobrança não carregaram: a lista não diz quem já foi cobrado.
            </p>
          )}
        </div>
      </div>

      <ListToolbar
        period={
          <PeriodSelector
            value={periodFilter}
            // Mudar o mês NÃO limpa o filtro de cartão escolhido: comparar as
            // vencidas mês a mês (entrada do "Precisa de você") é justamente para
            // isso que se troca o mês. Zerando aqui, a seta ‹ › devolvia a tela
            // para "Faturamento / Todos" a cada clique. (Até a #326 os chips
            // também espelhavam o cartão ativo; não espelham mais.)
            onChange={(pf) => setPeriodFilter(pf)}
            onRefresh={() => loadData()}
          />
        }
        search={<ToolbarSearch value={search} onChange={setSearch} placeholder="Buscar por código, cliente..." />}
        filters={
          <div className="flex gap-1.5 flex-wrap">
            {CHIPS_DE_STATUS.map((c) => ({
              ...c,
              activeClass: ({
                Todos: 'bg-slate-600 text-white font-semibold',
                Paga: `${TONE_BADGE.success} border ${TONE_BORDER.success}`,
                Aberta: `${TONE_BADGE.open} border ${TONE_BORDER.open}`,
                Parcial: `${TONE_BADGE.warning} border ${TONE_BORDER.warning}`,
                Vencida: `${TONE_BADGE.danger} border ${TONE_BORDER.danger}`,
                Rascunho: `${TONE_BADGE.warning} border ${TONE_BORDER.warning}`,
                Cancelada: `${TONE_BADGE.neutral} border ${TONE_BORDER.neutral}`,
              } as Record<string, string>)[c.key] || 'bg-slate-600 text-white font-semibold',
            })).map((s) => {
              // O chip acende só quando é ELE que está filtrando. Até 06/10/2026 o chip espelhava o cartão
              // ativo (cardToStatus) e mentia: o cartão "Vencidas" acendia o chip Vencida com outro conjunto
              // (o cartão incluía Parcial), e o "A Vencer" acendia "Aberta", que tem as vencidas. Com cartão
              // ativo, nenhum chip acende: o estado aparece no próprio cartão.
              const activeKey = cardFilter ? null : statusFilter;
              // Contagem CALCULADA no período, nunca escrita: o tamanho antes do clique.
              const n = monthInvoices.filter((inv) => passaNoChipDeStatus(inv, s.key)).length;
              return (
              <button
                key={s.key}
                onClick={() => { setStatusFilter(s.key); setCardFilter(null); if (s.key === 'Todos') setEmailIssueFilter(false); }}
                aria-pressed={activeKey === s.key}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeKey === s.key
                    ? s.activeClass
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                {s.label} <span className="tabular-nums opacity-70">{n}</span>
              </button>
              );
            })}
            <span className="text-slate-600 mx-1">|</span>
            {chipsDePagamento(monthInvoices, paymentFilter).map((p) => {
              const n = monthInvoices.filter((inv) => passaNoChipDePagamento(inv, p.key)).length;
              return (
              <button
                key={p.key}
                onClick={() => setPaymentFilter(p.key)}
                aria-pressed={paymentFilter === p.key}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  paymentFilter === p.key
                    ? p.key === 'boleto' ? `${TONE_BADGE.progress} border ${TONE_BORDER.progress}` : p.key === 'ted' ? `${TONE_BADGE.info} border ${TONE_BORDER.info}` : 'bg-slate-600 text-white font-semibold'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                {p.label} <span className="tabular-nums opacity-70">{n}</span>
              </button>
              );
            })}
            {(() => {
              // Conta faturas do período com falha (total/parcial) no último envio
              const failCount = monthInvoices.filter(inv => {
                const st = getEmailSummary(emailStatusMap[inv.id])?.state;
                return st === 'partial' || st === 'bounced';
              }).length;
              return (
                <button
                  onClick={() => setEmailIssueFilter(v => !v)}
                  title="Mostrar só faturas com falha de entrega de e-mail (total ou parcial) no último envio"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    emailIssueFilter
                      ? `${TONE_BADGE.danger} border ${TONE_BORDER.danger}`
                      : failCount > 0
                        ? 'bg-slate-800 text-red-400 hover:bg-slate-700 border border-red-500/30'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  ⚠ Falha e-mail{failCount > 0 ? ` (${failCount})` : ''}
                </button>
              );
            })()}
          </div>
        }
        secondRow={<>
            {/* As tr\u00eas exporta\u00e7\u00f5es (CSV da lista, PDF de todas, Excel do m\u00eas) moram num bot\u00e3o s\u00f3. O
                progresso do PDF e do Excel vai no pr\u00f3prio r\u00f3tulo, porque o menu fecha no clique. */}
            <MaisAcoes
              rotulo={
                downloadingAll
                  ? `Baixando ${downloadProgress.current}/${downloadProgress.total}...`
                  : excelExporting ? 'Exportando...' : 'Exportar'
              }
              title="Exportar faturas"
              ancora="esquerda"
              itens={[
                {
                  label: `CSV da lista (${filteredInvoices.length})`,
                  onClick: exportarCsvDaLista,
                  disabled: filteredInvoices.length === 0,
                  title: 'As faturas que est\u00e3o na lista, com os filtros de agora',
                },
                {
                  label: `PDF de todas (${filteredInvoices.length})`,
                  onClick: handleDownloadAllPdfs,
                  disabled: filteredInvoices.length === 0 || downloadingAll,
                  title: `Baixar ${filteredInvoices.length} fatura(s) em PDF`,
                },
                {
                  label: 'Excel',
                  onClick: handleExportExcel,
                  disabled: excelExporting,
                  title: 'Exportar faturas para Excel',
                },
              ]}
            />
            {/* Processar CDR grava consumo nas faturas — é escrita, não relatório. */}
            {canEdit && <button
              onClick={openCdrModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium whitespace-nowrap"
              title="Processar arquivo CDR"
            >
              <Icon name="chart" className="w-5 h-5" />
              Processar CDR
            </button>}
            {canEdit && <button
              onClick={() => openModal('generate-batch')}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium whitespace-nowrap"
              title="Gerar faturas para todas as assinaturas ativas"
            >
              <Icon name="calculator" className="w-5 h-5" />
              Gerar Fatura
            </button>}
            {canEdit && <button
              onClick={() => openModal('create')}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap"
            >
              <Icon name="plus" className="w-5 h-5" />
              Nova Fatura
            </button>}
            {/* "Novo Grupo" é raro (1 grupo na base em 05/10/2026, nenhum criado em 90 dias): sai da
                barra e vai para o ⋯, ao lado de Nova Fatura. Continua existindo, só para de ocupar lugar. */}
            {canEdit && (
              <MaisAcoes
                itens={[{
                  label: 'Novo grupo de parcelas',
                  onClick: () => setShowNewGroup(true),
                  title: 'Cria N faturas (1 boleto cada) com 1 NFS-e única — para serviços únicos parcelados',
                }]}
              />
            )}
        </>}
      />

      {showNewGroup && (
        <NewInvoiceGroupModal
          clients={clients}
          onClose={() => setShowNewGroup(false)}
          onCreated={(groupId) => {
            setShowNewGroup(false);
            setOpenGroupId(groupId);
            loadData();
          }}
        />
      )}
      {openGroupId && (
        <InvoiceGroupDetailModal
          groupId={openGroupId}
          onClose={() => setOpenGroupId(null)}
          onUpdate={loadData}
        />
      )}

      {/* Bulk Action Bar */}
      {/* Barra de seleção fixa (12/10/2026, D1 estendido): o <main> rola com padding-top de 24px e o sticky top-0 grudava 24px abaixo
            da borda, com a linha rolada aparecendo ACIMA dele. -top-6 desconta o padding (medido em produção na #470). */}
      {selectedInvoices.length > 0 && (
        <div className="sticky -top-6 z-30 mb-4 p-4 bg-slate-800 shadow-lg shadow-slate-950/50 rounded-xl border border-orange-600/60 flex items-center justify-between" ref={barraDeSelecaoRef}>
          <div className="flex items-center gap-4">
            <span className="text-white font-medium">
              {selectedInvoices.length} fatura(s) selecionada(s)
              <span className="ml-3 text-white font-semibold tabular-nums">
                R$ {fmt(selectedInvoices.reduce((sum, id) => sum + (parseFloat(String(invoices.find(i => i.id === id)?.totalValue)) || 0), 0))}
              </span>
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-slate-400 hover:text-white"
            >
              Limpar seleção
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkDownload}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50"
            >
              <Icon name="download" className="w-4 h-4" />
              Baixar Faturas
            </button>
            <button
              onClick={handleBulkDownloadTechnicalReports}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50"
            >
              <Icon name="chart" className="w-4 h-4" />
              Baixar Relatórios
            </button>
            {(() => {
              // Aprovar só faz sentido com Rascunho na seleção. Antes o botão
              // aparecia sempre, e clicar com fatura Paga selecionada a devolvia
              // para Aberta (o backend agora recusa, mas a tela não deve nem
              // oferecer). Com mistura, mostra quantas serão de fato aprovadas.
              const rascunhos = selectedInvoices.filter(
                id => invoices.find(i => i.id === id)?.status === 'Rascunho',
              );
              if (!canEdit || rascunhos.length === 0) return null;
              const parcial = rascunhos.length < selectedInvoices.length;
              return (
                <button
                  onClick={handleBulkFinalize}
                  disabled={bulkProcessing}
                  title={parcial
                    ? `${rascunhos.length} de ${selectedInvoices.length} selecionadas estão em Rascunho; só elas serão aprovadas`
                    : undefined}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50"
                >
                  <Icon name="check" className="w-4 h-4" />
                  {parcial ? `Aprovar (${rascunhos.length} de ${selectedInvoices.length})` : 'Aprovar'}
                </button>
              );
            })()}
            {canEdit && <button
              onClick={handleBulkResyncDueDates}
              disabled={bulkProcessing}
              title="Atualiza o vencimento das faturas selecionadas pelo dia de vencimento do cadastro do cliente (pagas/canceladas são ignoradas)"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50"
            >
              <Icon name="calendar" className="w-4 h-4" />
              Atualizar venc.
            </button>}
            {canEdit && <button
              onClick={handleBulkSendEmail}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50"
            >
              <Icon name="mail" className="w-4 h-4" />
              Enviar Email
            </button>}
            {canEdit && <button
              onClick={handleBulkSendBoletoCorrection}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50"
            >
              <Icon name="mail" className="w-4 h-4" />
              Comunicado Boleto
            </button>}
            {canEdit && <button
              onClick={handleBulkDelete}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 py-2 bg-transparent border border-central-destrutivo text-central-destrutivo rounded-lg hover:bg-central-destrutivo/10 font-medium disabled:opacity-50"
            >
              <Icon name="trash" className="w-4 h-4" />
              Excluir
            </button>}
          </div>
        </div>
      )}

      {/* Grupo econômico que não carregou: diz que as empresas dele estão aparecendo como avulsas. */}
      <AvisoDosGruposEconomicos grupos={gruposQueFaltaram} erro={erroDosGrupos} onTentarDeNovo={lerGruposDeNovo} />

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Carregando...</div>
      ) : (
        // Carregando, erro ou vazio de verdade (design/EstadoDaLista). A linha "Nenhuma fatura encontrada" da tabela
        // é o vazio legítimo e só aparece com a carga pronta; com a API fora, a tela diz que não carregou.
        <EstadoDaLista carga={cargaDasFaturas} temDados={invoices.length > 0} oQue="as faturas" onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
        <>
        {/* A casca das listas, SEM overflow de rolagem (era overflow-hidden + overflow-x-auto): o cabeçalho fixa no <main>
            e o ⋯ é fixo. A coluna Cliente deixou os 400 px e herda a sobra, para a tabela caber sem rolar de lado. */}
        <CascaDaLista>
            <TabelaDaLista>
              {/* -24 desconta o padding do <main>, como o -top-6 da peça. */}
              <CabecalhoDaLista topo={alturaDaBarraDeSelecao ? alturaDaBarraDeSelecao - 24 : undefined}>
                  <ColunaDaLista largura="w-12" rotulo={
                    <input
                      type="checkbox"
                      checked={filteredInvoices.length > 0 && selectedInvoices.length === filteredInvoices.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                    />
                  } />
                  {([
                    ['Código', 'code', 'w-52', 'esquerda'],
                    ['Cliente', 'clientName', '', 'esquerda'],
                    ['Vencimento', 'dueDate', 'w-40', 'esquerda'],
                    ['Valor Total', 'totalValue', 'w-36', 'direita'],
                    ['Status', 'status', 'w-28', 'centro'],
                  ] as const).map(([rotulo, col, largura, alinhamento]) => (
                    <ColunaDaLista key={col} rotulo={rotulo} largura={largura} alinhamento={alinhamento}
                      ordem={sort.col === col ? sort.dir : false} onOrdenar={() => toggleSort(col)} />
                  ))}
                  <ColunaDaLista rotulo={<span title="Nota fiscal, boleto e e-mail. Em branco ou cinza: em dia.">Documentos</span>} largura="w-36" alinhamento="centro" />
                  <ColunaDaLista rotulo="Pagamento" largura="w-32" alinhamento="centro" ordem={sort.col === 'paymentDate' ? sort.dir : false} onOrdenar={() => toggleSort('paymentDate')} />
                  <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-40" />
              </CabecalhoDaLista>
              <tbody className="divide-y divide-slate-800">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="p-4 bg-slate-700/50 rounded-full mb-4">
                          <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <p className="text-slate-400 font-medium">Nenhuma fatura encontrada</p>
                        <p className="text-slate-500 text-sm mt-1">Ajuste os filtros ou gere novas faturas</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row) => {
                    if (row.kind === 'grupoEcon') {
                      const aberto = !gruposEconFechados.has(row.chave);
                      const p = paineisDosGrupos.get(row.matrizId);
                      const empresasDoGrupo = p ? p.empresas.map((e) => e.clientId) : Array.from(new Set(row.invoices.map((i) => i.clientId)));
                      const r = resumoDoGrupoNaFatura(row.invoices, empresasDoGrupo);
                      const ids = row.invoices.map((i) => i.id);
                      const todas = ids.length > 0 && ids.every((id) => selectedInvoices.includes(id));
                      const algumas = ids.some((id) => selectedInvoices.includes(id));
                      const matriz = clients.find((c) => c.id === row.matrizId);
                      const pagas = row.invoices.filter((i) => i.status === 'Paga').length;
                      const status = pagas === row.invoices.length ? 'Paga'
                        : row.invoices.every((i) => i.status === 'Cancelada') ? 'Cancelada'
                        : pagas > 0 ? 'Parcial'
                        : row.invoices.some((i) => isOverdue(i)) ? 'Vencida'
                        : row.invoices.every((i) => i.status === 'Rascunho') ? 'Rascunho'
                        : 'Aberta';
                      const ref = row.invoices[0]?.referenceMonth;
                      const comp = ref ? `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(ref.split('-')[1], 10) - 1]}/${ref.split('-')[0]}` : '';
                      return (
                        <tr key={`econ-${row.chave}`} className={`${TONE_SURFACE.group} hover:brightness-125 transition`}>
                          <td className={`px-4 py-3 ${BARRA_DE_GRUPO.group}`}>
                            <div className="flex items-center gap-2">
                              {/* Marca as faturas FILHAS, uma a uma: o lote conta quantas são.
                                  Não existe "enviar o grupo". */}
                              <input
                                type="checkbox"
                                checked={todas}
                                ref={(el) => { if (el) el.indeterminate = algumas && !todas; }}
                                onChange={() => setSelectedInvoices((prev) => (todas ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))))}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                                title={todas ? 'Desmarcar as faturas do grupo' : `Marcar as ${ids.length} faturas do grupo, uma a uma`}
                              />
                              <button
                                onClick={() => setGruposEconFechados((prev) => { const n = new Set(prev); if (n.has(row.chave)) n.delete(row.chave); else n.add(row.chave); return n; })}
                                className={`${TONE_TEXT.group} hover:text-white`}
                                title={aberto ? 'Recolher as faturas do grupo' : 'Mostrar as faturas do grupo'}
                                aria-expanded={aberto}
                              >
                                {/* E1 (03/11/2026): a mesma seta de Assinaturas, o chevron que gira. */}
                                <span className={`inline-block transition-transform ${aberto ? 'rotate-90' : ''}`}><Icon name="chevronRight" className="w-4 h-4" /></span>
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${TONE_BADGE.group} whitespace-nowrap`}>grupo · {row.invoices.length} {row.invoices.length === 1 ? 'fatura' : 'faturas'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="min-w-0">
                              <p className="font-medium text-white truncate">{matriz?.name?.trim() || matriz?.tradeName || matriz?.companyName || `Grupo #${row.matrizId}`}</p>
                              <p className={`text-xs truncate ${r.faturadas < r.empresas ? 'text-amber-300' : 'text-slate-400'}`} title="Empresas do grupo com fatura nesta competência">
                                {comp} · faturou {r.faturadas} de {r.empresas}
                                {r.comBoleto > 0 && <span className={r.boletosConfirmados < r.comBoleto ? 'text-amber-300' : 'text-slate-400'}> · {r.boletosConfirmados} de {r.comBoleto} {r.comBoleto === 1 ? 'boleto confirmado' : 'boletos confirmados'}</span>}
                              </p>
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-left text-sm whitespace-nowrap ${r.vencimentosDivergem ? 'text-amber-300' : 'text-slate-300'}`} title={r.vencimentosDivergem ? 'Os vencimentos divergem dentro do grupo' : undefined}>
                            {r.vencimento}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="text-sm font-semibold text-central-texto tabular-nums">R$ {fmt(r.total)}</div>
                            <div className="text-[11px] text-slate-500">soma do grupo</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {/* O mesmo mapa da linha (utils/tomDoStatusDaFatura): aqui Cancelada era vermelha e Rascunho, slate. */}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(status)}`}>{status}</span>
                            {pagas > 0 && pagas < row.invoices.length && <div className="text-[11px] text-slate-500 mt-0.5">{pagas}/{row.invoices.length} pagas</div>}
                          </td>
                          <td className="px-4 py-3 text-center">{getDocumentosDoGrupo(row.invoices)}</td>
                          <td className="px-4 py-3 text-center text-slate-500 text-xs">—</td>
                          {/* Sem ações: grupo não emite, não baixa boleto, não envia. */}
                          <td className="px-4 py-3 text-center text-slate-600 text-xs" title="Ações ficam em cada fatura">—</td>
                        </tr>
                      );
                    }
                    if (row.kind === 'group') {
                      const expanded = expandedGroupIds.has(row.groupId);
                      const groupInvoiceIds = row.invoices.map(i => i.id);
                      const allSelected = groupInvoiceIds.every(id => selectedInvoices.includes(id));
                      const someSelected = groupInvoiceIds.some(id => selectedInvoices.includes(id));
                      const toggleGroupSelection = () => {
                        if (allSelected) {
                          setSelectedInvoices(prev => prev.filter(id => !groupInvoiceIds.includes(id)));
                        } else {
                          setSelectedInvoices(prev => Array.from(new Set([...prev, ...groupInvoiceIds])));
                        }
                      };
                      const groupTotal = row.invoices.reduce((s, i) => s + (parseFloat(String(i.totalValue)) || 0), 0);
                      const dueDates = row.invoices.map(i => i.dueDate).filter(Boolean).sort();
                      const dueRange = dueDates.length > 0
                        ? `${fmtShortDate(dueDates[0])} → ${fmtShortDate(dueDates[dueDates.length - 1])}`
                        : '—';
                      const paidCount = row.invoices.filter(i => i.status === 'Paga').length;
                      const cancelledCount = row.invoices.filter(i => i.status === 'Cancelada').length;
                      const aggStatus = paidCount === row.invoices.length ? 'Paga'
                        : cancelledCount === row.invoices.length ? 'Cancelada'
                        : paidCount > 0 ? 'Parcial'
                        : row.invoices.some(i => isOverdue(i)) ? 'Vencida'
                        : 'Aberta';
                      const firstInv = row.invoices[0];
                      const client = clients.find((c) => c.id === firstInv?.clientId);
                      return (
                        // Grupo de PARCELAS: tom info, não o group do grupo econômico (§4).
                        <tr key={`grp-${row.groupId}`} className={`hover:brightness-125 transition ${TONE_SURFACE.info}`}>
                          <td className={`px-4 py-3 ${BARRA_DE_GRUPO.info}`}>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                onChange={toggleGroupSelection}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                                title={allSelected ? 'Desmarcar todas as parcelas' : 'Marcar todas as parcelas'}
                              />
                              <button
                                onClick={() => toggleGroupExpand(row.groupId)}
                                className={`${TONE_TEXT.info} hover:text-white`}
                                title={expanded ? 'Recolher parcelas' : 'Expandir parcelas'}
                                aria-expanded={expanded}
                              >
                                <span className={`inline-block transition-transform ${expanded ? 'rotate-90' : ''}`}><Icon name="chevronRight" className="w-4 h-4" /></span>
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setOpenGroupId(row.groupId)}
                              className="font-mono text-sm text-slate-300 bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded"
                            >{row.groupCode}</button>
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium ${TONE_BADGE.info}`}>
                              {row.invoices.length} PARCELAS
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <ClientPopover clientId={firstInv?.clientId || 0}>
                              <div className="min-w-0">
                                <p className="font-medium text-white truncate">{client?.name?.trim() || client?.companyName || '-'}</p>
                                <p className="text-xs text-slate-500 truncate">{client?.companyName || '-'}</p>
                              </div>
                            </ClientPopover>
                          </td>
                          <td className="px-4 py-3 text-left text-slate-300 text-sm whitespace-nowrap">
                            {dueRange}
                            <div className="text-[11px] text-slate-500">{firstInv?.referenceMonth ? `comp. ${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(firstInv.referenceMonth.split('-')[1],10)-1]}/${firstInv.referenceMonth.split('-')[0]}` : ''}</div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="text-sm font-semibold text-central-texto tabular-nums">R$ {fmt(groupTotal)}</div>
                            <div className="text-[11px] text-slate-500">total do grupo</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {/* O mesmo mapa da linha: aqui Cancelada era vermelha e Rascunho caía no verde de Aberta. */}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(aggStatus)}`}>{aggStatus}</span>
                            {paidCount > 0 && paidCount < row.invoices.length && (
                              <div className="text-[11px] text-slate-500 mt-0.5">{paidCount}/{row.invoices.length} pagas</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">{getDocumentosDoGrupo(row.invoices)}</td>
                          <td className="px-4 py-3 text-center text-slate-500 text-xs">—</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => setOpenGroupId(row.groupId)}
                              className="text-slate-400 hover:text-white text-xs font-medium px-2 py-1 hover:bg-slate-700 rounded"
                            >Detalhes</button>
                          </td>
                        </tr>
                      );
                    }
                    const inv = row.invoice;
                    const client = clients.find((c) => c.id === inv.clientId);
                    const overdue = isOverdue(inv);
                    // Filha de grupo: fundo do grupo (mais leve que a linha dele) e a barra na primeira célula, para
                    // se ver onde o grupo começa e termina. Selecionada e vencida continuam vencendo o tom do grupo.
                    const tomDoGrupo: 'group' | 'info' | null = row.isChildOfGroup ? (row.econ ? 'group' : 'info') : null;
                    // E1: a vencida diz pelo prazo (a CelulaPrazo e a barra no tom do atraso, como em Recebíveis); a paga
                    // deixou o fundo verde. O prazo só acusa o que a tela chama de vencida (isOverdue): o Rascunho de data
                    // passada não vira "vencido há N dias".
                    const tomDaVencida = overdue ? tomDoPrazo(inv.dueDate) || 'danger' : null;
                    const selecionada = selectedInvoices.includes(inv.id);
                    return (
                      <LinhaDaLista key={inv.id} destacada={selecionada} className={!selecionada && tomDoGrupo ? FILHA_DE_GRUPO[tomDoGrupo] : ''} data-filha-de-grupo={tomDoGrupo || undefined}>
                        <td className={`${CELULA} ${tomDoGrupo ? BARRA_DE_GRUPO[tomDoGrupo] : barraDoEstado(tomDaVencida)}`}>
                          <input
                            type="checkbox"
                            checked={selectedInvoices.includes(inv.id)}
                            onChange={() => toggleSelectInvoice(inv.id)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                          />
                        </td>
                        <td className={CELULA}>
                          {/* `flex-wrap`, nao `whitespace-nowrap`: a tabela e
                              `table-fixed` e esta coluna tem largura fixa, entao
                              o badge que nao cabe VAZAVA por cima do nome do
                              cliente em vez de quebrar (COBRADA 1x sobre a razao
                              social). Com wrap ele desce para a linha de baixo,
                              dentro da propria celula. */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-sm text-slate-300">
                            {tomDoGrupo && (
                              <span className={`mr-1.5 ${TONE_TEXT[tomDoGrupo]}`} aria-hidden data-galho={row.ultima ? 'fim' : 'meio'}>
                                {row.ultima ? '└' : '├'}
                              </span>
                            )}
                            {inv.code}
                          </span>
                          {inv.discountStatus === 'antecipado' && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${TONE_BADGE.info}`} title={inv.borderoNumber ? `Borderô ${inv.borderoNumber}` : 'Antecipado via borderô'}>ANT</span>
                          )}
                          {inv.discountStatus === 'estornado' && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${TONE_BADGE.danger}`} title="Antecipação estornada">EST</span>
                          )}
                          {inv.invoiceGroupId && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenGroupId(inv.invoiceGroupId!); }}
                              className={`px-1.5 py-0.5 rounded text-xs font-medium ${TONE_BADGE.info} hover:brightness-125`}
                              title={`Parcela ${inv.installmentNumber}/${inv.totalInstallments} do grupo${inv.invoiceGroup?.code ? ' ' + inv.invoiceGroup.code : ''} — clique para ver detalhes`}
                            >
                              GRP {inv.installmentNumber}/{inv.totalInstallments}
                            </button>
                          )}
                          {/* Tipo da fatura (consolidada, avulsa): mora no código, não no cliente (auditoria do
                              painel, 07/10/2026). É da fatura; na coluna Cliente empurrava o nome. */}
                          {tipoDaFatura(inv) === 'consolidada' && (
                            <span className={`px-1.5 py-0.5 ${TONE_BADGE.info} text-xs font-medium rounded`}>
                              CONSOLIDADA
                            </span>
                          )}
                          {tipoDaFatura(inv) === 'avulsa' && (
                            <span className={`px-1.5 py-0.5 ${TONE_BADGE.neutral} text-xs font-medium rounded`} title="Fatura lançada à parte (retroativo, ajuste): não cobre a competência da assinatura">
                              AVULSA
                            </span>
                          )}
                          {/* PIX vencido: o QR que o cliente tem na caixa de
                              entrada saiu com valor FECHADO e prazo curto.
                              Passado o prazo aquele número não é mais o valor
                              devido — juros correram —, e pior: a conciliação
                              automática casa o crédito pelo valor gravado no
                              envio, então um pagamento pelo código velho entra
                              sem dono. Cobrar de novo gera QR novo. */}
                          {(() => {
                            const c = dunningMap[inv.id];
                            if (!c?.pixExpirado) return null;
                            return (
                              <span
                                className={`px-1.5 py-0.5 rounded text-xs font-medium ${TONE_BADGE.danger}`}
                                title={`O PIX enviado valia até ${c.pixValidUntil?.split('-').reverse().join('/')}. O valor daquele QR está defasado — cobre de novo para gerar um código atualizado.`}
                              >
                                PIX VENCEU
                              </span>
                            );
                          })()}
                          {/* Estado da cobrança, SÓ PARA LER (B1 da unificação, 30/10/2026): cobrar e
                              silenciar moram na Cobrança. Clicar leva à linha desta fatura lá. */}
                          {(() => {
                            const c = dunningMap[inv.id];
                            if (!c) return null;
                            const logsCob = dunningLogsMap[inv.id] || [];
                            const abriu = logsCob.some(l => l.openedAt);
                            const titulo = c.snoozedUntil
                              ? `Silenciada até ${fmtDate(c.snoozedUntil)}${c.snoozeReason ? ': ' + c.snoozeReason : ''}`
                              : c.dunningCount > 0
                                ? `Cobrada ${c.dunningCount}× · última em ${fmtDate(c.lastDunningAt)}${abriu ? ` · cliente ABRIU a cobrança${logsCob.some(l => (l.openCount || 0) > 1) ? ' mais de uma vez' : ''}` : logsCob.length > 0 ? ' · ainda não abriu' : ''}`
                                : c.blockedReason || `Vencida há ${c.daysOverdue} dias, pronta para cobrar`;
                            const cls = c.snoozedUntil
                              ? TONE_BADGE.neutral
                              : c.dunningCount > 0
                                ? TONE_BADGE.warning
                                : c.blockedReason
                                  ? TONE_BADGE.neutral
                                  : TONE_BADGE.danger;
                            // O olho abre a informacao que decide o proximo passo:
                            // quem ABRIU e nao pagou ja sabe da divida (cobrar de
                            // novo rende pouco, telefone rende mais); quem nao
                            // abriu pode nem ter recebido.
                            const texto = c.snoozedUntil
                              ? 'SILENCIADA'
                              : c.dunningCount > 0
                                ? `COBRADA ${c.dunningCount}×${abriu ? ' 👁' : ''}`
                                : `${c.daysOverdue}D`;
                            if (!onVerNaCobranca) {
                              return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${cls}`} title={titulo} data-selo-cobranca>{texto}</span>;
                            }
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onVerNaCobranca(inv.code); }}
                                className={`px-1.5 py-0.5 rounded text-xs font-medium hover:brightness-125 ${cls}`}
                                title={`${titulo} · ver na Cobrança`}
                                data-selo-cobranca
                              >
                                {texto}
                              </button>
                            );
                          })()}
                          </div>
                        </td>
                        {/* C1: o nome (que abre o cartão do cliente) e UMA sub-linha, razão · CNPJ. O nome que não cabe corta
                            com reticências e aparece inteiro no cartão. */}
                        <CelulaCliente
                          nome={<ClientPopover clientId={inv.clientId} block>{client?.name?.trim() || client?.companyName || '-'}</ClientPopover>}
                          razao={client?.companyName && client.companyName !== client?.name?.trim() ? client.companyName : null}
                          cnpj={client?.cnpj}
                          extra={!row.isChildOfGroup && grupoSolto.get(inv.clientId) ? <MarcaDoGrupo marca={grupoSolto.get(inv.clientId)!} /> : undefined}
                        />
                        {/* O vencimento é a peça do prazo (a Situação de Recebíveis); a competência embaixo. */}
                        <CelulaPrazo vencimento={inv.dueDate} quitado={!overdue} rotuloQuitado={null} mostrarData>
                          <span className="text-[11px] text-slate-500">comp. {getCompetencia(inv)}</span>
                        </CelulaPrazo>
                        {/* E1: o valor é branco com peso; o estado mora no Status e no prazo (era verde, laranja, vermelho e azul). */}
                        <CelulaDinheiro valor={inv.totalValue}>
                          {/* Segunda linha: o que falta NESTE valor, o reajuste (pendente ou aplicado depois). Morava
                              na coluna Cliente e a empurrava (auditoria do painel, 07/10/2026). O CDR NÃO mora aqui:
                              vai na coluna Documentos, sempre (decisão do Heitor, 07/10/2026). Com os dois aqui, a
                              linha ia de 67 a 91 px em 6 das 10 faturas com reajuste de outubro. */}
                          <div className="flex flex-wrap justify-end gap-1 mt-0.5 empty:hidden">
                              {/* Selo "reajuste pendente": o período desta fatura já alcança
                                  anualidade devida/pedida/deferida do contrato. NÃO trava
                                  aprovação: a regra é faturar o valor antigo até a apostila e
                                  cobrar o retroativo em complementar. Só o aviso + diferença. */}
                              {inv.contractId && inv.status !== 'Cancelada' && (() => {
                                const p = pendingAdjustmentForInvoice(contracts.find((c: any) => c.id === inv.contractId), inv);
                                if (!p) return null;
                                const a = p.latest;
                                return (
                                  <span
                                    className={`px-1.5 py-0.5 ${TONE_BADGE.warning} text-xs font-medium rounded whitespace-nowrap`}
                                    title={`Reajuste pendente (${a.status}): ${a.indexName || ''} ${fmtPctAdj(a.percentage)} desde ${String(a.effectsFrom).slice(0, 10).split('-').reverse().join('/')}. Esta fatura saiu ${fmtBRLAdj(p.monthlyDiff)} a menos. Faturar o valor antigo até a apostila; o retroativo vai em complementar.`}
                                  >
                                    reajuste {p.monthlyDiff > 0 ? `+${fmtBRLAdj(p.monthlyDiff)}` : 'pendente'}
                                  </span>
                                );
                              })()}
                              {/* Selo "reajuste aplicado depois": o caso oposto do de cima.
                                  Vem pronto do backend (reajusteNaoCobrado, só na
                                  listagem); NÃO recalcular aqui contra o mrr atual do
                                  contrato. Paga → complementar; aberta → corrigir; complementar
                                  em rascunho → emitir a que já existe (amarelo). */}
                              {inv.status !== 'Cancelada' && (() => {
                                const s = seloReajusteNaoCobrado(inv);
                                if (!s) return null;
                                return (
                                  <span
                                    className={`px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap cursor-help ${s.situacao === 'rascunho' ? TONE_BADGE.warning : TONE_BADGE.danger}`}
                                    title={s.titulo}
                                  >
                                    {s.texto}
                                  </span>
                                );
                              })()}
                          </div>
                        </CelulaDinheiro>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                overdue ? 'Vencida' : inv.status
                              )}`}
                            >
                              {overdue ? 'Vencida' : inv.status}
                            </span>
                            {motivoRetencao(inv) && (
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.warning} cursor-help`}
                                title={motivoRetencao(inv) || ''}
                              >
                                🔒 Retida
                              </span>
                            )}
                            {inv.status === 'Parcial' && inv.paidValue > 0 && (
                              <span className={`text-[11px] ${TONE_TEXT.warning}`}>
                                R$ {fmt(inv.paidValue)} / {fmt(inv.totalValue)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getDocumentosCell(inv)}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {/* Data do ÚLTIMO pagamento + quantos houve + a lista no
                              relógio. `paymentDate` sozinho não cabe em fatura
                              paga em mais de uma vez (FUNDETEC). */}
                          <PagamentoDaFatura inv={inv as any} />
                        </td>
                        {/* D1: o olho e o PDF à vista (o olho voltou à linha a pedido do Heitor, 22/10/2026: "usamos bastante");
                            o resto no ⋯ (MaisAcoes, fixo), com Cancelar e Excluir em perigo e separados. As confirmações são as de
                            antes, da tela. */}
                        <CelulaAcoes
                          rotuloDoMenu={`Mais ações da fatura ${inv.code}`}
                          antes={
                            <button type="button" onClick={() => openModal('view', inv)} className="p-1.5 text-slate-400 hover:text-white hover:bg-central-elevado rounded-lg" title="Ver detalhes" aria-label="Ver detalhes">
                              <Icon name="eye" className="w-4 h-4" />
                            </button>
                          }
                          verbo={{ rotulo: 'PDF', title: 'Baixar PDF', onClick: () => openDownloadModal(inv) }}
                          itens={[
                            ...(canEdit && inv.status === 'Rascunho' ? [
                              { label: 'Editar', onClick: () => openModal('edit', inv) },
                              { label: 'Finalizar', onClick: () => handleFinalize(inv.id) },
                            ] : []),
                            ...(canEdit && inv.status === 'Aberta' ? [
                              { label: 'Enviar Email', onClick: () => handleSendEmail(inv.id), disabled: sendingEmail === inv.id },
                              { label: 'Reabrir p/ Edição', onClick: () => handleReopen(inv.id) },
                            ] : []),
                            ...(canEdit ? [{ label: 'Duplicar', onClick: () => handleDuplicate(inv) }] : []),
                            ...(client?.ec2InstanceId ? [{ label: 'Rel. Técnico', onClick: () => handleGenerateTechnicalReport(inv) }] : []),
                            ...(canEdit && inv.status === 'Aberta' ? [{ label: 'Cancelar', onClick: () => handleCancel(inv.id), danger: true, separadoAntes: true }] : []),
                            ...(canEdit && podeExcluirFatura(inv) ? [{ label: 'Excluir', onClick: () => handleDelete(inv.id), danger: true, separadoAntes: !(canEdit && inv.status === 'Aberta') }] : []),
                          ]}
                        />
                      </LinhaDaLista>
                    );
                  })
                )}
              </tbody>
            </TabelaDaLista>
        </CascaDaLista>
        </>
        </EstadoDaLista>
      )}

      {/* Create/Edit Modal, na CascaDeModal em altura FIXA: "Dados Gerais" e "Itens e
          Consumo" têm tamanhos bem diferentes e a caixa saltava a cada troca de aba.
          Só o miolo rola. O total está na faixa e no rodapé, nas duas abas. */}
      {(modal === 'create' || modal === 'edit') && (() => {
        const clienteDoForm = clients.find((c: any) => c.id === form.clientId);
        const nomeDoForm = nomeDeExibicao(clienteDoForm);
        const dias = diasNoPeriodo(form.periodStart, form.periodEnd);
        const diasDoCiclo = form.referenceMonth && form.periodStart ? getCycleDays(form.referenceMonth, String(form.periodStart).split('T')[0]) : 0;
        const comPeriodoProprio = itensComPeriodoProprio(form.items, form.periodStart, form.periodEnd);
        const faixaDoEditor: NumeroDaFaixa[] = [
          { k: 'Total', v: fmt(totalValue), cls: 'text-white', dica: 'Atualiza enquanto você edita' },
          {
            k: 'Serviço',
            v: (
              <span>
                {periodoDeServico(form.periodStart, form.periodEnd, fmtShortDate)}
                {dias > 0 && diasDoCiclo > 0 && (
                  <span className={`ml-1.5 text-xs font-medium ${dias < diasDoCiclo ? TONE_TEXT.warning : TONE_TEXT.open}`}>{dias}/{diasDoCiclo}d</span>
                )}
              </span>
            ),
            dica: dias > 0 ? `${dias} dia(s) de ${diasDoCiclo || '—'} do ciclo` : undefined,
          },
          { k: 'Vencimento', v: form.dueDate ? fmtDate(form.dueDate) : '—' },
          { k: 'Itens', v: (form.items || []).length },
          ...(comPeriodoProprio > 0
            ? [{ k: 'Período próprio', v: `${comPeriodoProprio} ${comPeriodoProprio === 1 ? 'item' : 'itens'}`, cls: 'text-amber-300', dica: 'Itens com período diferente do da fatura (proporcional). Confira antes de salvar.' }]
            : []),
        ];
        return (
        <CascaDeModal
          altura="fixa"
          onClose={closeModal}
          temEdicaoNaoSalva={() => JSON.stringify(form) !== JSON.stringify(formAoAbrir)}
          titulo={modal === 'create' ? (nomeDoForm ? `Nova fatura · ${nomeDoForm}` : 'Nova fatura') : `Editar fatura · ${nomeDoForm || nomeDeExibicao(selectedInvoice?.client)}`}
          subtitulo={[
            modal === 'edit' ? selectedInvoice?.code : null,
            form.referenceMonth ? `competência ${form.referenceMonth.split('-').reverse().join('/')}` : null,
            clienteDoForm?.cnpj ? `CNPJ ${fmtCNPJ(clienteDoForm.cnpj)}` : null,
          ].filter(Boolean).join(' · ') || undefined}
          selos={modal === 'edit' && selectedInvoice ? (
            <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedInvoice.status)}`}>{selectedInvoice.status}</span>
          ) : undefined}
          aviso={
            <>
            {/* O que já saiu para o mundo, acima das abas para valer nas duas.
                Não bloqueia: às vezes é preciso corrigir. Ver utils/avisoDeEdicaoDaFatura. */}
            {modal === 'edit' && (() => {
              const aviso = avisoDeEdicao(selectedInvoice, fiscalDocsStatus?.invoiceId === selectedInvoice?.id ? fiscalDocsStatus?.fiscalDocuments : [], fmt);
              if (!aviso) return null;
              return (
                <div className="mx-5 mt-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-200" role="alert">
                  <Icon name="alertCircle" className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                  <span>{aviso.texto}</span>
                </div>
              );
            })()}
            </>
          }
          abas={[{ chave: 'dados', rotulo: 'Dados gerais' }, { chave: 'itens', rotulo: 'Itens e consumo' }]}
          abaAtiva={activeTab}
          onAba={(k) => setActiveTab(k as any)}
          faixa={faixaDoEditor}
          corpoClassName="p-6"
          rodape={
            <>
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.clientId || !form.referenceMonth}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Salvando...' : 'Salvar fatura'}
              </button>
              <span className={`ml-auto font-mono text-base font-semibold ${TONE_TEXT.open}`} title="Total geral da fatura">R$ {fmt(totalValue)}</span>
            </>
          }
        >
              {activeTab === 'dados' && (
                // Coluna que ocupa a altura toda: os campos no topo, e o que sobra
                // vai para as observações, o único campo que cresce.
                <div className="flex flex-col gap-4 min-h-full">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Cliente *
                      </label>
                      <select
                        value={form.clientId || 0}
                        onChange={(e) => handleClientChange(Number(e.target.value))}
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      >
                        <option value={0}>Selecione...</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {nomeDeExibicao(c)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Assinatura (opcional)
                      </label>
                      <select
                        value={form.contractId || ''}
                        onChange={(e) =>
                          handleContractChange(e.target.value ? Number(e.target.value) : 0)
                        }
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      >
                        <option value="">Sem assinatura</option>
                        {contracts
                          .filter((c) => !form.clientId || c.clientId === form.clientId)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              #{c.id} - {nomeDeExibicao(c.client, 'Assinatura')} (MRR: R$ {fmt(c.mrr)})
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Competencia (Mes/Ano) *
                      </label>
                      {/* A competência é o mês da fatura (o da geração mensal); o período de serviço sai dela pela regra
                          do cliente, lida da geração do backend. Ver utils/previaDaGeracao. */}
                      <input
                        type="month"
                        aria-label="Competência"
                        value={form.referenceMonth || ''}
                        onChange={(e) => {
                          const month = e.target.value;
                          setForm({ ...form, referenceMonth: month });
                          lerPreviaDaGeracao(month, form.clientId, modal === 'create' ? form.contractId : null, modal === 'create');
                        }}
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      />
                      <p className="mt-1 text-xs text-slate-400" data-periodo-da-geracao>
                        {previaDaGeracao?.estado === 'lendo' ? 'Lendo o período da geração...'
                          : previaDaGeracao?.estado === 'erro' ? (
                            <span className={TONE_TEXT.danger}>
                              Não consegui ler o período da geração ({previaDaGeracao.erro}).{' '}
                              <button type="button" onClick={previaDaGeracao.repetir} className="underline">Tentar de novo</button>
                            </span>
                          )
                          : previaDaGeracao?.previa ? `Serviço de ${fmtDate(previaDaGeracao.previa.periodStart)} a ${fmtDate(previaDaGeracao.previa.periodEnd)}${rotuloDaRegra(previaDaGeracao.previa.regraDoPeriodo) ? `, ${rotuloDaRegra(previaDaGeracao.previa.regraDoPeriodo)}` : ''}`
                          : modal === 'create' && !form.clientId ? 'O período sai da regra do cliente: escolha o cliente ou a assinatura.'
                          : null}
                      </p>
                      {/* A geração recusaria este mês: a prévia diz qual fatura já está nele, e o operador decide. O POST
                          /invoices não tem o dedup do mês (Bastião, #588), por isso o aviso diz o que o salvar faz. */}
                      {modal === 'create' && previaDaGeracao?.previa?.faturaExistente && (
                        <p className={`mt-1 text-xs ${TONE_TEXT.warning}`} data-fatura-existente>
                          Esta assinatura já tem a {previaDaGeracao.previa.faturaExistente.code} ({previaDaGeracao.previa.faturaExistente.status}) nesta competência. Salvar aqui cria uma segunda fatura para o mesmo mês, porque a criação manual não confere isso.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Data de Vencimento
                      </label>
                      <input
                        type="date"
                        value={form.dueDate || ''}
                        onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Data de Emissao
                      </label>
                      <input
                        type="date"
                        value={form.issueDate || ''}
                        onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Periodo Inicio
                      </label>
                      <input
                        type="date"
                        value={form.periodStart || ''}
                        onChange={(e) => setInvoicePeriod('periodStart', e.target.value)}
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Periodo Fim
                      </label>
                      <input
                        type="date"
                        value={form.periodEnd || ''}
                        onChange={(e) => setInvoicePeriod('periodEnd', e.target.value)}
                        className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                      />
                    </div>

                  </div>

                  <div className="flex-1 flex flex-col">
                    {/* O aviso sobe para o rótulo, em âmbar: é a diferença entre nota
                        interna e texto que o cliente (ou o órgão) lê. */}
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Observações <span className="font-normal text-amber-300">· sai no PDF da fatura e na NFCom, o cliente lê</span>
                    </label>
                    <textarea
                      value={form.notes || ''}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={3}
                      className={`flex-1 min-h-[6rem] w-full px-4 py-2.5 ${campo} rounded-lg resize-none`}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'itens' && (
                <div className="space-y-6">
                  {/* Items Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-slate-300 uppercase">
                        Itens da Fatura (Assinaturas)
                      </h4>
                      <div className="flex gap-2">
                        <button
                          onClick={fetchPendingPSItems}
                          disabled={loadingPS || !form.contractId}
                          className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm disabled:opacity-50"
                          title={!form.contractId ? 'Selecione uma assinatura primeiro' : 'Puxar itens P&S pendentes do contrato'}
                        >
                          <Icon name="download" className="w-4 h-4" />
                          {loadingPS ? 'Carregando...' : 'Puxar P&S'}
                        </button>
                        <button
                          onClick={addItem}
                          className="flex items-center gap-1 px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success-strong text-sm"
                        >
                          <Icon name="plus" className="w-4 h-4" />
                          Adicionar Item
                        </button>
                      </div>
                    </div>

                    {(form.items || []).length === 0 ? (
                      <p className="text-slate-500 text-sm py-4 text-center">
                        Nenhum item adicionado
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {(form.items || []).map((item: any, index: number) => {
                          // Calcular indicador de dias
                          const pStart = item.periodStart || form.periodStart?.split('T')[0] || '';
                          const pEnd = item.periodEnd || form.periodEnd?.split('T')[0] || '';
                          let periodDays = 0;
                          let totalDaysInMonth = 0;
                          if (pStart && pEnd && form.referenceMonth) {
                            totalDaysInMonth = getCycleDays(form.referenceMonth, pStart);
                            const start = new Date(pStart);
                            const end = new Date(pEnd);
                            periodDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                          }
                          const isPartial = periodDays > 0 && periodDays < totalDaysInMonth;

                          return (
                            <div
                              key={index}
                              className="bg-central-fundo rounded-lg border border-central-borda overflow-hidden"
                            >
                              {/* Linha 1: Qtd, Nome, Lixeira */}
                              <div className="flex items-end gap-3 p-4 pb-3">
                                <div className="w-16">
                                  <label className="block text-xs text-slate-500 mb-1">Qtd</label>
                                  <input
                                    type="number"
                                    value={item.quantity || 1}
                                    onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                                    className={`w-full px-2 py-2 rounded-lg text-sm text-center ${campo}`}
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs text-slate-500 mb-1">Nome do Servico</label>
                                  <input
                                    type="text"
                                    value={item.name || ''}
                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`}
                                  />
                                </div>
                                <div className="w-10">
                                  <button
                                    onClick={() => removeItem(index)}
                                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                                    title="Remover"
                                  >
                                    <Icon name="trash" className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Linha 2: Período e Valores */}
                              <div className="flex items-end gap-2 px-4 pb-4">
                                <div className="w-28">
                                  <label className="block text-xs text-slate-500 mb-1">De</label>
                                  <input
                                    type="date"
                                    value={pStart}
                                    onChange={(e) => updateItem(index, 'periodStart', e.target.value)}
                                    className={`w-full px-2 py-2 rounded-lg text-xs ${campo}`}
                                  />
                                </div>
                                <div className="w-28">
                                  <label className="block text-xs text-slate-500 mb-1">Até</label>
                                  <input
                                    type="date"
                                    value={pEnd}
                                    onChange={(e) => updateItem(index, 'periodEnd', e.target.value)}
                                    className={`w-full px-2 py-2 rounded-lg text-xs ${campo}`}
                                  />
                                </div>
                                {periodDays > 0 && (
                                  <div className={`px-2 py-2 rounded-lg text-xs whitespace-nowrap ${isPartial ? TONE_BADGE.warning : TONE_BADGE.open}`}>
                                    {periodDays}/{totalDaysInMonth}d
                                  </div>
                                )}
                                <div className="flex-1"></div>
                                <div className="w-24">
                                  <label className="block text-xs text-slate-400 mb-1">Vlr Base</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.baseValue || ''}
                                    onChange={(e) => updateItem(index, 'baseValue', Number(e.target.value))}
                                    placeholder={fmt(item.unitValue || 0)}
                                    className={`w-full px-2 py-2 rounded-lg text-sm text-right placeholder:text-slate-500 ${campo}`}
                                    title="Valor cheio do mês (muda datas = recalcula)"
                                  />
                                </div>
                                <div className="w-24">
                                  <label className="block text-xs text-slate-500 mb-1">Vlr Unit</label>
                                  <div className={`px-2 py-2 ${campoSomenteLeitura} rounded-lg text-sm text-right`}>
                                    {fmt(item.unitValue || 0)}
                                  </div>
                                </div>
                                <div className="w-24">
                                  <label className="block text-xs text-slate-500 mb-1">Total</label>
                                  <div className={`px-2 py-2 rounded-lg text-sm font-semibold text-right ${isPartial ? TONE_BADGE.warning : TONE_BADGE.open}`}>
                                    {fmt(item.totalValue)}
                                  </div>
                                </div>
                              </div>

                              {/* Por que a linha cobra parte do período (vigência do item, da prévia da geração). */}
                              {item.motivoDaVigencia && (
                                <div className={`px-4 pb-3 -mt-1 text-xs ${TONE_TEXT.warning}`} data-vigencia>
                                  {item.motivoDaVigencia}
                                </div>
                              )}

                              {/* Descrição */}
                              {item.description && (
                                <div className="px-4 pb-4">
                                  <div className="px-3 py-2 bg-central-elevado/50 border border-central-borda text-orange-300/70 rounded-lg text-xs leading-relaxed whitespace-pre-line">
                                    {item.description}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {modal === 'create' && previaDaGeracao?.previa && (() => {
                      const p = previaDaGeracao.previa;
                      if (!p.foraDoPeriodo.length && !p.avisos.length && !p.psPendentes) return null;
                      return (
                        <div className="mt-4 space-y-2">
                          {p.foraDoPeriodo.length > 0 && (
                            <div className="rounded-lg border border-central-borda bg-central-elevado/40 px-4 py-3" data-fora-do-periodo>
                              <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Fora deste período, não entra na fatura</p>
                              <ul className="space-y-1">
                                {p.foraDoPeriodo.map((f, i) => (
                                  <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                                    <span className="text-slate-300">
                                      {f.name} <span className={`text-xs ${TONE_TEXT.warning}`}>· {motivoForaDoPeriodo(f)}</span>
                                    </span>
                                    <span className="font-mono text-slate-500 line-through whitespace-nowrap">R$ {fmt(f.totalValue)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {p.psPendentes > 0 && (
                            <p className="text-xs text-slate-400" data-ps-pendentes>
                              {p.psPendentes === 1 ? '1 item de P&S pendente' : `${p.psPendentes} itens de P&S pendentes`} na assinatura: use Puxar P&S para incluir.
                            </p>
                          )}
                          {p.avisos.map((a, i) => (
                            <p key={i} className={`text-xs ${TONE_TEXT.warning}`} data-aviso-da-geracao>{a}</p>
                          ))}
                        </div>
                      );
                    })()}

                    <div className="flex justify-end mt-3">
                      <div className="text-right">
                        <span className="text-slate-400 text-sm">Subtotal Itens: </span>
                        <span className="text-white font-semibold">R$ {fmt(subtotalItems)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Usage Items Section */}
                  <div className="border-t border-central-borda pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-slate-300 uppercase">
                        Consumo / Tarifas (CDR)
                      </h4>
                      <div className="flex gap-2">
                        <button
                          onClick={addAllUsageItems}
                          className="flex items-center gap-1 px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success-strong text-sm"
                        >
                          <Icon name="plus" className="w-4 h-4" />
                          Adicionar Todas
                        </button>
                        <button
                          onClick={addUsageItem}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                        >
                          <Icon name="plus" className="w-4 h-4" />
                          Adicionar Tarifa
                        </button>
                      </div>
                    </div>

                    {(form.usageItems || []).length === 0 ? (
                      <p className="text-slate-500 text-sm py-4 text-center">
                        Nenhum consumo adicionado
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {(form.usageItems || []).map((item: any, index: number) => (
                          <div
                            key={index}
                            className="bg-central-fundo rounded-lg p-4 border border-central-borda"
                          >
                            <div className="grid grid-cols-12 gap-3">
                              <div className="col-span-4">
                                <label className="block text-xs text-slate-500 mb-1">Tarifa</label>
                                <select
                                  value={item.tariff || TARIFF_TYPES[0]}
                                  onChange={(e) => updateUsageItem(index, 'tariff', e.target.value)}
                                  className={`w-full px-2 py-1.5 ${campo} rounded text-sm`}
                                >
                                  {TARIFF_TYPES.map((t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-span-3">
                                <label className="block text-xs text-slate-500 mb-1">Minutos</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.minutes || 0}
                                  onChange={(e) =>
                                    updateUsageItem(index, 'minutes', Number(e.target.value))
                                  }
                                  className={`w-full px-2 py-1.5 ${campo} rounded text-sm`}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-xs text-slate-500 mb-1">
                                  Valor/Min
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.unitValue || 0}
                                  onChange={(e) =>
                                    updateUsageItem(index, 'unitValue', Number(e.target.value))
                                  }
                                  className={`w-full px-2 py-1.5 ${campo} rounded text-sm`}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-xs text-slate-500 mb-1">Total</label>
                                <div className="px-2 py-1.5 border bg-central-fundo border-central-borda text-blue-400 cursor-not-allowed rounded text-sm font-medium">
                                  {fmt(item.totalValue)}
                                </div>
                              </div>
                              <div className="col-span-1 flex items-end">
                                <button
                                  onClick={() => removeUsageItem(index)}
                                  className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"
                                >
                                  <Icon name="trash" className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-end mt-3">
                      <div className="text-right">
                        <span className="text-slate-400 text-sm">Subtotal Consumo: </span>
                        <span className="text-blue-400 font-semibold">R$ {fmt(subtotalUsage)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
        </CascaDeModal>
        );
      })()}

      {/* Generate Batch Modal */}
      {modal === 'generate-batch' && (
        // Seleção de assinaturas/clientes: não fecha ao clicar fora (como já era).
        <CascaDeModal
          largura="base"
          titulo="Gerar Faturas em Lote"
          subtitulo={form.referenceMonth ? `Competência ${form.referenceMonth}` : undefined}
          onClose={closeModal}
          corpoClassName="p-6 space-y-4"
          rodape={!batchResult ? (
            <>
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.referenceMonth || (consolidatedMode ? selectedClients.length === 0 : selectedContracts.length === 0)}
                // Uma primária laranja (§8.1) nos dois modos; "e Emitir" está no rótulo.
                className="flex-1 px-4 py-2.5 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed bg-primary hover:bg-primary-strong"
              >
                {saving
                  ? (autoEmitMode ? 'Gerando e Emitindo...' : 'Gerando...')
                  : autoEmitMode
                    ? (consolidatedMode
                        ? `⚡ Gerar e Emitir ${selectedClients.length} Consolidada(s)`
                        : `⚡ Gerar e Emitir ${selectedContracts.length} Fatura(s)`)
                    : (consolidatedMode
                        ? `Gerar ${selectedClients.length} Fatura(s) Consolidada(s)`
                        : `Gerar ${selectedContracts.length} Fatura(s)`)
                }
              </button>
            </>
          ) : (
            <button
              onClick={closeModal}
              className="flex-1 px-4 py-2.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 font-medium"
            >
              Fechar
            </button>
          )}
        >
              {!batchResult ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Competencia (Mes/Ano) *
                    </label>
                    <input
                      type="month"
                      value={form.referenceMonth || ''}
                      onChange={(e) => setForm({ ...form, referenceMonth: e.target.value })}
                      onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                      className={`w-full px-4 py-2.5 ${campo} rounded-lg cursor-pointer`}
                    />
                  </div>

                  {/* Toggle: Individual vs Consolidada */}
                  <div className="bg-central-fundo rounded-lg p-4 border border-central-borda">
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Tipo de Fatura
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setConsolidatedMode(false); setSelectedClients([]); }}
                        className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                          !consolidatedMode
                            ? 'bg-slate-700 text-white ring-1 ring-orange-500/60'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        Individual por Assinatura
                      </button>
                      <button
                        onClick={() => { setConsolidatedMode(true); setSelectedContracts([]); }}
                        className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                          consolidatedMode
                            ? 'bg-slate-700 text-white ring-1 ring-orange-500/60'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        Consolidada por Cliente
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {consolidatedMode
                        ? '1 fatura por cliente com todos os itens das assinaturas'
                        : '1 fatura por assinatura (modo tradicional)'
                      }
                    </p>
                  </div>

                  {/* Toggle: Gerar vs Gerar e Emitir */}
                  <div className="bg-central-fundo rounded-lg p-4 border border-central-borda">
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Modo de Geração
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAutoEmitMode(false)}
                        className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                          !autoEmitMode
                            ? 'bg-slate-700 text-white ring-1 ring-orange-500/60'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        Apenas Gerar (Rascunho)
                      </button>
                      <button
                        onClick={() => setAutoEmitMode(true)}
                        className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                          autoEmitMode
                            ? 'bg-slate-700 text-white ring-1 ring-orange-500/60'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        Gerar e Emitir ⚡
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {autoEmitMode
                        ? '⚡ Automação completa: Cria fatura → Emite NF → Gera boleto (se aplicável)'
                        : 'Cria faturas em Rascunho para revisão manual'
                      }
                    </p>
                  </div>

                  {/* Seleção de Clientes (modo consolidado) */}
                  {consolidatedMode && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300">
                          Selecionar Clientes ({selectedClients.length} selecionados)
                        </label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const clientIds = Array.from(new Set(contracts.filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0).map(c => c.clientId)))
                                .filter(id => !billedMonthSets.invoicedClientIds.has(id));
                              setSelectedClients(clientIds);
                            }}
                            className="text-xs text-slate-400 hover:text-white"
                          >
                            Selecionar Todos
                          </button>
                          <span className="text-slate-600">|</span>
                          <button
                            onClick={() => setSelectedClients([])}
                            className="text-xs text-slate-400 hover:text-slate-300"
                          >
                            Limpar
                          </button>
                        </div>
                      </div>
                      <div className="relative mb-2">
                        <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="text"
                          value={batchSearchTerm}
                          onChange={(e) => setBatchSearchTerm(e.target.value)}
                          placeholder="Buscar cliente..."
                          className={`w-full pl-10 pr-4 py-2 ${campo} rounded-lg text-sm`}
                        />
                      </div>
                      <div className="bg-central-fundo rounded-lg border border-central-borda max-h-64 overflow-y-auto">
                        {(() => {
                          // Agrupar contratos por cliente
                          const clientGroups: { [key: number]: { client: any; contracts: any[]; totalMrr: number } } = {};
                          contracts.filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0).forEach(c => {
                            if (!clientGroups[c.clientId]) {
                              clientGroups[c.clientId] = { client: c.client, contracts: [], totalMrr: 0 };
                            }
                            clientGroups[c.clientId].contracts.push(c);
                            clientGroups[c.clientId].totalMrr += parseFloat(c.mrr) || 0;
                          });
                          const clientList = Object.entries(clientGroups)
                            .map(([clientId, data]) => ({ clientId: parseInt(clientId), ...data }))
                            .filter(g => !billedMonthSets.invoicedClientIds.has(g.clientId))
                            .filter(g => {
                              if (!batchSearchTerm) return true;
                              const searchLower = batchSearchTerm.toLowerCase();
                              const clientName = (g.client?.name || g.client?.companyName || '').toLowerCase();
                              return clientName.includes(searchLower);
                            })
                            .sort((a, b) => {
                              const nameA = a.client?.name?.trim() || a.client?.companyName || '';
                              const nameB = b.client?.name?.trim() || b.client?.companyName || '';
                              return nameA.localeCompare(nameB, 'pt-BR');
                            });

                          if (clientList.length === 0) {
                            return <p className="text-slate-500 text-sm p-4 text-center">Nenhum cliente pendente nesta competência</p>;
                          }

                          return clientList.map(({ clientId, client, contracts: clientContracts, totalMrr }) => {
                            const isSelected = selectedClients.includes(clientId);
                            return (
                              <label
                                key={clientId}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-800 border-b border-slate-800 last:border-0 ${isSelected ? 'bg-orange-500/5' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedClients([...selectedClients, clientId]);
                                    } else {
                                      setSelectedClients(selectedClients.filter(id => id !== clientId));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {client?.name?.trim() || client?.companyName || 'Cliente'}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {clientContracts.length} assinatura(s) - MRR Total: R$ {fmt(totalMrr)}
                                  </p>
                                </div>
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Seleção de Assinaturas (modo individual) */}
                  {!consolidatedMode && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-300">
                          Selecionar Assinaturas ({selectedContracts.length} selecionadas)
                        </label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedContracts(contracts.filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0 && !isContractBilled(c)).map(c => c.id))}
                            className="text-xs text-slate-400 hover:text-white"
                          >
                            Selecionar Todas
                          </button>
                          <span className="text-slate-600">|</span>
                          <button
                            onClick={() => setSelectedContracts([])}
                            className="text-xs text-slate-400 hover:text-slate-300"
                          >
                            Limpar
                          </button>
                        </div>
                      </div>
                      <div className="relative mb-2">
                        <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="text"
                          value={batchSearchTerm}
                          onChange={(e) => setBatchSearchTerm(e.target.value)}
                          placeholder="Buscar cliente..."
                          className={`w-full pl-10 pr-4 py-2 ${campo} rounded-lg text-sm`}
                        />
                      </div>
                      {form.referenceMonth && contracts.filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0 && isContractBilled(c)).length > 0 && (
                        <p className="text-[11px] text-slate-500 mb-1">{contracts.filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0 && isContractBilled(c)).length} assinatura(s) já faturada(s) nesta competência (ocultas)</p>
                      )}
                      <div className="bg-central-fundo rounded-lg border border-central-borda max-h-64 overflow-y-auto">
                        {contracts.filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0 && !isContractBilled(c)).length === 0 ? (
                          <p className="text-slate-500 text-sm p-4 text-center">Nenhuma assinatura ativa pendente nesta competência</p>
                        ) : (
                          contracts
                            .filter(c => c.status === 'Ativo' && parseFloat(c.mrr) > 0 && !isContractBilled(c))
                            .filter(c => {
                              if (!batchSearchTerm) return true;
                              const searchLower = batchSearchTerm.toLowerCase();
                              const clientName = (c.client?.name || c.client?.companyName || '').toLowerCase();
                              return clientName.includes(searchLower);
                            })
                            .sort((a, b) => {
                              const nameA = a.client?.name?.trim() || a.client?.companyName || '';
                              const nameB = b.client?.name?.trim() || b.client?.companyName || '';
                              return nameA.localeCompare(nameB, 'pt-BR');
                            })
                            .map((contract) => {
                              const isSelected = selectedContracts.includes(contract.id);
                              return (
                                <label
                                  key={contract.id}
                                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-800 border-b border-slate-800 last:border-0 ${isSelected ? 'bg-orange-500/5' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedContracts([...selectedContracts, contract.id]);
                                      } else {
                                        setSelectedContracts(selectedContracts.filter(id => id !== contract.id));
                                      }
                                    }}
                                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">
                                      {contract.client?.name?.trim() || contract.client?.companyName || 'Cliente'}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      Assinatura #{contract.id} - MRR: R$ {fmt(contract.mrr)}
                                    </p>
                                  </div>
                                </label>
                              );
                            })
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resumo da seleção */}
                  {consolidatedMode && selectedClients.length > 0 && (
                    <div className="bg-central-fundo rounded-lg p-4 border border-central-borda">
                      <div className="flex items-center gap-3">
                        <Icon name="check" className={`w-5 h-5 ${TONE_TEXT.open}`} />
                        <div>
                          <p className="text-sm text-slate-200">
                            <strong>{selectedClients.length}</strong> cliente(s) selecionado(s)
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {(() => {
                              const selectedContracts = contracts.filter(c => selectedClients.includes(c.clientId) && c.status === 'Ativo');
                              const totalMrr = selectedContracts.reduce((sum, c) => sum + (parseFloat(c.mrr) || 0), 0);
                              return `${selectedContracts.length} assinatura(s) - Total MRR: R$ ${fmt(totalMrr)}`;
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!consolidatedMode && selectedContracts.length > 0 && (
                    <div className="bg-central-fundo rounded-lg p-4 border border-central-borda">
                      <div className="flex items-center gap-3">
                        <Icon name="check" className={`w-5 h-5 ${TONE_TEXT.open}`} />
                        <div>
                          <p className="text-sm text-slate-200">
                            <strong>{selectedContracts.length}</strong> assinatura(s) selecionada(s)
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Total MRR: R$ {fmt(contracts.filter(c => selectedContracts.includes(c.id)).reduce((sum, c) => sum + (parseFloat(c.mrr) || 0), 0))}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className={`${TONE_SURFACE.success} rounded-lg p-4 border ${TONE_BORDER.success}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${TONE_BADGE.success}`}>
                        <Icon name="check" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className={`text-lg font-bold ${TONE_TEXT.success}`}>{batchResult.success}</p>
                        <p className={`text-sm ${TONE_TEXT.success}`}>Faturas geradas</p>
                      </div>
                    </div>
                  </div>

                  {batchResult.skipped > 0 && (
                    <div className={`rounded-lg p-4 border ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`}>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-central-elevado rounded-full">
                          <Icon name="alertCircle" className={`w-5 h-5 ${TONE_TEXT.warning}`} />
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${TONE_TEXT.warning}`}>{batchResult.skipped}</p>
                          <p className="text-sm text-slate-300">Ignoradas (já existem ou MRR zero)</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {batchResult.errors.length > 0 && (
                    <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-red-500/20 rounded-full">
                          <Icon name="x" className="w-5 h-5 text-red-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-red-400">{batchResult.errors.length} Erros</p>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {batchResult.errors.map((err, i) => (
                              <p key={i} className="text-xs text-red-300 mt-1">{err}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
        </CascaDeModal>
      )}

      {/* Comprovante de envio: uma instância, no nível da tabela. */}
      {emailPopoverInvoiceId != null && renderComprovanteDeEnvio(emailPopoverInvoiceId)}

      {/* View Modal: a FICHA DA FATURA. Prévia: https://claude.ai/artifact/3MMLW4wr53hQ5KSEaZTuiB
          ⛔ LAYOUT: a caixa é max-h-[90vh] flex flex-col overflow-hidden e o corpo é
          flex-1 min-h-0 overflow-y-auto; cabeçalho, faixa e rodapés são shrink-0.
          Até 03/10/2026 eram duas alturas chumbadas (caixa 90vh, corpo 70vh): sobravam
          20vh para cabeçalho + empenho + botões e, a 90% num notebook, o overflow-hidden
          CORTAVA os botões. O corpo agora recebe o que sobrar; nenhuma altura é adivinhada. */}
      {modal === 'view' && selectedInvoice && (() => {
        const inv = selectedInvoice;
        const cliente = inv.client || clients.find((c: any) => c.id === inv.clientId);
        const publico = !!cliente?.isPublicSector;
        const boleto = situacaoDoBoleto(inv);
        const pago = Number(inv.paidValue) || 0;
        const total = Number(inv.totalValue) || 0;
        const allItems = (inv.items || []) as any[];
        const regularItems = allItems.filter((item: any) => Number(item.totalValue ?? item.total ?? 0) >= 0);
        const discountItems = allItems.filter((item: any) => Number(item.totalValue ?? item.total ?? 0) < 0);
        const chave = (i: string) => `${inv.id}-${i}`;
        const alternar = (k: string) => setDescricoesAbertas(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
        const nomeCliente = nomeDeExibicao(cliente, '—');
        const corDoBoleto = boleto.tom === 'ok' ? TONE_TEXT.success : boleto.tom === 'aviso' ? 'text-amber-300' : boleto.tom === 'erro' ? 'text-red-300' : 'text-white';
        const secao = (titulo: string, direita?: React.ReactNode) => (
          <div className="flex items-center gap-2 pt-3 pb-1 mt-2 border-t border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
            <span>{titulo}</span>
            {direita && <span className="ml-auto normal-case tracking-normal text-[11px]">{direita}</span>}
          </div>
        );
        const numeros: NumeroDaFaixa[] = [
          { k: 'Total', v: fmt(total), cls: 'text-white' },
          { k: 'Vencimento', v: fmtDate(inv.dueDate), dica: inv.issueDate ? `Emitida em ${fmtDate(inv.issueDate)}` : undefined },
          { k: 'Serviço', v: periodoDeServico(inv.periodStart, inv.periodEnd, fmtShortDate), dica: `${fmtDate(inv.periodStart)} a ${fmtDate(inv.periodEnd)}` },
          { k: 'Itens', v: allItems.length },
          { k: 'Pago', v: fmt(pago), cls: pago >= total - 0.005 && total > 0 ? TONE_TEXT.success : pago > 0 ? 'text-amber-300' : 'text-slate-300', dica: inv.paymentDate ? `Pago em ${fmtDate(inv.paymentDate)}` : undefined },
          { k: 'Boleto', v: boleto.texto, cls: corDoBoleto, dica: boleto.dica },
        ];
        return (
        <CascaDeModal
          onClose={closeModal}
          titulo={<ClientPopover clientId={inv.clientId}><span>{nomeCliente}</span></ClientPopover>}
          subtitulo={[inv.code, getCompetencia(inv), cliente?.cnpj ? `CNPJ ${fmtCNPJ(cliente.cnpj)}` : null].filter(Boolean).join(' · ')}
          selos={
            <>
              {tipoDaFatura(inv) === 'consolidada' && (
                <span className={`shrink-0 px-2 py-0.5 ${TONE_BADGE.info} text-xs font-medium rounded-full`}>Consolidada</span>
              )}
              {motivoRetencao(inv) && (
                <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.warning} cursor-help`} title={motivoRetencao(inv) || ''}>🔒 Retida</span>
              )}
              <SeloDoAceite inv={inv} />
              <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(isOverdue(inv) ? 'Vencida' : inv.status)}`}>
                {isOverdue(inv) ? 'Vencida' : inv.status}
              </span>
            </>
          }
          faixa={numeros}
          // Rodapé (§8.1): UMA ação laranja, e só se ela grava ou gera. Download
          // neutro; o raro (regerar NF) e o que não é a primária vão para o ⋯.
          rodape={(() => {
            const retidaEmRascunho = !!motivoRetencao(inv) && inv.status === 'Rascunho';
            const nfParcialPrimaria = !retidaEmRascunho && publico;
            const botaoPrimario = 'flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm font-medium disabled:opacity-50';
            const botaoNeutro = 'flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium';
            const menu = [
              {
                label: regeneratingFiscal ? 'Regerando…' : 'Regerar nota fiscal',
                onClick: () => handleRegenerateFiscal(inv),
                disabled: regeneratingFiscal,
                title: 'Cria um novo rascunho de NF (após cancelar a atual em Documentos). Transmita em Documentos para emitir com a data de hoje.',
              },
              ...(!nfParcialPrimaria
                ? [{
                    label: abrindoPartialNf ? 'Consultando NFS-e…' : 'NF parcial',
                    onClick: () => openPartialNf(inv),
                    disabled: abrindoPartialNf,
                    title: 'Cria rascunho de NFS-e só com os itens selecionados (ex.: uma NFS-e por nota de empenho). Não transmite automaticamente.',
                  }]
                : []),
            ];
            return (
              <>
                <button onClick={closeModal} className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 text-sm font-medium">
                  Fechar
                </button>
                <button onClick={() => { setModal(null); setTimeout(() => openDownloadModal(inv), 100); }} className={botaoNeutro}>
                  <Icon name="download" className="w-4 h-4" />
                  Baixar PDF
                </button>
                {retidaEmRascunho && (
                  <button onClick={() => handleLiberarEAprovar(inv)} title={`Retida: ${motivoRetencao(inv)}`} className={botaoPrimario}>
                    🔒 Liberar e aprovar
                  </button>
                )}
                {nfParcialPrimaria && (
                  <button
                    onClick={() => openPartialNf(inv)}
                    disabled={abrindoPartialNf}
                    title="Cria rascunho de NFS-e só com os itens selecionados (ex.: uma NFS-e por nota de empenho). Não transmite automaticamente."
                    className={botaoPrimario}
                  >
                    <Icon name="fileText" className="w-4 h-4" />
                    {abrindoPartialNf ? 'Consultando NFS-e…' : 'NF parcial'}
                  </button>
                )}
                <MaisAcoes itens={menu} abrePara="cima" />
                <span className={`ml-auto font-mono text-base font-semibold ${TONE_TEXT.open}`} title="Total geral da fatura">R$ {fmt(total)}</span>
              </>
            );
          })()}
        >
              <LinhaDoAceite inv={inv} />
              {/* Itens: mesma linha da ficha da assinatura (LinhaDeItem). */}
              {allItems.length > 0 && (
                <div>
                  {regularItems.map((item: any, i: number) => {
                    const unitVal = Number(item.unitValue ?? item.unitPrice ?? 0);
                    const gross = unitVal * Number(item.quantity || 1);
                    const totalVal = Number(item.totalValue ?? item.total ?? 0);
                    const discountValue = gross - totalVal;
                    const discountPct = gross > 0 && discountValue > 0.01 ? (discountValue / gross * 100) : 0;
                    const temDescricao = item.name && item.description && item.description !== item.name;
                    const k = chave(`r${i}`);
                    return (
                      <LinhaDeItem
                        key={k}
                        nome={item.name || item.description || '-'}
                        descricao={temDescricao ? item.description : null}
                        aberta={descricoesAbertas.has(k)}
                        onAlternar={() => alternar(k)}
                        selos={item.locationName ? <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-300">{item.locationName}</span> : null}
                        quantidade={item.quantity || 1}
                        unitario={<>{fmt(unitVal)}{discountPct > 0 ? <span className="text-amber-300"> −{discountPct % 1 === 0 ? discountPct.toFixed(0) : discountPct.toFixed(1)}%</span> : null}</>}
                        total={fmt(totalVal)}
                        tom="verde"
                      />
                    );
                  })}
                  {discountItems.map((item: any, i: number) => {
                    const totalVal = Number(item.totalValue ?? item.total ?? 0);
                    const temDescricao = item.name && item.description && item.description !== item.name;
                    const k = chave(`d${i}`);
                    return (
                      <LinhaDeItem
                        key={k}
                        nome={item.name || item.description || '-'}
                        descricao={temDescricao ? item.description : null}
                        aberta={descricoesAbertas.has(k)}
                        onAlternar={() => alternar(k)}
                        quantidade={item.quantity || 1}
                        unitario={fmt(totalVal)}
                        total={fmt(totalVal)}
                        tom="negativo"
                      />
                    );
                  })}
                  <div className="flex items-center justify-end gap-3 pt-2 text-xs text-slate-400">
                    Subtotal dos itens <span className="w-24 text-right font-mono text-sm text-white">{fmt(inv.subtotalItems)}</span>
                  </div>
                </div>
              )}

              {inv.usageItems && inv.usageItems.length > 0 && (
                <>
                  {secao('Consumo / tarifas')}
                  {inv.usageItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 py-1.5 border-b border-central-borda last:border-b-0 text-sm">
                      <span className="w-4 shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-slate-200">{item.tariff}</span>
                      <span className="w-12 text-right font-mono text-xs text-slate-400" title="Minutos">{item.minutes.toFixed(2)}</span>
                      <span className="w-24 text-right font-mono text-xs text-slate-400" title="Valor por minuto">{fmt(item.unitValue)}/min</span>
                      <span className="w-24 text-right font-mono text-sm font-medium text-blue-400">{fmt(item.totalValue)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-end gap-3 pt-2 text-xs text-slate-400">
                    Subtotal do consumo <span className="w-24 text-right font-mono text-sm text-blue-400">{fmt(inv.subtotalUsage)}</span>
                  </div>
                </>
              )}

              {/* Notas fiscais: seção da mesma lista, com a régua dos itens. */}
              {(loadingFiscalDocs || (fiscalDocsStatus && fiscalDocsStatus.fiscalDocuments.length > 0)) && (
                <>
                  {secao('Notas fiscais', fiscalDocsStatus ? (
                    <span className="text-slate-500">
                      Pago: R$ {fmt(fiscalDocsStatus.totalPaid)} de R$ {fmt(fiscalDocsStatus.totalValue)}
                      {fiscalDocsStatus.paidGeneric > 0 && <> · sem rótulo de NF: R$ {fmt(fiscalDocsStatus.paidGeneric)}</>}
                    </span>
                  ) : null)}
                  {loadingFiscalDocs ? (
                    <div className="py-2 text-slate-500 text-sm">Carregando notas fiscais…</div>
                  ) : fiscalDocsStatus!.fiscalDocuments.map(d => {
                    // Status = status FISCAL do documento; o pagamento fica na coluna ao lado.
                    const fiscal = d.fiscalStatus || 'Autorizada';
                    const isCancelled = fiscal === 'Cancelada';
                    const statusClass =
                      fiscal === 'Autorizada' ? TONE_BADGE.success
                      : fiscal === 'Processando' ? TONE_BADGE.progress
                      : fiscal === 'Rascunho' ? TONE_BADGE.warning
                      : isCancelled || fiscal === 'Rejeitada' || fiscal === 'Erro' ? TONE_BADGE.danger
                      : 'bg-slate-700 text-slate-400';
                    return (
                      <div key={d.id} className={`flex items-center gap-3 py-1.5 border-b border-central-borda last:border-b-0 text-sm ${isCancelled ? 'opacity-60' : ''}`}>
                        <span className="w-4 shrink-0" />
                        <span className={`flex-1 min-w-0 truncate text-slate-200 ${isCancelled ? 'line-through' : ''}`}>
                          <span className="uppercase text-slate-400">{d.type}</span> · <span className="font-mono">{d.documentNumber || '—'}</span>
                        </span>
                        <span className={`w-28 text-right font-mono text-xs ${d.status === 'paid' ? TONE_TEXT.open : d.status === 'partial' ? TONE_TEXT.warning : 'text-slate-500'}`} title="Pago nesta nota">
                          {d.paidAmount > 0 ? `pago ${fmt(d.paidAmount)}${d.status === 'partial' ? ' (parcial)' : ''}` : '—'}
                        </span>
                        <span className={`w-24 text-right font-mono text-sm text-slate-300 ${isCancelled ? 'line-through' : ''}`}>{fmt(d.totalValue)}</span>
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>{fiscal}</span>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Empenho: campo e botão só em ÓRGÃO PÚBLICO (ou se já houver número).
                  Em cliente privado vira uma linha de texto. O número chega por
                  WhatsApp ou e-mail dias antes da nota sair, e enquanto ele não está
                  aqui a pendência fica classificada como espera do órgão. */}
              {secao('Empenho')}
              {mostraCampoDeEmpenho(publico, inv.empenhoNumber) ? (
                <div className="flex flex-wrap items-end gap-2 py-1.5">
                  <div className="w-48">
                    <label className="block text-xs text-slate-500 mb-1">Nº do Empenho desta fatura</label>
                    <input
                      type="text"
                      value={empenhoRegistro}
                      onChange={e => setEmpenhoRegistro(e.target.value.slice(0, 60))}
                      placeholder={inv.empenhoNumber || 'ex.: 308/2026'}
                      className={`w-full px-3 py-1.5 ${campo} text-sm rounded-lg focus:outline-none`}
                    />
                  </div>
                  <button
                    onClick={salvarEmpenho}
                    disabled={salvandoEmpenho || empenhoRegistro.trim() === (inv.empenhoNumber || '')}
                    className="px-3 py-1.5 bg-slate-700 text-slate-200 border border-slate-600 rounded-lg text-sm hover:bg-slate-600 disabled:opacity-40"
                  >
                    {salvandoEmpenho ? 'Salvando…' : 'Registrar'}
                  </button>
                  <p className="text-xs text-slate-500 pb-1.5 flex-1 min-w-[12rem]">
                    {inv.empenhoNumber
                      ? `Registrado: ${inv.empenhoNumber}. A nota ainda precisa ser emitida em NF parcial.`
                      : 'Registre assim que o órgão informar. Sem isso, a fatura conta como espera do órgão e não entra na cobrança diária.'}
                  </p>
                </div>
              ) : (
                <div className="py-1.5 pl-7 text-sm text-slate-500">Não se aplica · cliente privado</div>
              )}

              {inv.notes && (
                <>
                  {secao('Observações')}
                  <p className="pl-7 py-1 text-slate-300 text-sm whitespace-pre-line">{inv.notes}</p>
                </>
              )}

              {/* Contatos (G1, 30/10/2026): o que o cliente disse e prometeu sobre esta fatura. O mesmo painel da gaveta da
                  aba Cobrança; registra quem é admin ou editor, como no backend. */}
              {secao('Contatos')}
              <div className="pl-7 py-1">
                <ContatosDaFatura invoiceId={inv.id} clientId={inv.clientId} podeRegistrar={papelNaCobranca().registra} />
              </div>
        </CascaDeModal>
        );
      })()}

      {/* Modal NF PARCIAL — seleção de itens p/ NFS-e por empenho */}
      {partialNfModal && selectedInvoice && (
        // Seleção de itens e empenho digitado: não fecha ao clicar fora (como já era).
        <CascaDeModal
          largura="md"
          zIndex="z-[60]"
          titulo="NFS-e parcial"
          subtitulo={selectedInvoice.code}
          onClose={() => setPartialNfModal(false)}
          corpoClassName="p-4 space-y-3"
          topo={
            // Fora da rolagem, como era: o empenho vale para os itens marcados lá embaixo.
            <div className="px-4 pt-3 pb-1 space-y-2">
              <p className="text-sm text-slate-400">
                Marque os itens que entram nesta NFS-e. Preencha o empenho abaixo — ele é acrescentado ao nome dos itens (é assim que sai na nota).
              </p>
            <div className="flex items-end gap-2">
              <div className="flex-1 max-w-xs">
                <label className="block text-xs font-medium text-slate-400 mb-1">Nº do Empenho</label>
                <input
                  type="text"
                  value={partialNfEmpenho}
                  onChange={e => setPartialNfEmpenho(e.target.value)}
                  onBlur={() => applyEmpenhoToItems(partialNfEmpenho)}
                  placeholder="ex.: 238/2026"
                  className={`w-full px-3 py-2 ${campo} text-sm rounded-lg focus:outline-none`}
                />
              </div>
              <button
                onClick={() => applyEmpenhoToItems(partialNfEmpenho)}
                disabled={!partialNfEmpenho.trim()}
                className="px-3 py-2 bg-slate-700 text-slate-300 border border-slate-600 rounded-lg text-sm hover:bg-slate-600 disabled:opacity-40"
              >
                Aplicar aos itens selecionados
              </button>
            </div>
            </div>
          }
          rodape={
            <>
              {(() => {
                const total = partialNfItems.filter(i => i._selected).reduce((s, i) => s + Number(i.totalValue || 0), 0);
                const passa = !!partialNfCobertura && total > partialNfCobertura.resta + 0.01;
                return (
                  <div className="text-sm text-slate-300 space-y-0.5">
                    <div>
                      Total da NFS-e:{' '}
                      <span className={`font-semibold ${passa ? 'text-red-400' : 'text-white'}`}>R$ {fmt(total)}</span>
                    </div>
                    {partialNfCobertura ? (
                      <div className="text-xs text-slate-400">
                        Já notado: R$ {fmt(partialNfCobertura.jaNotado)} · Resta notar:{' '}
                        <span className={passa ? 'text-red-400' : 'text-slate-200'}>R$ {fmt(partialNfCobertura.resta)}</span>
                        {passa && ' — passa do que resta'}
                      </div>
                    ) : (
                      <div className="text-xs text-amber-400">Não consegui consultar as NFS-e já emitidas desta fatura.</div>
                    )}
                  </div>
                );
              })()}
              <div className="ml-auto flex gap-3">
                <button
                  onClick={() => setPartialNfModal(false)}
                  className="px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreatePartialNf}
                  disabled={
                    creatingPartialNf ||
                    partialNfItems.every(i => !i._selected) ||
                    (!!partialNfCobertura && partialNfItems.filter(i => i._selected).reduce((s, i) => s + Number(i.totalValue || 0), 0) > partialNfCobertura.resta + 0.01)
                  }
                  className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50"
                >
                  {creatingPartialNf ? 'Criando…' : 'Criar rascunho NFS-e'}
                </button>
              </div>
            </>
          }
        >
              {(() => {
                const cartao = (it: any, idx: number) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    it._selected ? 'border-orange-500/50 bg-orange-500/5' : 'border-central-borda bg-central-fundo/50 opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={it._selected}
                    disabled={!!it._jaNotado}
                    title={it._jaNotado ? 'Este item já está numa NFS-e ativa' : undefined}
                    onChange={e =>
                      setPartialNfItems(arr =>
                        arr.map((x, i) => (i === idx ? { ...x, _selected: e.target.checked } : x)),
                      )
                    }
                    className="mt-2 w-4 h-4 accent-orange-500"
                  />
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={it._editName}
                      disabled={!it._selected}
                      onChange={e =>
                        setPartialNfItems(arr =>
                          arr.map((x, i) => (i === idx ? { ...x, _editName: e.target.value } : x)),
                        )
                      }
                      className={`w-full rounded-lg px-3 py-2 text-sm disabled:opacity-50 ${campo}`}
                    />
                    <div className="text-xs text-slate-400 mt-1">
                      Qtd {it.quantity} × R$ {fmt(it.unitValue)} = <span className="text-slate-200">R$ {fmt(it.totalValue)}</span>
                      {it.period ? <> · {it.period}</> : null}
                    </div>
                    {it._jaNotado && (
                      <div className={`mt-1.5 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded ${TONE_BADGE.success} border ${TONE_BORDER.success}`}>
                        <Icon name="check" className="w-3.5 h-3.5" />
                        Já notado na NFS-e {it._jaNotado.documentNumber ? `nº ${it._jaNotado.documentNumber}` : `#${it._jaNotado.id}`}
                        {it._jaNotado.status !== 'Autorizada' ? ` (${it._jaNotado.status})` : ''}
                      </div>
                    )}
                  </div>
                </div>
                );
                // Um empenho só (ou fatura anterior ao #273, sem o campo): a lista
                // de sempre. Dois ou mais: uma seção por empenho, e "Emitir este
                // grupo" marca os itens livres dele e põe o empenho certo.
                if (partialNfGrupos.length < 2) return partialNfItems.map((it, idx) => cartao(it, idx));
                return partialNfGrupos.map(g => {
                  const ativo = partialNfGrupoAtivo === g.chave;
                  return (
                    <div key={g.chave || '__sem'} className={`rounded-xl border p-3 space-y-2 ${ativo ? 'border-orange-500/60 bg-orange-500/5' : 'border-central-borda'}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className={`text-sm font-semibold ${rotuloDoGrupoDeEmpenho(g).aviso ? TONE_TEXT.warning : 'text-white'}`}>
                            {rotuloDoGrupoDeEmpenho(g).titulo}
                          </div>
                          <div className="text-xs text-slate-400">
                            {g.indices.length} {g.indices.length === 1 ? 'item' : 'itens'}
                            {rotuloDoGrupoDeEmpenho(g).detalhe && ` · ${rotuloDoGrupoDeEmpenho(g).detalhe}`}
                            {!g.concluido && ` · a notar: R$ ${fmt(g.totalLivre)}`}
                          </div>
                        </div>
                        {g.concluido ? (
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ${TONE_BADGE.success} border ${TONE_BORDER.success}`}>
                            <Icon name="check" className="w-3.5 h-3.5" /> Concluído
                          </span>
                        ) : ativo ? (
                          <span className={`text-xs px-2 py-1 rounded ${TONE_BADGE.info} border ${TONE_BORDER.info}`}>Nesta nota</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => escolherGrupoDeEmpenho(g)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600"
                          >
                            Emitir este grupo
                          </button>
                        )}
                      </div>
                      {g.indices.map(idx => cartao(partialNfItems[idx], idx))}
                    </div>
                  );
                });
              })()}
        </CascaDeModal>
      )}

      {/* Modal P&S Pendentes */}
      {showPSModal && (
        // Só leitura e clique: fecha ao clicar fora (antes não fechava).
        <CascaDeModal
          largura="sm"
          fecharAoClicarFora
          titulo="Itens P&S Pendentes"
          subtitulo="Selecione para adicionar à fatura"
          onClose={() => setShowPSModal(false)}
          corpoClassName="p-4"
          rodape={
            <button
              onClick={() => setShowPSModal(false)}
              className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              Fechar
            </button>
          }
        >
              {pendingPSItems.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="check" className={`w-12 h-12 ${TONE_TEXT.success} mx-auto mb-3`} />
                  <p className="text-slate-400">Nenhum item P&S pendente</p>
                  <p className="text-xs text-slate-500 mt-1">Todos os itens de ativação já foram faturados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingPSItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-central-fundo rounded-lg p-4 border border-central-borda hover:border-orange-500/50 cursor-pointer transition-all"
                      onClick={() => addPSItemToInvoice(item)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{item.name}</h4>
                          {item.description && (
                            <p className="text-xs text-slate-400 mt-1">{item.description}</p>
                          )}
                          {item.installments > 1 && (
                            <p className={`text-xs mt-1 ${TONE_TEXT.ps}`}>
                              Parcela {item.currentInstallment} de {item.installments}
                              <span className="text-slate-500 ml-2">
                                (Restam {item.remainingInstallments} parcelas)
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${TONE_TEXT.ps}`}>R$ {fmt(item.installmentValue)}</p>
                          {item.installments > 1 && (
                            <p className="text-xs text-slate-500">
                              Total: R$ {fmt(item.totalValue)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button className="px-3 py-1.5 bg-slate-700 text-slate-200 text-xs rounded-lg hover:bg-slate-600">
                          + Adicionar à Fatura
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </CascaDeModal>
      )}

      {/* Modal Download PDF com Anexo */}
      {modal === 'download' && selectedInvoice && (
        // Guarda o anexo escolhido: não fecha ao clicar fora (como já era).
        <CascaDeModal
          largura="xs"
          titulo="Baixar PDF da Fatura"
          subtitulo={selectedInvoice.code}
          onClose={closeModal}
          corpoClassName="p-4 space-y-4"
          rodape={
            <>
              <button
                onClick={() => handleDownloadPdf(selectedInvoice, downloadAttachment)}
                disabled={downloadingPdf}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingPdf ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Gerando PDF...
                  </>
                ) : (
                  <>
                    <Icon name="download" className="w-5 h-5" />
                    {downloadAttachment ? 'Baixar PDF com Anexo' : 'Baixar PDF'}
                  </>
                )}
              </button>
              {selectedInvoice?.paymentMethod === 'boleto' && selectedInvoice?.nossoNumero && (
                <button
                  onClick={() => { handleDownloadClientView(selectedInvoice); closeModal(); }}
                  disabled={downloadingPdf}
                  title="Baixa o mesmo PDF que o cliente recebe no e-mail: fatura + página do boleto com linha digitável"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="eye" className="w-5 h-5" />
                  Visão do cliente (c/ boleto)
                </button>
              )}
              <button
                onClick={closeModal}
                disabled={downloadingPdf}
                className="px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium disabled:opacity-50"
              >
                Cancelar
              </button>
            </>
          }
        >
              <div
                className="bg-central-fundo rounded-lg p-4"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer?.files?.length) handleAttachmentFiles(e.dataTransfer.files);
                }}
              >
                <h4 className="text-sm font-semibold text-slate-300 mb-2">Anexar arquivo ao PDF (opcional)</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Voce pode anexar um arquivo CSV ou PDF (ex: CDR detalhado) que sera incluido ao final da fatura.
                </p>
                <input
                  type="file"
                  accept=".csv,.pdf"
                  onChange={handleAttachmentChange}
                  className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600"
                />
                {downloadAttachment && (
                  <div className={`mt-2 flex items-center gap-2 text-sm ${TONE_TEXT.success}`}>
                    <Icon name="check" className="w-4 h-4" />
                    <span>{downloadAttachment.filename}</span>
                    <button
                      onClick={() => setDownloadAttachment(null)}
                      className="ml-auto text-red-400 hover:text-red-300"
                    >
                      <Icon name="x" className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

            {/* Link público para visualização */}
            <div>
              {!pdfLink ? (
                <button
                  onClick={() => handleGetPdfLink(selectedInvoice)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium"
                >
                  <Icon name="link" className="w-4 h-4" />
                  Gerar link público para visualização
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={pdfLink}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs ${campo}`}
                  />
                  <button
                    onClick={handleCopyPdfLink}
                    className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 ${linkCopied ? `${TONE_BADGE.open}` : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                  >
                    <Icon name={linkCopied ? 'check' : 'copy'} className="w-4 h-4" />
                    {linkCopied ? 'Copiado!' : 'Copiar'}
                  </button>
                  <button
                    onClick={() => window.open(pdfLink, '_blank')}
                    className="px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center gap-1"
                  >
                    <Icon name="eye" className="w-4 h-4" />
                    Abrir
                  </button>
                </div>
              )}
            </div>
        </CascaDeModal>
      )}

      {/* Modal Processar CDR */}
      {modal === 'cdr' && (
        // Arquivo carregado e faturas escolhidas: não fecha ao clicar fora (como já era).
        <CascaDeModal
          largura="lg"
          titulo="Processar CDR"
          subtitulo="Processe e exporte relatórios de CDR por cliente"
          onClose={closeModal}
          corpoClassName="p-4"
          rodape={
            <div className="w-full flex flex-col gap-3">
              {cdrResult?.clients && cdrResult.clients.length > 0 && (
                <div className="flex gap-3">
                  {/* Uma primária (§8.1): "nas Faturas". "nas Assinaturas" fica neutro. */}
                  <button
                    onClick={applyAllCdrToContracts}
                    disabled={applyingAllContracts || cdrResult.clients.every((c: any) => cdrAppliedContracts.includes(`${c.clientTag}_${c.direction}`))}
                    className="flex-1 px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {applyingAllContracts ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <Icon name="check" className="w-4 h-4" />
                        Aplicar Todos nas Assinaturas
                      </>
                    )}
                  </button>
                  <button
                    onClick={applyAllCdrToInvoices}
                    disabled={applyingAll || cdrResult.clients.every((c: any) => cdrAppliedInvoices.includes(`${c.clientTag}_${c.direction}`))}
                    className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {applyingAll ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <Icon name="check" className="w-4 h-4" />
                        Aplicar Todos nas Faturas
                      </>
                    )}
                  </button>
                </div>
              )}
              <button
                onClick={closeModal}
                className="w-full px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
              >
                Fechar
              </button>
            </div>
          }
        >
              {/* Upload de arquivo */}
              <div
                className="bg-central-fundo rounded-lg p-4 mb-4"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer?.files?.length) handleCdrFiles(e.dataTransfer.files);
                }}
              >
                <h4 className="text-sm font-semibold text-slate-300 mb-2">1. Selecione o arquivo CDR</h4>
                <div className="flex gap-3">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCdrFileUpload}
                    className="flex-1 text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600"
                  />
                  <button
                    onClick={processCdr}
                    disabled={!cdrContent || cdrProcessing}
                    // Primária só antes do resultado; depois a laranja é "aplicar" (§8.1).
                    className={`px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${cdrResult ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-primary text-white hover:bg-primary-strong'}`}
                  >
                    {cdrProcessing ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando...
                      </>
                    ) : (
                      <>
                        <Icon name="refresh" className="w-4 h-4" />
                        Processar
                      </>
                    )}
                  </button>
                </div>
                {cdrContent && !cdrResult && (
                  <p className={`text-sm ${TONE_TEXT.success} mt-2`}>Arquivo carregado. Clique em Processar para analisar.</p>
                )}
              </div>

              {/* Resultados */}
              {cdrResult && (
                <div className="space-y-4">
                  {/* Resumo */}
                  <div className="bg-central-fundo rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3">2. Resumo do processamento</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-central-elevado rounded-lg p-3">
                        <p className="text-xs text-slate-500">Total de Registros</p>
                        <p className="text-xl font-bold text-white">{cdrResult.summary?.totalRecords || 0}</p>
                      </div>
                      <div className="bg-central-elevado rounded-lg p-3">
                        <p className="text-xs text-slate-500">Registros Processados</p>
                        <p className={`text-xl font-bold ${TONE_TEXT.progress}`}>{cdrResult.summary?.processedRecords || 0}</p>
                      </div>
                      <div className="bg-central-elevado rounded-lg p-3">
                        <p className="text-xs text-slate-500">Clientes Encontrados</p>
                        <p className="text-xl font-bold text-orange-400">{cdrResult.summary?.clientsFound?.length || 0}</p>
                      </div>
                    </div>

                    {/* Ligações descartadas: acima de 1% abrem sozinhas (DescarteDoCdr). */}
                    <DescarteDoCdr key={`${cdrResult.summary?.totalRecords}-${cdrResult.summary?.processedRecords}`} resumo={cdrResult.summary} />
                  </div>

                  {/* Lista de clientes */}
                  <div className="bg-central-fundo rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-1">3. Dados por cliente</h4>
                    <p className="text-xs text-slate-500 mb-3" data-testid="cdr-dica-fatura">
                      Qualquer fatura pode receber CDR, inclusive Aberta; as que já têm nota autorizada vêm marcadas com ⚠.
                    </p>
                    <div className="space-y-3">
                      {(cdrResult.clients || []).map((client: any, idx: number) => (
                        <div key={idx} className="bg-central-elevado rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                client.direction === 'entrada' ? TONE_BADGE.progress :
                                client.direction === 'entrada_0800' ? TONE_BADGE.info :
                                TONE_BADGE.open
                              }`}>
                                {client.direction === 'entrada' ? 'ENTRADA (NORMAL)' :
                                 client.direction === 'entrada_0800' ? 'ENTRADA (0800)' : 'SAÍDA'}
                              </span>
                              <span className="text-white font-semibold">{client.clientTag}</span>
                            </div>
                            <button
                              onClick={() => downloadCdrCsv(client.clientTag, client.direction)}
                              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium"
                            >
                              <Icon name="download" className="w-4 h-4" />
                              Baixar CSV
                            </button>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500 text-xs">Total Ligações</p>
                              <p className="text-white font-semibold">{client.totalCalls}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Atendidas</p>
                              <p className="text-white font-semibold">{client.answeredCalls || 0}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Min. Reais</p>
                              <p className="text-slate-400 font-semibold">{client.totalMinutes?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Min. Tarifados (0/30/6)</p>
                              <p className={`font-bold ${TONE_TEXT.progress}`}>{client.totalMinutesBilled?.toFixed(2) || '0.00'}</p>
                            </div>
                          </div>

                          {/* Detalhamento por tipo - Minutos Tarifados */}
                          {client.minutesBilledByType && (
                            <div className="mt-3 pt-3 border-t border-central-borda">
                              <p className="text-xs text-slate-500 mb-2">Minutos Tarifados por Tipo (0/30/6):</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                {client.minutesBilledByType.celularLocal > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Celular Local:</span>
                                    <span className={`ml-1 font-semibold ${TONE_TEXT.progress}`}>{client.minutesBilledByType.celularLocal.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.celularLDN > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Celular LDN:</span>
                                    <span className={`ml-1 font-semibold ${TONE_TEXT.progress}`}>{client.minutesBilledByType.celularLDN.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.fixoLocal > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Fixo Local:</span>
                                    <span className={`ml-1 font-semibold ${TONE_TEXT.progress}`}>{client.minutesBilledByType.fixoLocal.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.fixoLDN > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Fixo LDN:</span>
                                    <span className={`ml-1 font-semibold ${TONE_TEXT.progress}`}>{client.minutesBilledByType.fixoLDN.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.ldi > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">LDI:</span>
                                    <span className={`ml-1 font-semibold ${TONE_TEXT.progress}`}>{client.minutesBilledByType.ldi.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.especial > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Especial:</span>
                                    <span className={`ml-1 font-semibold ${TONE_TEXT.info}`}>{client.minutesBilledByType.especial.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.emergencial > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Emergencial:</span>
                                    <span className="text-red-400 ml-1 font-semibold">{client.minutesBilledByType.emergencial.toFixed(2)}</span>
                                  </div>
                                )}
                                {client.minutesBilledByType.entrada > 0 && (
                                  <div className="bg-central-fundo rounded px-2 py-1">
                                    <span className="text-slate-400">Entrada:</span>
                                    <span className="text-blue-400 ml-1 font-semibold">{client.minutesBilledByType.entrada.toFixed(2)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Aplicar na Assinatura */}
                          <div className="mt-3 pt-3 border-t border-central-borda">
                            <p className="text-xs text-slate-500 mb-2">Aplicar minutos na assinatura:</p>
                            {client.direction === 'entrada' && (
                              <p className="text-xs text-blue-400 mb-2">
                                💡 Entrada Normal - aparece apenas no relatório (R$ 0,00)
                              </p>
                            )}
                            {client.direction === 'entrada_0800' && (
                              <p className={`text-xs mb-2 ${TONE_TEXT.info}`}>
                                💡 Entrada 0800 - será cobrada conforme tarifa configurada
                              </p>
                            )}
                              <div className="flex flex-col sm:flex-row gap-2">
                                <select
                                  value={cdrContractMap[`${client.clientTag}_${client.direction}`] || ''}
                                  onChange={(e) => {
                                    const contractId = parseInt(e.target.value) || 0;
                                    // Aplicar para todas as direções do mesmo cliente
                                    const newMap = { ...cdrContractMap };
                                    const directions = ['saida', 'entrada', 'entrada_0800'];
                                    directions.forEach(dir => {
                                      const key = `${client.clientTag}_${dir}`;
                                      if (!newMap[key] || cdrResult?.clients?.some((c: any) => c.clientTag === client.clientTag && c.direction === dir)) {
                                        newMap[key] = contractId;
                                      }
                                    });
                                    setCdrContractMap(newMap);
                                  }}
                                  className="flex-1 px-3 py-2 bg-slate-900 border border-orange-600 text-white rounded-lg text-sm focus:border-orange-500"
                                >
                                  <option value="">Selecione a assinatura...</option>
                                  {contracts
                                    .filter(c => c.status === 'Ativo')
                                    .map(c => {
                                      const clientData = clients.find(cl => cl.id === c.clientId);
                                      const displayName = clientData?.name?.trim() || clientData?.tradeName?.trim() || clientData?.companyName || 'Cliente';
                                      return { ...c, displayName };
                                    })
                                    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'))
                                    .map(c => (
                                      <option key={c.id} value={c.id}>
                                        {c.displayName}{c.contractNumber ? ` (${c.contractNumber})` : ''}{c.has0800Link ? ' [0800]' : ''}
                                      </option>
                                    ))}
                                </select>
                                {cdrAppliedContracts.includes(`${client.clientTag}_${client.direction}`) ? (
                                  <div className={`flex items-center justify-center gap-2 px-4 py-2 ${TONE_BADGE.success} rounded-lg text-sm font-medium whitespace-nowrap`}>
                                    <Icon name="check" className="w-4 h-4" />
                                    Aplicado
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => applyMinutesToContract(client.clientTag, client.direction, client.minutesBilledByType)}
                                    disabled={!cdrContractMap[`${client.clientTag}_${client.direction}`] || cdrApplying === `${client.clientTag}_${client.direction}`}
                                    className="flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap bg-primary hover:bg-primary-strong"
                                  >
                                    {cdrApplying === `${client.clientTag}_${client.direction}` ? (
                                      <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Aplicando...
                                      </>
                                    ) : (
                                      <>
                                        <Icon name="check" className="w-4 h-4" />
                                        Aplicar na Assinatura
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>

                          {/* Aplicar na Fatura */}
                          <div className="mt-3 pt-3 border-t border-central-borda">
                            <p className="text-xs text-slate-500 mb-2">Aplicar CDR na fatura:</p>
                            <SeletorDeFaturaDoCdr
                              clientTag={client.clientTag}
                              bloco={opcoesDoBlocoCdr(client.clientTag)}
                              valor={cdrInvoiceMap[`${client.clientTag}_${client.direction}`] || 0}
                              onEscolher={(invoiceId) => {
                                // Aplicar para todas as direções do mesmo cliente
                                const newMap = { ...cdrInvoiceMap };
                                const directions = ['saida', 'entrada', 'entrada_0800'];
                                directions.forEach(dir => {
                                  const key = `${client.clientTag}_${dir}`;
                                  // Só atualiza se não tiver valor ou for o mesmo cliente
                                  if (!newMap[key] || cdrResult?.clients?.some((c: any) => c.clientTag === client.clientTag && c.direction === dir)) {
                                    newMap[key] = invoiceId;
                                  }
                                });
                                setCdrInvoiceMap(newMap);
                              }}
                            >
                                {cdrAppliedInvoices.includes(`${client.clientTag}_${client.direction}`) ? (
                                  <div className={`flex items-center justify-center gap-2 px-4 py-2 ${TONE_BADGE.success} rounded-lg text-sm font-medium whitespace-nowrap`}>
                                    <Icon name="check" className="w-4 h-4" />
                                    Aplicado
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => applyCdrToInvoiceForClient(client.clientTag, client.direction, client)}
                                    disabled={!cdrInvoiceMap[`${client.clientTag}_${client.direction}`] || cdrApplyingInvoice === `${client.clientTag}_${client.direction}`}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap hover:bg-slate-600"
                                  >
                                    {cdrApplyingInvoice === `${client.clientTag}_${client.direction}` ? (
                                      <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Aplicando...
                                      </>
                                    ) : (
                                      <>
                                        <Icon name="fileText" className="w-4 h-4" />
                                        Aplicar na Fatura
                                      </>
                                    )}
                                  </button>
                                )}
                            </SeletorDeFaturaDoCdr>
                            </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              )}
        </CascaDeModal>
      )}
      {/* Batch Progress Modal */}
      {batchProgress && (
        // Enquanto roda: fechavel={false} (sem ×, clique fora e Esc não fecham), como
        // antes, quando a caixa não tinha nenhuma saída até terminar. Concluído, vira
        // leitura: ganha o × e fecha ao clicar fora; o "Fechar" fica neutro.
        <CascaDeModal
          largura="xs"
          fechavel={batchProgress.status === 'done'}
          fecharAoClicarFora
          titulo={batchProgress.status === 'done'
            ? (batchProgress.type === 'finalize' ? 'Aprovação concluída' : batchProgress.type === 'correction' ? 'Comunicados enviados' : 'Envio concluído')
            : (batchProgress.type === 'finalize' ? 'Aprovando Faturas...' : batchProgress.type === 'correction' ? 'Enviando Comunicados...' : 'Enviando Emails...')}
          onClose={() => setBatchProgress(null)}
          corpoClassName="p-6"
          rodape={batchProgress.status === 'done' ? (
            <button
              onClick={() => setBatchProgress(null)}
              className="ml-auto px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium"
            >
              Fechar
            </button>
          ) : undefined}
        >
            <div className="bg-slate-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-primary h-3 rounded-full transition-all duration-300"
                style={{ width: `${batchProgress.percent}%` }}
              />
            </div>
            <p className="text-sm text-slate-300 mt-2">
              {batchProgress.completed}/{batchProgress.total} ({batchProgress.percent}%)
            </p>
            {(batchProgress.errors.length > 0 || (batchProgress.skipped?.length || 0) > 0) && (
              <p className="text-sm mt-1">
                {batchProgress.errors.length > 0 && <span className="text-red-400">{batchProgress.errors.length} erro(s)</span>}
                {batchProgress.errors.length > 0 && (batchProgress.skipped?.length || 0) > 0 && <span className="text-slate-500"> · </span>}
                {(batchProgress.skipped?.length || 0) > 0 && <span className="text-amber-400">{batchProgress.skipped!.length} pulado(s)</span>}
              </p>
            )}
            {batchProgress.status === 'done' && (
              <div className="mt-4 space-y-2">
                {batchProgress.errors.length > 0 && (
                  <div className="bg-central-fundo rounded-lg p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-semibold text-slate-400 mb-1">ERROS</p>
                    {batchProgress.errors.map((e: any, i: number) => (
                      <p key={i} className="text-xs text-red-400 truncate">Fatura {e.id}: {e.error}</p>
                    ))}
                  </div>
                )}
                {(batchProgress.skipped?.length || 0) > 0 && (
                  <div className="bg-central-fundo rounded-lg p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-semibold text-slate-400 mb-1">PULADOS <span className="text-slate-500 font-normal">{batchProgress.type === 'finalize' ? '(não aprovados: abra a fatura para aprovar com o motivo)' : '(não recebem por email)'}</span></p>
                    {batchProgress.skipped!.map((s: any, i: number) => (
                      <p key={i} className="text-xs text-amber-400" data-pulado-do-lote>{s.code || `#${s.id}`}: {s.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
        </CascaDeModal>
      )}
      {aprovacaoComEmpenho && (
        <ConfirmacaoDeEmpenho
          codigo={aprovacaoComEmpenho.inv.code}
          previa={aprovacaoComEmpenho.previa}
          titulo={aprovacaoComEmpenho.force ? `Liberar a retenção e aprovar ${aprovacaoComEmpenho.inv.code}?` : undefined}
          aviso={aprovacaoComEmpenho.motivoRetencao
            ? <p className={`text-sm rounded-lg border px-3 py-2 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>🔒 Retida: {aprovacaoComEmpenho.motivoRetencao}</p>
            : undefined}
          enviando={aprovandoComEmpenho}
          onCancelar={() => setAprovacaoComEmpenho(null)}
          onConfirmar={aprovarComEmpenho}
        />
      )}
      {/* Email Result Modal */}
      {emailResult && (
        // Só leitura: fecha ao clicar fora (antes não fechava). Os três totais são a
        // faixa da casca (§11): cor só no valor.
        <CascaDeModal
          largura="xs"
          fecharAoClicarFora
          titulo="Resultado do Envio"
          onClose={() => setEmailResult(null)}
          faixa={[
            { k: 'Enviados', v: emailResult.sent, cls: TONE_TEXT.open },
            { k: 'Ignorados', v: emailResult.skipped, cls: TONE_TEXT.warning },
            { k: 'Erros', v: emailResult.errors, cls: TONE_TEXT.danger },
          ]}
          corpoClassName="p-4"
          rodape={
            <button
              onClick={() => setEmailResult(null)}
              className="w-full px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium"
            >
              Fechar
            </button>
          }
        >
              {emailResult.details && emailResult.details.length > 0 ? (
                <div className="bg-central-fundo rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-400 mb-2">DETALHES</p>
                  <div className="space-y-1">
                    {emailResult.details.map((d: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={d.status === 'sent' ? TONE_TEXT.open : d.status === 'skipped' ? TONE_TEXT.warning : TONE_TEXT.danger}>
                          {d.status === 'sent' ? '✓' : d.status === 'skipped' ? '–' : '✕'}
                        </span>
                        <span className="text-slate-300 truncate">{d.code || `#${d.invoiceId}`}</span>
                        <span className="text-slate-500 truncate ml-auto">{d.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-2">Sem detalhes por fatura.</p>
              )}
        </CascaDeModal>
      )}
    </div>
  );
}
